// Ultimate Card Game (UCG) - Master Game Orchestrator

import { SoundEngine } from './engine/audio.js';
import { ParticleEngine } from './engine/particles.js';
import { Battlefield } from './engine/battlefield.js';
import { EconomyEngine } from './engine/economy.js';
import { CardSystem, resolveDeckCards } from './engine/cardSystem.js';
import { BotAI, createChargeLedger } from './engine/ai.js';
import { aiAdaptiveMemory } from './engine/ai/adaptiveMemory.js';
import { aiOpponentMemory, currentPlayerKey } from './engine/ai/opponentMemory.js';
import { sendAiMatchTelemetry } from './engine/ai/telemetry.js';
import { Renderer } from './engine/renderer.js';
import { BuildingSystem } from './engine/buildings.js';
import { HUD } from './ui/hud.js';
import { DeckBuilder } from './ui/deckBuilder.js';
import { DeckBuilderModal } from './ui/deckBuilderModal.js';
import { createRNG } from './engine/rng.js';
import { CommandQueue } from './commands/commandQueue.js';
import { CommandProcessor } from './commands/commandProcessor.js';
import { StateStreamManager } from './net/stateStream.js';
import { recoverAccount, previewRecoveryCode, isSelfRecoveryCode, rememberSelfRecoveryCode, isSessionExpired, getDisplayName, getMMR, addIdentityListener, fetchProfile, getAuth } from './net/playerIdentity.js';
import { initCloudSync } from './net/cloudSync.js';
import { initUpdateManager, notifyUpdateManagerIdle } from './ui/updateManager.js';
import { showLegalModal } from './ui/legalModal.js';
import { showCallsignModal } from './ui/callsignModal.js';
import { showLeaderboardModal, getRankTier } from './ui/leaderboardModal.js';
import { getIconSvg } from './ui/icons.js';
import { showAccountFullscreenModal } from './ui/accountFullscreenModal.js';
import { showAccountSwitchPrompt } from './ui/accountOverlay.js';
import { showOptionsFullscreenModal } from './ui/optionsFullscreenModal.js';
import { StartScreen } from './ui/startScreen.js';
import { OnlineLobby } from './ui/onlineLobby.js';
import { Viewport } from './ui/viewport.js';
import { Minimap } from './ui/minimap.js';
import { PerformanceManager } from './engine/performance.js';
import { ErrorReporter } from './engine/errorReporter.js';
import { clearSpriteBitmapCache, clearAuraBitmapCache, clearStatueBitmapCache } from './engine/render/unitSprites.js';
import { clearModuleBitmapCache } from './engine/render/modules.js';
import { MatchStats } from './engine/stats.js';
import { computeGamerank } from './engine/gamerank.js';
import { computeUnitMvp } from './engine/unitMvp.js';
import { createUnitMvpSpotlight } from './ui/unitMvpSpotlight.js';
import { wallet } from './engine/wallet.js';
import { encodeDeck, decodeDeck } from './engine/deckEncoder.js';
import { grantAlphaAccess } from './ui/alphaGate.js';
import { directives, MatchDirectiveTracker } from './engine/directives.js';
import { DirectivesModal } from './ui/directivesModal.js';
import { DirectiveTrackerHUD } from './ui/directiveTrackerHUD.js';
import { PackOpeningModal } from './ui/packOpeningModal.js';
import { packs } from './engine/packs.js';
import { SlotMachineModal } from './ui/slotMachineModal.js';
import { slotMachine, REEL_3_STYLES, REEL_3_STYLES_MAP } from './engine/slotMachine.js';
import { ShowroomModal } from './ui/showroomModal.js';
import { hubNavigator } from './ui/hubNavigator.js';
import { preloadAllCardThumbnails, releasePreloadedThumbnails } from './data/cardThumbUrl.js';
import { inventory, isBulletColorExcluded, isAuraExcluded, isRocketTrailUnit } from './engine/inventory.js';
import { progress } from './engine/progress.js';
import { ACHIEVEMENTS } from './data/achievements.js';
import { scheduleBaselineWarmup } from './engine/ai/deckProfile.js';
import { ALL_TAUNT_IDS } from './data/taunts.js';
import { DECKBUILDER_THEMES } from './data/deckbuilderThemes.js';
import { CARD_THEMES } from './data/cardThemes.js';
import { DECK_ICON_PRESETS } from './data/customDecks.js';
import { buildAchievementFacts, inspectDeckForAchievements } from './engine/achievementFacts.js';
import { LevelUpOverlay } from './ui/levelUpOverlay.js';
import { AchievementOverlay } from './ui/achievementOverlay.js';
import { resolveAvatarImageUrl } from './ui/avatarImage.js';
import { getAvatarById, getAvatarCatalog } from './engine/avatarCatalog.js';
import { SpecialCardToast } from './ui/specialCardToast.js';
import { TauntManager } from './ui/tauntManager.js';
import { OnboardingGuide } from './ui/onboardingGuide.js';
import { inboxManager } from './net/inboxManager.js';
import { chooseLocalMapId, MAP_IDS } from './engine/maps.js';
import { CutscenePlayer } from './engine/cutscenePlayer.js';
import { AudioDebugOverlay } from './ui/audioDebugOverlay.js';
import { startMemProbe } from './ui/memProbe.js';
import { resolveHpBarAccent } from './ui/hpBarAccent.js';
import { initAbFlags, activeAbFlags } from './ui/abFlags.js';
import { isDevToolsOn } from './ui/devTools.js';

import { DECKS_DATA } from './data/decks.js';
import { CARDS_DATA, getCardByName, getProfileArtworkCards } from './data/cards.js';
import { BALANCE } from './data/balance.js';
import { CAMPAIGN_MISSIONS } from './data/campaignMissions.js';
import { CAMPAIGN_CUTSCENES } from './data/campaignCutscenes.js';

// Crash/memory breadcrumbs for the iOS "white flash -> back on the start screen" reports
// (jetsam kills leave no JS trace). Started as early as possible so boot-time kills are seen too;
// Game wires in the pack-opening state below. Off by default; ?probe=on opts in.
startMemProbe();
initAbFlags(); // developer A/B switches, all off unless enabled - see js/ui/abFlags.js

const ICON_PLAY = '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><polygon points="6,4 20,12 6,20"/></svg>';
const ICON_PAUSE = '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><rect x="5" y="4" width="4" height="16" rx="1"/><rect x="15" y="4" width="4" height="16" rx="1"/></svg>';

export const STORAGE_KEY_AI_DIFFICULTY = 'ucg_ai_difficulty';
export const STORAGE_KEY_SHOW_AI_HAND = 'ucg_show_ai_hand';
export const DEFAULT_AI_DIFFICULTY = 'medium';
export const VALID_AI_DIFFICULTIES = new Set(['easy', 'medium', 'hard', 'veryHard', 'insane', 'invincible']);

export class Game {
  // 60 Hz fixed tick - matches the game's existing balance values (attack
  // speeds, regen rates etc. were all authored against a 60fps assumption).
  static TICK_DT = 1 / 60;
  // Rendering has no reason to run faster than the sim's own fixed rate -
  // capping actual canvas draws here (independent of the RAF callback rate)
  // stops high-refresh displays (120Hz iPhone ProMotion, M1 MacBook) from
  // paying 2x the GPU/CPU cost for frames that look identical. See gameLoop().
  static TARGET_RENDER_DT = 1 / 60;
  // Backlog cap per rendered frame. Lower than the fixed-timestep-pattern
  // textbook default (which would allow catching up half a second - 30
  // ticks - in one frame): now that update() is DOM-free (see update()'s
  // own comment), 8 ticks/frame is plenty cheap to run every frame without
  // dropping frames, and it spreads a bigger backlog (e.g. after a real
  // network stall) over several frames instead of one large stutter-step -
  // a ~1s backlog then takes ~8 frames (~130ms) to fully absorb instead of
  // 2 frames, which reads as smooth catch-up rather than a single jump.
  static MAX_TICKS_PER_FRAME = 8;
  // Online-only ceiling on how much "owed simulation time" the fixed-step
  // accumulator is allowed to hold - see its use in gameLoop() for the full
  // reasoning. ~0.2s = 12 ticks, comfortably more than the ~100-220ms round
  // trip measured against a real Strato-hosted instance, without ever
  // letting a multi-second wait turn into a permanent debt.
  static MAX_ONLINE_ACCUMULATOR_SEC = 0.2;

  constructor() {
    // Registered first, before anything else below gets a chance to throw -
    // catches errors from click/drag/hotkey handlers, network callbacks, and
    // any other DOM-triggered code the game loop's own try/catch (see
    // gameLoop() below) never sees. See js/engine/errorReporter.js.
    this.errorReporter = new ErrorReporter(this);

    this.canvas = document.getElementById('battlefield-canvas');
    this.audio = new SoundEngine();
    // Global level-up presentation (docs/XP_PROGRESSION_PLAN.md 5.4) - XP
    // can land from a match, a directive, or an achievement outside any
    // match, so this listens to progress.js directly rather than being
    // triggered only from handleGameOver().
    this.levelUpOverlay = new LevelUpOverlay(this.audio);
    this.achievementOverlay = new AchievementOverlay(this.audio);
    this.questOverlay = this.achievementOverlay;
    progress.addListener((_state, detail) => {
      if (detail?.levelUp) {
        this.levelUpOverlay.show(detail.levelUp.newLevel, detail.levelUp.title);
      }
    });
    directives.addCompletionListener((directive, jackpotInfo) => {
      this.achievementOverlay?.showDirective?.(directive);
      if (jackpotInfo?.jackpot) {
        this.achievementOverlay?.showJackpot?.(jackpotInfo);
      }
    });
    this.particles = new ParticleEngine();
    this.particles.audio = this.audio;
    this.particles.game = this;
    // Single source of sim-critical randomness (spawn offsets, target picks,
    // attack timing - NOT card draws, those are server-issued in an online
    // match, see js/engine/cardSystem.js). Locally seeded from time by
    // default; startOnlineMatch() reseeds this in place with a value both
    // peers share, so Battlefield/CardSystem (which hold this same object)
    // pick it up automatically.
    this.rng = createRNG();
    this.battlefield = new Battlefield(1600, 540, this.rng);
    this.economy = new EconomyEngine();
    this.cardSystem = new CardSystem(this.economy, this.battlefield, this.particles, this.audio, this.rng);
    this.cardSystem.game = this;
    this.buildings = new BuildingSystem(this.battlefield, this.particles, this.audio);
    // One charge ledger over this.superweaponCharges for every bot this
    // Game creates, so the HUD, CommandProcessor._nuke() and the bots all
    // read and spend the same counters (the object itself is replaced per
    // match - createChargeLedger() re-reads it on every access).
    this.superweaponLedger = createChargeLedger(() => this.superweaponCharges);
    let storedDifficulty = null;
    if (typeof localStorage !== 'undefined') {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_AI_DIFFICULTY);
        if (saved && VALID_AI_DIFFICULTIES.has(saved)) {
          storedDifficulty = saved;
        }
      } catch (_) {}
    }
    this.currentAIDifficulty = storedDifficulty || DEFAULT_AI_DIFFICULTY;
    let storedShowAiHand = false;
    if (typeof localStorage !== 'undefined') {
      try {
        storedShowAiHand = localStorage.getItem(STORAGE_KEY_SHOW_AI_HAND) === 'true';
      } catch (_) {}
    }
    this.showAiHand = storedShowAiHand;
    this.consecutiveLossesAgainstAI = 0;
    this.consecutiveWinsAgainstAI = 0;
    if (typeof sessionStorage !== 'undefined') {
      try {
        const savedLosses = parseInt(sessionStorage.getItem('ucg_consecutive_ai_losses') || '0', 10);
        if (!isNaN(savedLosses)) this.consecutiveLossesAgainstAI = savedLosses;
        const savedWins = parseInt(sessionStorage.getItem('ucg_consecutive_ai_wins') || '0', 10);
        if (!isNaN(savedWins)) this.consecutiveWinsAgainstAI = savedWins;
      } catch (_) {}
    }
    this.ai = new BotAI(this.economy, this.cardSystem, this.battlefield, this.particles, this.audio, this.currentAIDifficulty, 'eastern', null, { superweapons: this.superweaponLedger });
    this.renderer = new Renderer(this.canvas);
    this.renderer.game = this;
    // Camera: maps the fixed 1600x540 world onto whatever this device's
    // canvas box actually is (see js/ui/viewport.js for why the world
    this.viewport = new Viewport(this.canvas, this.battlefield.width, this.battlefield.height);
    this.viewport.onResize = () => {
      if (this.isRunning) this.render();
    };
    this.renderer.setViewport(this.viewport);

    // Portrait-only overview strip (self-gates to a no-op in every other
    // layout - see js/ui/minimap.js).
    this.minimap = new Minimap(this);
    // Auto-tunes render quality from actual measured frame time - see
    // js/engine/performance.js. Purely cosmetic, never touches simulation
    // state.
    this.performanceManager = new PerformanceManager(this);
    this.audioDebugOverlay = new AudioDebugOverlay(this);

    this.battlefield.economy = this.economy;
    this.economy.stats = this.battlefield.stats;
    this.battlefield.buildingSystem = this.buildings;

    this.hud = new HUD(this);
    // Preload deferred to StartScreen idle phase to ensure smooth first paint
    this.deckBuilder = new DeckBuilder(this);
    this.deckBuilderModal = new DeckBuilderModal(this);
    this.onlineLobby = new OnlineLobby(this);
    this.startScreen = new StartScreen(this);
    this.directivesModal = new DirectivesModal(this);
    this.directiveTrackerHUD = new DirectiveTrackerHUD(document.body, this.directivesModal);
    this.specialCardToast = new SpecialCardToast();
    this.packOpeningModal = new PackOpeningModal(this.audio, this);
    startMemProbe({
      getContext: () => ({
        pack: this.packOpeningModal?._state,
        warp: !!this.packOpeningModal?._warpActive, // Warp Speed run in progress (WarpFrameTracker)
        packOpen: !!this.packOpeningModal?.isOpen?.(),
        running: this.isRunning,
        audioState: window.Howler?.ctx?.state,
        ab: activeAbFlags(), // developer A/B switches on right now, stamped into every recorded tear
      }),
    });
    this.slotMachineModal = new SlotMachineModal(this.audio, this.packOpeningModal, this);
    this.showroomModal = new ShowroomModal(this.audio, this.slotMachineModal, this);
    this.hubNavigator = hubNavigator;
    this.hubNavigator.setModals({
      packOpeningModal: this.packOpeningModal,
      slotMachineModal: this.slotMachineModal,
      showroomModal: this.showroomModal,
      deckBuilder: this.deckBuilder,
      deckBuilderModal: this.deckBuilderModal,
      audio: this.audio,
      game: this
    });
    // Schedule background warmup for AI deck profile statistics during idle
    scheduleBaselineWarmup();
    this.tauntManager = new TauntManager(this);
    this.onboardingGuide = new OnboardingGuide(this);
    this.matchDirectiveTracker = new MatchDirectiveTracker('western');
    this._directiveCheckTimer = 0;
    this.battlefield.playerTeam = 'western';

    // All player/bot actions that change simulation state (play a card,
    // draw a card, nuke) go through here instead of mutating engine state
    // directly from a click handler - see js/commands/commandQueue.js for
    // why.
    this.commandQueue = new CommandQueue();
    this.commandProcessor = new CommandProcessor(this);

    this.isRunning = false;
    this.isPaused = false;
    this._openMenus = new Set();
    this._pausedByMenu = false;
    this.speedMultiplier = 1;
    this.lastTime = 0;
    this._lastRenderTime = 0;
    this.accumulator = 0;
    this.activeTargetingPower = null;
    this._lastSuperBtnKey = null;
    this._elBtnCoSuper = null;

    // Online match state - all default/false until startOnlineMatch() runs.
    // See js/net/turnScheduler.js and MULTIPLAYER_PLAN.md section 9.
    // myTeam is which REAL team ('western'/'eastern') the local human
    // controls - always 'western' in single-player, but 'eastern' for a
    // player who joined rather than created an online match. Team labels
    // themselves carry real simulation meaning (western spawns/attacks
    // from the left, eastern from the right) and are never relabeled per
    // client - CommandProcessor and CardSystem's draw sources key off
    // myTeam instead to know which side is "mine" for UI feedback and
    // hand secrecy.
    this.myTeam = 'western';
    this.isOnline = false;
    this.onlineMode = 'normal';
    this._lastMatchMode = null;
    this.netMatch = null;
    this.turnScheduler = null;

    this.superweaponCharges = { western: 1, eastern: 1 };
    this.nukesAvailable = this.superweaponCharges;

    // Auto-draw toggle: when on, my own empty hand slots are drawn
    // automatically as soon as I can afford it, same as pressing 'D'/a
    // number key myself - off by default so nobody's plasma gets spent
    // without them asking for it first.
    this.autoDrawEnabled = false;
    this._aiRematchTimer = null;

    // Singleplayer campaign (docs/game-design-singleplayer-kampagne.md):
    // true while a campaign mission - as opposed to a normal PvP/Exhibition
    // match - is active. Gates out BotAI card-play (campaign enemies are
    // pre-placed guards spawned directly via spawnRaw(), never drawn from a
    // deck - see startCampaignMission()) and holds combat until the player
    // plays their first card (see enqueueCommand()'s campaignCombatArmed
    // flip and gameLoop()'s use of it).
    this.isCampaignMission = false;
    this.campaignCombatArmed = false;
    this.currentCampaignMissionId = null;
    this._campaignEnemyUnitIds = [];
    this._campaignMissionEnded = false;
    this._campaignShadeTracked = false;
    // Set once the intro cutscene's real `unitActor` Shade (js/data/
    // campaignCutscenes.js) is done marching in - CommandProcessor._play()
    // (docs/singleplayer implementation soft-foraging-flamingo.md Phase 5)
    // activates THIS existing unit instead of spawning a new one, so the
    // cutscene actor and the fighting unit stay the same object.
    this._campaignActivationUnit = null;
    this.cutscenePlayer = new CutscenePlayer(this);

    // Debug & Bisect URL Flags (v3 §3 P0)
    this.debugFlags = {
      noPost: false,
      noBloom: false,
      noBg: false,
      noHud: false,
      fps30: false,
      eco: false,
      // Developer-only: force the bot's hidden doctrine (js/data/aiDoctrines.js)
      // via ?aiDoctrine=<id>. Honoured only with the dev tools switched on
      // (js/ui/devTools.js), so a shared link can never reveal or fix a
      // player's opponent style (design decision E2).
      aiDoctrine: null
    };
    if (typeof window !== 'undefined' && window.location?.search) {
      try {
        const params = new URLSearchParams(window.location.search);
        this.debugFlags.noPost = params.get('nopost') === '1';
        this.debugFlags.noBloom = params.get('nobloom') === '1';
        this.debugFlags.noBg = params.get('nobg') === '1';
        this.debugFlags.noHud = params.get('nohud') === '1';
        this.debugFlags.fps30 = params.get('fps') === '30';
        this.debugFlags.eco = params.get('eco') === '1';
        const mapParam = params.get('map');
        if (mapParam && MAP_IDS.includes(mapParam)) {
          this.debugFlags.map = mapParam;
        }
        if (typeof localStorage !== 'undefined' && isDevToolsOn(localStorage)) {
          this.debugFlags.aiDoctrine = params.get('aiDoctrine') || null;
        }
      } catch (_) {}
    }

    // Eco Mode (Battery Saver 30 FPS rendering) (v3 §3 P1b)
    this.isEcoMode = this.debugFlags.fps30 || this.debugFlags.eco;
    if (typeof localStorage !== 'undefined' && !this.isEcoMode) {
      try {
        this.isEcoMode = localStorage.getItem('ucg_eco_mode') === 'true';
      } catch (_) {}
    }

    // Generic Overlay Gate for fullscreen dialogs (v3 §3 P2 #3)
    this.overlayGate = {
      _count: 0,
      acquire: () => {
        this.overlayGate._count++;
      },
      release: () => {
        this.overlayGate._count = Math.max(0, this.overlayGate._count - 1);
      },
      isOpen: () => this.overlayGate._count > 0
    };

    this.initEventListeners();
  }

  setEcoMode(enabled) {
    this.isEcoMode = !!enabled;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('ucg_eco_mode', String(this.isEcoMode));
      } catch (_) {}
    }
    const btnRefresh = document.getElementById('btn-toggle-refresh-rate');
    if (btnRefresh) {
      btnRefresh.innerText = this.isEcoMode ? 'Refresh Rate: 30Hz (ECO)' : 'Refresh Rate: 60Hz';
      btnRefresh.classList.toggle('eco-active', this.isEcoMode);
    }
    const ecoBtn = document.getElementById('options-toggle-eco');
    if (ecoBtn) {
      ecoBtn.textContent = this.isEcoMode ? '30HZ' : '60HZ';
      ecoBtn.classList.toggle('active', this.isEcoMode);
    }
    return this.isEcoMode;
  }

  setAIDifficulty(difficulty) {
    if (!VALID_AI_DIFFICULTIES.has(difficulty)) return this.currentAIDifficulty;
    this.currentAIDifficulty = difficulty;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY_AI_DIFFICULTY, difficulty);
      } catch (_) {}
    }
    const selDiff = document.getElementById('select-ai-difficulty');
    if (selDiff && selDiff.value !== difficulty) {
      selDiff.value = difficulty;
    }
    const modalButtons = typeof document !== 'undefined' && document.querySelectorAll
      ? document.querySelectorAll('#options-ai-difficulty-buttons .options-preset-btn[data-difficulty]')
      : [];
    modalButtons.forEach((b) => b.classList.toggle('active', b.dataset.difficulty === difficulty));
    if (this.ai) this.ai.setDifficulty(difficulty);
    if (this.aiWestern) this.aiWestern.setDifficulty(difficulty);
    if (this.aiEastern) this.aiEastern.setDifficulty(difficulty);
    this.consecutiveLossesAgainstAI = 0;
    this.consecutiveWinsAgainstAI = 0;
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem('ucg_consecutive_ai_losses');
        sessionStorage.removeItem('ucg_consecutive_ai_wins');
      } catch (_) {}
    }
    this.hud?._hideAiDifficultyHint?.();
    return this.currentAIDifficulty;
  }

  toggleShowAiHand() {
    this.showAiHand = !this.showAiHand;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY_SHOW_AI_HAND, String(this.showAiHand));
      } catch (_) {}
    }
    return this.showAiHand;
  }

  isShowAiHandEnabled() {
    return !this.isOnline && !!this.isExhibition && !!this.showAiHand;
  }

  getRandomDeckId() {
    const deckKeys = Object.keys(DECKS_DATA);
    return deckKeys[Math.floor(Math.random() * deckKeys.length)];
  }

  enqueueCommand(command) {
    if (this.isOnline && (this.isOnlinePaused || this.onlinePauseState)) {
      return;
    }
    if (command.type === 'PLAY' && this.isCampaignMission && !this.campaignCombatArmed) {
      // Flips synchronously, before this command even reaches the queue -
      // so by the next tick (which drains the queue inside update()),
      // gameLoop()'s isCampaignMission/campaignCombatArmed hold has
      // already lifted and this very command gets applied normally. See
      // docs/game-design-singleplayer-kampagne.md Kapitel 9.2.
      this.campaignCombatArmed = true;
    }
    if (!command.id) {
      command.id = `cmd_${command.team || this.myTeam}_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
    }
    if (command.type === 'PLAY' && this.battlefield.selectedUnit) {
      // Snapshot the locally-selected target now, as an id: clone_unit /
      // upper_hand resolve their target from command.targetUnitId (see
      // CardSystem.executeSpell) instead of reading battlefield.selectedUnit
      // directly. That field is local UI-click state, never transmitted -
      // both lockstep peers replay the identical command object, so without
      // this they'd each read their OWN local selection and could clone or
      // sacrifice different units, breaking the lockstep the instant either
      // card is played online.
      command.targetUnitId = this.battlefield.selectedUnit.id;
    }
    if (command.type === 'PLAY' && (!command.team || command.team === this.myTeam)) {
      // Special-edition ownership (Gold Edition etc.) lives only in this
      // client's own local inventory (js/engine/inventory.js) - the remote
      // peer's client has no way to look it up, so - exactly like
      // targetUnitId/cardName above - it must be resolved into a command
      // field here, once, before the command crosses the network. See
      // docs/INVARIANTS.md rule 1.
      const myCard = this.cardSystem.hands[this.myTeam]?.[command.slot];
      if (myCard) {
        if (inventory.isSpecialCardActive ? inventory.isSpecialCardActive(myCard.name, 'gold') : inventory.hasSpecialCard(myCard.name, 'gold')) {
          command.edition = 'gold';
        }
        const auraStyleId = inventory.getEquippedUnitAura?.(myCard.name);
        if (auraStyleId) {
          const style = REEL_3_STYLES_MAP.get(auraStyleId);
          if (style?.hex) {
            command.customAuraHex = style.hex;
          }
        }
      }
    }
    if (this.isOnline) {
      if (this.myRole) {
        command.playerRole = this.myRole;
      }
      if (command.type === 'PLAY' && (!command.team || command.team === this.myTeam)) {
        const myHand = this.cardSystem.hands[this.myTeam];
        const card = myHand?.[command.slot];
        if (card) command.cardName = card.name;
      }
      if (this.wsClient) {
        this.wsClient.sendClientCommand(command);
      } else {
        this.turnScheduler?.recordLocalCommand(command);
      }
    } else {
      this.commandQueue.enqueue(command);
    }
  }

  // Deals BALANCE.startHandSize cards to both hands at match start - a free
  // initial deal, not a paid draw, so it bypasses the DRAW command/plasma
  // cost entirely. In an online match this only ever runs on the
  // authoritative server (server/gameSimulation.js), which draws both
  // hands directly from its own CardSystem - see js/engine/cardSystem.js's
  // drawCard(). Must run after setDeck()/setDrawSource() for both teams.
  fillStartingHands() {
    for (const team of ['western', 'eastern']) {
      this.cardSystem.drawBatch(team, BALANCE.startHandSize, { allowEpicInSlot: 0 });
    }
  }

  init() {
    let savedDeckId = null;
    try {
      savedDeckId = typeof localStorage !== 'undefined' ? localStorage.getItem('ucg_last_deck_id') : null;
    } catch (e) {}
    const initialPlayerDeck = savedDeckId || 'Standard';
    const randomEnemyDeck = this.getRandomDeckId();
    this.currentPlayerDeckId = initialPlayerDeck;
    this.cardSystem.setDeck('western', initialPlayerDeck);
    this.cardSystem.setDeck('eastern', randomEnemyDeck);
    this.fillStartingHands();
    this.hud?.resetForNewMatch?.();

    this.initSuperButton();
    this.initRematchButton();
    this.updateSuperButtonState();
    this.isRunning = false;
    this.lastTime = performance.now();
    this._lastRenderTime = performance.now();
    this.accumulator = 0;
    this._rafId = null;
    this.startGameLoop();

    // Show Fullscreen Start Screen on boot (instead of jumping into Archive/Battlefield)
    this.startScreen?.show();
    this.audio?.playMenuMusic?.();

    // Fire-and-forget: adopts a bookmarked recovery link if the player
    // arrived via one. Never awaited here - a slow or failed network call
    // must not delay boot, see docs/GO_LIVE_PLAN_2026-09.md section 2.4.
    // Deliberately does NOT fall back to silently registering a new
    // anonymous account - that only happens lazily, from
    // StartScreen.handleEnterTheVoid(), so a visitor who never gets past
    // the Start Screen never creates a server-side account (see
    // docs/COMMANDER_INTERFACE_AND_MENU_SYSTEM_SPEC_v2.md decision log #1).
    this._checkRecoveryLink();
    this._checkTacticalDareLink();
    initCloudSync();
    initUpdateManager(this);

    // Unlocks Web Audio (AudioContext) and HTML5 Audio (BGM) on macOS/iOS/desktop:
    // WebKit (Safari on macOS/iOS) requires click, touchend, mouseup, or keydown to
    // unlock HTMLMediaElement.play(), and rejects pointerdown.
    // We listen to all interaction events in capture phase, retry until playback
    // actually starts, and only remove listeners once audio is confirmed running.
    const unlockEvents = ['click', 'pointerdown', 'pointerup', 'touchend', 'keydown'];
    let unlockInProgress = false;
    const tryUnlockAudio = () => {
      if (unlockInProgress) return;
      this.audio.init();
      if (this.audio.ctx && this.audio.ctx.state === 'suspended') {
        this.audio.ctx.resume().catch(() => {});
      }
      if (this.audio._musicContext === 'menu' && !this.audio.isMuted && this.audio.musicEnabled) {
        if (!this.audio.bgm) {
          this.audio.playMenuMusic();
        } else if (this.audio.bgm.paused) {
          const baseVol = typeof this.audio._currentBgmBaseVolume === 'number' ? this.audio._currentBgmBaseVolume : 1.0;
          const targetVol = (this.audio.isDucking ? (this.audio.musicVolume * 0.15) : this.audio.musicVolume) * baseVol;
          if (this.audio.bgm.volume <= 0 && targetVol > 0) {
            this.audio.bgm.volume = targetVol;
          }
          const playPromise = this.audio.bgm.play();
          if (playPromise && typeof playPromise.then === 'function') {
            unlockInProgress = true;
            playPromise.then(() => {
              unlockInProgress = false;
              removeUnlock();
            }).catch(() => {
              unlockInProgress = false;
              // Rejected on this gesture (e.g. pointerdown in WebKit) -
              // keep listeners so following click/touchend/mouseup unlocks it
            });
            return;
          }
        }
      }

      // If music is not enabled (SFX only mode), unlock Web Audio & iOS audio session via silent audio:
      const unlockPromise = this.audio.unlockAudioSession?.();
      if (unlockPromise && typeof unlockPromise.then === 'function') {
        unlockInProgress = true;
        unlockPromise.then(() => {
          unlockInProgress = false;
          if (!this.audio.musicEnabled || this.audio.isMuted || (this.audio.bgm && !this.audio.bgm.paused)) {
            removeUnlock();
          }
        }).catch(() => {
          unlockInProgress = false;
        });
      } else if (!this.audio.musicEnabled || this.audio.isMuted || (this.audio.bgm && !this.audio.bgm.paused)) {
        removeUnlock();
      }

      if (this.audio._ambience && !this.audio.isMuted && this.audio.musicEnabled && !this.audio._ambience.playing?.()) {
        try { this.audio._ambience.play(); } catch {}
      }
      if (!this.audio.musicEnabled || !this.audio.bgm || !this.audio.bgm.paused) {
        if (this.audio._iosAudioSessionUnlocked) {
          removeUnlock();
        }
      }
    };

    const removeUnlock = () => {
      for (const ev of unlockEvents) {
        document.removeEventListener(ev, tryUnlockAudio, true);
      }
    };

    for (const ev of unlockEvents) {
      document.addEventListener(ev, tryUnlockAudio, { capture: true, passive: true });
    }

    this.initWakeLock();
  }

  // Adopts a bookmarked recovery link (docs/GO_LIVE_PLAN_2026-09.md
  // section 2.2 - the code lives in the URL fragment, which browsers never
  // send to the server and which never appears in any access log) if
  // present, otherwise silently registers/reads back this device's
  // anonymous account. Shows the one-time recovery-code overlay only for a
  // genuinely new registration - never for a returning device or a
  // successful link-based recovery, since both already have their code.
  async _checkRecoveryLink() {
    const fragmentMatch = location.hash.match(/^#a=(.+)$/);
    if (!fragmentMatch) return;
    const rawCode = decodeURIComponent(fragmentMatch[1]).trim();
    // Strip the fragment immediately - it's a bearer credential and must
    // not linger in the address bar/history longer than this one read.
    history.replaceState(null, '', location.pathname + location.search);

    const cleanCode = rawCode.replace(/[\s-]+/g, '-').toUpperCase();
    if (!/^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/i.test(cleanCode)) {
      return;
    }

    const currentAuth = getAuth();
    if (currentAuth?.accountId) {
      // 1. Fast synchronous local fingerprint check (0ms, 0 network requests):
      // If this recovery link is already verified as belonging to the active local account,
      // skip the switch prompt completely (e.g. launching from a bookmarked #a=... link).
      if (isSelfRecoveryCode(cleanCode, currentAuth.accountId)) {
        if (!currentAuth.secret || isSessionExpired()) {
          await recoverAccount(cleanCode);
        } else {
          grantAlphaAccess();
        }
        return;
      }

      // 2. Server pre-check (preview mode - resolves accountId without transferring secret/saveData):
      const preview = await previewRecoveryCode(cleanCode);
      if (preview?.ok && Number(preview.accountId) === Number(currentAuth.accountId)) {
        // Incoming link belongs to the SAME Commander already active on this device!
        // Cache salted fingerprint so future bookmark launches resolve locally in 0ms.
        rememberSelfRecoveryCode(currentAuth.accountId, cleanCode);
        if (!currentAuth.secret || isSessionExpired()) {
          await recoverAccount(cleanCode);
        } else {
          grantAlphaAccess();
        }
        return;
      }

      if (preview && !preview.ok && preview.error === 'not_found') {
        // Invalid recovery code - do not prompt to overwrite an active session with a dead link.
        return;
      }

      // An active account exists on this device and the link belongs to a DIFFERENT Commander
      // (or offline fallback) -> prompt for confirmation before switching!
      showAccountSwitchPrompt({
        currentAuth,
        incomingCode: cleanCode,
        incomingAccount: preview?.ok ? preview : null,
        onCancel: () => {
          // Fragment already removed from URL; current session preserved untouched
        }
      });
      return;
    }

    // No existing account on this device -> seamless 1-click restore
    await recoverAccount(cleanCode);
  }

  // Real-time game with no keyboard/mouse input for stretches at a time
  // (you're just watching the lane) - without this, phones dim/lock the
  // screen mid-match. Re-acquired on visibility change since the OS always
  // releases the lock when the tab/app is backgrounded.
  initWakeLock() {
    if (!('wakeLock' in navigator)) return;
    const request = () => {
      if (document.visibilityState !== 'visible') return;
      navigator.wakeLock.request('screen').catch(() => {});
    };
    request();
    document.addEventListener('visibilitychange', request);
  }

  // Match-start camera framing: opens on the player's own main building
  // (western base for singleplayer, whichever side the server assigned for
  // online - see startOnlineMatchWS()) before handing off to the normal
  // in-match camera. Desktop reuses the existing click-to-track zoom
  // (Viewport.startTrackingUnit/stopTrackingUnit) with the base standing in
  // for a unit; mobile frames the player's own base for ~1s and then slowly
  // and smoothly wanders to the center of the battlefield before handing off
  // to the active frontline tracking perspective.
  _startCameraIntro() {
    const myBase = this.battlefield?.bases?.[this.myTeam || 'western'];
    if (!myBase || !this.viewport) return;
    clearTimeout(this._introTimer);
    this.viewport.resetPortraitZoom?.();
    if (this.viewport.isDesktop()) {
      // A plain y-offset on top of the standard 1.85x unit-click zoom still
      // clamped back down to almost the same spot (that zoom leaves very
      // little vertical clamp headroom on this 540px-tall world - see
      // Viewport.updateUnitTracking()), so the base kept hugging the bottom
      // HUD regardless of the offset. __introZoomMult opts this stand-in
      // into a tighter zoom (more headroom before the world's bottom edge
      // clamps it) and __introPushMaxY frames at that clamp ceiling
      // outright, rather than at the base's own (low-in-the-lane) y.
      this._introCameraTarget = Object.assign(Object.create(myBase), {
        __introZoomMult: 2.9,
        __introPushMaxY: true
      });
      this.viewport.startTrackingUnit(this._introCameraTarget);
      this._introTimer = setTimeout(() => {
        if (this.viewport.trackedUnit === this._introCameraTarget) this.viewport.stopTrackingUnit();
      }, 2000);
    } else {
      if (this.viewport.canPan()) {
        this.viewport.startMobileIntro(myBase.x, this.viewport.worldW / 2, 1.0, 2.2);
      }
    }
  }

  // Campaign-only UI suppression (docs/game-design-singleplayer-kampagne.md):
  // hides/restores the two base HP-bar panels in #top-hud. The plasma
  // readout (#plasma-capsule-container) is a separate sibling and stays
  // untouched either way - the player still needs to see their resource
  // during an encounter.
  _setBaseUiVisible(visible) {
    document.querySelectorAll('.boss-column').forEach(el => {
      el.style.display = visible ? '' : 'none';
    });
  }

  startMatch(playerDeckId, enemyDeckId = null) {
    this._clearAiRematchCountdown?.();
    this.isExhibition = false;
    this.aiWestern = null;
    this._setBaseUiVisible(true);
    // Defensive: a campaign encounter (startCampaignMission()) hides
    // #top-hud/#bottom-hud via hud.setVisible(false) and, for the one-card
    // opening beat, swaps the hand into #campaign-cinematic-hud via
    // setCampaignCinematicMode(true). Neither is guaranteed to be undone by
    // the time a real match starts (Mission 1 never sets fullHudCombat, so
    // commandProcessor.js's setVisible(true) call is dead for it, and a
    // defeat skips the victory outro entirely) - leaving the hand tray
    // invisible/inert until a page refresh. Unconditional here since a
    // normal match always wants both back on anyway.
    this.hud?.setVisible?.(true);
    this.hud?.setCampaignCinematicMode?.(false);
    // Defensive: if this is reached while an online match was active (e.g.
    // the deck modal was still reachable mid-match), leave that state
    // behind cleanly rather than starting single-player half-online.
    this._endOnlineSession();

    if (!enemyDeckId) {
      enemyDeckId = this.getRandomDeckId();
    }
    const enemyDeckInfo = DECKS_DATA[enemyDeckId] || { name: enemyDeckId, race: 'Random' };
    this.currentPlayerDeckId = playerDeckId;
    if (this.deckBuilder && playerDeckId) {
      this.deckBuilder._lastSelectedDeckKey = playerDeckId;
    }
    this._lastMatchMode = {
      type: 'singleplayer',
      deckId: playerDeckId || 'Standard',
      enemyDeckId
    };
    try {
      if (typeof localStorage !== 'undefined' && playerDeckId) {
        localStorage.setItem('ucg_last_deck_id', playerDeckId);
      }
    } catch (e) {}
    clearSpriteBitmapCache();
    clearAuraBitmapCache();
    clearStatueBitmapCache();
    clearModuleBitmapCache();
    this.renderer?.releaseStaticCaches?.();
    this.particles?.reset?.();
    this.deckBuilder?.releaseBay?.();
    this.battlefield.reset();
    this.battlefield.playerTeam = 'western';
    if (this.canvas) this.canvas._suspended = false;
    this.viewport.resize(); // fresh camera fit/center for the new match
    this._startCameraIntro();
    this.buildings.reset();
    this.economy.reset();
    this.battlefield.economy = this.economy;
    this.economy.stats = this.battlefield.stats;
    this.cardSystem.setDeck('western', playerDeckId);
    if (this.ai && this.currentAIDifficulty && this.ai.difficulty !== this.currentAIDifficulty) {
      this.ai.setDifficulty(this.currentAIDifficulty);
    }
    // What the bot remembers about this player (js/engine/ai/opponentMemory.js).
    this.ai.attachOpponentMemory(aiOpponentMemory, currentPlayerKey());
    // The bot's hidden doctrine decides how its race deck is weighted - rolled
    // here, before the starting hand is dealt.
    if (this.activeDare?.enemyDeck) {
      this.cardSystem.setDeck('eastern', this.activeDare.enemyDeck);
    } else {
      this.cardSystem.setDeck('eastern', this.ai.selectDoctrine(enemyDeckInfo.race, this.debugFlags.aiDoctrine, enemyDeckId));
    }
    this.battlefield.mapId = this.debugFlags.map || chooseLocalMapId(this.battlefield.bases.western.race);
    this.battlefield.mapSeed = Math.floor(Math.random() * 1000000);
    this.fillStartingHands();
    this.hud?.resetForNewMatch?.();
    if (this.hud?.avatarRenderer) {
      this.hud.avatarRenderer.setDecks(playerDeckId, enemyDeckId);
    }
    if (this.hud?.resetLiveGamerank) {
      this.hud.resetLiveGamerank();
    }
    if (this.matchDirectiveTracker) {
      this.matchDirectiveTracker.reset();
      this.matchDirectiveTracker.myTeam = this.myTeam || 'western';
    }
    if (this.directiveTrackerHUD) {
      this.directiveTrackerHUD.hideChip();
    }
    // Show badge only when non-default rarity draw is active
    document.getElementById('flat-draw-badge')?.classList.toggle('hidden', !!BALANCE.flatDrawRates);
    this._directiveCheckTimer = 0;
    this.superweaponCharges = { western: 1, eastern: 1 };
    this.nukesAvailable = this.superweaponCharges;
    this.ai?.resetForMatch();
    this.activeTargetingPower = null;
    this._closeGameOverModal();
    this._hidePostGameReturnOverlay();
    this._lastSuperBtnKey = null;
    this.updateSuperButtonState(true);
    this.updateBaseLabels();
    // Battlefield-Ambience-Loop als atmosphärische Kulisse im Match
    this.audio.playBattlefieldAmbience(this.battlefield?.mapId);
    // Batch 1 (docs/SOUND_BATCH_PLAN.md) - "round_start", approved by the
    // user against tools/sound-review/data/batch-01.json.
    this.audio.playRoundStart();
    this.isRunning = true;
    this.isPaused = false;

    // Prompt after 3 consecutive losses against the AI: show "Change AI Mode" bubble
    if (!this.isOnline && !this.isExhibition && !this.isCampaignMission && (this.consecutiveLossesAgainstAI || 0) >= 3) {
      setTimeout(() => {
        if (this.isRunning && !this.isPaused) {
          this.hud?.showAiDifficultyHint?.();
        }
      }, 800);
      this.consecutiveLossesAgainstAI = 0;
      if (typeof sessionStorage !== 'undefined') {
        try {
          sessionStorage.removeItem('ucg_consecutive_ai_losses');
        } catch (_) {}
      }
    }

    // Prompt after 3 consecutive wins against the AI: suggest higher difficulty (unless already at max)
    if (!this.isOnline && !this.isExhibition && !this.isCampaignMission &&
        (this.currentAIDifficulty || DEFAULT_AI_DIFFICULTY) !== 'invincible' &&
        (this.consecutiveWinsAgainstAI || 0) >= 3) {
      setTimeout(() => {
        if (this.isRunning && !this.isPaused) {
          this.hud?.showAiDifficultyHint?.({ isPromote: true });
        }
      }, 800);
      this.consecutiveWinsAgainstAI = 0;
      if (typeof sessionStorage !== 'undefined') {
        try {
          sessionStorage.removeItem('ucg_consecutive_ai_wins');
        } catch (_) {}
      }
    }
    this._openMenus.clear();
    this._pausedByMenu = false;
    const btnPause = document.getElementById('btn-pause');
    const pauseOverlay = document.getElementById('pause-overlay');
    if (btnPause) {
      btnPause.innerHTML = ICON_PAUSE;
      btnPause.title = 'Pause';
      btnPause.classList.remove('pause-active');
    }
    if (pauseOverlay) pauseOverlay.classList.add('hidden');
    this.lastTime = performance.now();
    this._lastRenderTime = performance.now();
    this.accumulator = 0;
  }

  // Starts a singleplayer campaign mission (docs/game-design-singleplayer-
  // kampagne.md Kapitel 5/9). Deliberately NOT a thin wrapper around
  // startMatch(): campaign enemies are pre-placed guards spawned directly
  // via battlefield.spawnRaw() with a per-encounter hp override, never
  // drawn from an opposing deck - so there is no cardSystem.setDeck()
  // call, no BotAI, for 'eastern' at all (see update()'s isCampaignMission
  // guard). Combat starts player-initiated: isRunning goes true immediately
  // (so the intro cutscene can render and run every frame), but the actual
  // tick/AI simulation stays held until the player plays their first card -
  // see enqueueCommand()'s campaignCombatArmed flip and gameLoop().
  startCampaignMission(missionId) {
    const mission = CAMPAIGN_MISSIONS[missionId];
    if (!mission) {
      console.error(`[UCG] Unknown campaign mission: ${missionId}`);
      return;
    }

    this._clearAiRematchCountdown?.();
    this.isExhibition = false;
    this.aiWestern = null;
    this.aiEastern = null;
    this._endOnlineSession();
    document.getElementById('flat-draw-badge')?.classList.add('hidden');

    // The generic first-time "Commander Briefing" PvP tutorial (js/ui/
    // onboardingGuide.js) is scheduled from Game.init() independently of
    // campaign mode - if it's still active (or about to start) when a
    // campaign mission begins, its hint bubble would sit right where the
    // new cinematic HUD's own menu button appears, effectively hiding it
    // (docs/singleplayer implementation soft-foraging-flamingo.md). Cancels
    // without marking the guide "completed" - a brand-new player still
    // sees the real tutorial the next time they start a normal match.
    this.onboardingGuide?.cancelForCampaign?.();

    this.isCampaignMission = true;
    this.campaignCombatArmed = false;
    this.currentCampaignMissionId = missionId;
    this.currentPlayerDeckId = null;
    this._campaignMissionEnded = false;
    this._campaignShadeTracked = false;
    this._campaignActivationUnit = null;
    // "Vollwertiger Kampf" paradigm (docs/singleplayer implementation
    // soft-foraging-flamingo.md): a lightweight, guaranteed-solvable puzzle
    // encounter like Mission 1 keeps the reduced cinematic presentation
    // (no HUD, no HP bars, no click-to-zoom-out) all the way through
    // combat - only a mission explicitly flagged fullHudCombat:true (a
    // future boss fight) gets the real full-HUD skirmish treatment once
    // its card is played. Read by CommandProcessor._play()'s activation
    // branch, handleUnitClick() below, and battlefield.hideHealthBars.
    this._campaignFullHudCombat = !!mission.fullHudCombat;

    clearSpriteBitmapCache();
    clearAuraBitmapCache();
    clearStatueBitmapCache();
    clearModuleBitmapCache();
    this.renderer?.releaseStaticCaches?.();
    this.particles?.reset?.();
    this.deckBuilder?.releaseBay?.();
    this.battlefield.reset();
    this.battlefield.playerTeam = 'western';
    // "Karte ohne Basis": this reads as an isolated squad encounter, not a
    // two-fortress lane battle - suppress the base visuals (renderer.js)
    // and their HUD HP-bar panels. The base data objects themselves stay
    // (see reset()'s comment in battlefield.js and renderer.js's hideBases
    // gate) - only what the player sees is affected.
    this.battlefield.hideBases = true;
    this.battlefield.hideHealthBars = !this._campaignFullHudCombat;
    this._setBaseUiVisible(false);
    if (this.canvas) this.canvas._suspended = false;
    this.viewport.resize();
    this.buildings.reset();
    this.economy.reset();
    this.battlefield.economy = this.economy;
    this.economy.stats = this.battlefield.stats;

    this.cardSystem.setDeck('western', mission.playerDeck);
    // No cardSystem.setDeck('eastern', ...): the encounter's enemies are
    // pre-placed below, never played from a hand. Just clear out whatever
    // hand a previous normal match may have left in this slot - setDeck()
    // would also do this, but calling it here would give 'eastern' a real
    // deck/race derived from CARDS_DATA's full active roster (see its
    // fallback in resolveDeckCards()), which we don't want for a team that
    // never plays a card.
    this.cardSystem.hands.eastern = new Array(this.cardSystem.maxHandSize).fill(null);

    this.battlefield.mapId = mission.mapId;
    this.battlefield.mapSeed = Math.floor(Math.random() * 1000000);

    // Only the player's hand is dealt - one card is all Mission 1 needs
    // (see docs/game-design-singleplayer-kampagne.md Kapitel 9.1).
    this.cardSystem.drawBatch('western', 1, { allowEpicInSlot: 0 });
    this.hud?.resetForNewMatch?.();

    this._campaignEnemyUnitIds = [];
    for (const enemy of mission.enemies) {
      const baseCard = getCardByName(enemy.cardName);
      if (!baseCard) {
        console.error(`[UCG] Campaign mission "${missionId}" references unknown card: ${enemy.cardName}`);
        continue;
      }
      const cardData = enemy.hpOverride != null ? { ...baseCard, hp: enemy.hpOverride } : baseCard;
      const unit = this.battlefield.spawnRaw(cardData, 'eastern', enemy.x, enemy.y);
      this._campaignEnemyUnitIds.push(unit.id);
    }

    // Decorative ground props (docs/singleplayer implementation
    // soft-foraging-flamingo.md) - permanent, isCinematic:true, never
    // hidden/removed for the rest of the mission. team is arbitrary here
    // (only affects the TEAM-color cells baked into the sprite, see
    // unitSprites.js) - 'eastern' reads as battlefield wreckage rather than
    // player-owned equipment.
    for (const prop of (mission.groundProps || [])) {
      const propCard = getCardByName(prop.cardName);
      if (!propCard) {
        console.error(`[UCG] Campaign mission "${missionId}" references unknown ground prop card: ${prop.cardName}`);
        continue;
      }
      this.battlefield.spawnRaw(propCard, 'eastern', prop.x, prop.y, { isCinematic: true, isCinematicProp: true });
    }

    this.superweaponCharges = { western: 1, eastern: 1 };
    this.nukesAvailable = this.superweaponCharges;
    this.activeTargetingPower = null;
    this._closeGameOverModal();
    this._hidePostGameReturnOverlay();

    this.isRunning = true; // frames must render/update the intro cutscene
    this.isPaused = false;
    this.lastTime = performance.now();
    this._lastRenderTime = performance.now();
    this.accumulator = 0;

    const introId = mission.cutscenes?.intro;
    const introTimeline = introId ? CAMPAIGN_CUTSCENES[introId] : null;
    // Battlefield reset / enemy spawn above already happened synchronously -
    // only the camera/cutscene part waits on the video, so the marines exist
    // the instant the in-engine cutscene itself starts (docs/singleplayer
    // implementation soft-foraging-flamingo.md Phase 0).
    this._playCampaignIntroVideo(() => {
      // Fires the instant the video stops and the in-engine sequence
      // starts - see the <audio> element's own comment in index.html for
      // why this isn't routed through js/engine/audio.js's SoundEngine.
      const jumpAudio = document.getElementById('campaign-jump-audio');
      if (jumpAudio) {
        if (!jumpAudio.src || !jumpAudio.src.includes('intro_jump_cominin.mp3')) {
          jumpAudio.src = 'assets/intro_jump_cominin.mp3';
          try { jumpAudio.load(); } catch (_) {}
        }
        jumpAudio.currentTime = 0;
        jumpAudio.play().catch(() => {}); // autoplay-block-safe, silent no-op
      }
      // Sound-Batch-Pipeline Music Director (docs/SOUND_BATCH_PLAN.md
      // section 5, docs/about music folder.rtf): loops underneath the demo
      // until stopCampaignDemoMusicHardCut() cuts it (js/ui/deckBuilder.js's
      // openModal(), reached via _playCampaignOutroSequence()'s deck-modal
      // reopen or a defeat exit - either way).
      this.audio.stopMusic?.();
      this.audio.playBattlefieldAmbience?.(this.battlefield?.mapId);
      if (introTimeline) {
        this.cutscenePlayer.play(introTimeline, () => {
          // The intro cutscene's real `unitActor` Shade (js/data/
          // campaignCutscenes.js) is done marching in and is still sitting
          // in cutscenePlayer.actors (play() only clears that map at the
          // START of a timeline, not on finish()) - CommandProcessor._play()
          // activates this exact unit instead of spawning a new one (docs/
          // singleplayer implementation soft-foraging-flamingo.md Phase 5).
          this._campaignActivationUnit = this.cutscenePlayer.actors.get('shade') || null;
          this._startCampaignPreBattleCutscene(mission);
        });
      } else {
        this._startCampaignPreBattleCutscene(mission);
      }
    });
  }

  // Fullscreen, skippable narrative cold-open (assets/introvideo.mp4) played
  // BEFORE the in-engine intro cutscene - only ever called from
  // startCampaignMission(), no touch point with startMatch()/
  // startExhibitionMatch() (docs/singleplayer implementation
  // soft-foraging-flamingo.md Phase 0). `onComplete` fires exactly once,
  // whether the video plays to the end, the player clicks Skip/the video, or
  // the browser blocks autoplay outright.
  _playCampaignIntroVideo(onComplete) {
    const overlay = document.getElementById('campaign-intro-video-overlay');
    const video = document.getElementById('campaign-intro-video');
    const skipBtn = document.getElementById('campaign-intro-video-skip');
    if (!overlay || !video) {
      onComplete();
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      video.removeEventListener('ended', finish);
      skipBtn?.removeEventListener('click', finish);
      video.removeEventListener('click', finish);
      video.pause();
      overlay.classList.add('hidden');
      onComplete();
    };

    video.addEventListener('ended', finish);
    skipBtn?.addEventListener('click', finish);
    video.addEventListener('click', finish);

    overlay.classList.remove('hidden');
    if (!video.src || !video.src.includes('introvideo.mp4')) {
      video.src = 'assets/introvideo.mp4';
      try { video.load(); } catch (_) {}
    }
    video.currentTime = 0;
    video.play().catch(() => finish()); // autoplay blocked - skip straight to the cutscene
  }

  _startCampaignPreBattleCutscene(mission) {
    const preBattleId = mission.cutscenes?.preBattle;
    const timeline = preBattleId ? CAMPAIGN_CUTSCENES[preBattleId] : null;
    if (timeline) {
      this.cutscenePlayer.play(timeline, null);
    }
    // Combat is now armed to start the instant the player plays their
    // first card (enqueueCommand()) - no automatic cut into battle, per
    // docs/game-design-singleplayer-kampagne.md Kapitel 9.2.
    //
    // Reduced cinematic HUD (docs/singleplayer implementation soft-
    // foraging-flamingo.md): #top-hud/#bottom-hud stay hidden (the intro
    // cutscene's `hud` step never turns them back on) - only a top-left
    // menu toggle and the one playable card appear, by themselves. Runs
    // unconditionally right here rather than waiting on the (currently
    // unused) preBattle timeline's own onComplete - fine for m1_absprung,
    // which has no preBattle cutscene; a future mission that adds one
    // would need this moved into that timeline's onComplete instead.
    this.hud?.setCampaignCinematicMode?.(true);
  }

  // Checked once per tick from update() while a campaign mission is
  // active: the encounter's win condition is "every spawned enemy is
  // dead", NOT the normal base-hp check in Battlefield.update() (bases are
  // never touched in a campaign encounter - see startCampaignMission()).
  // Reuses battlefield.isGameOver/winner so the existing handleGameOver()
  // call sites keep working unchanged.
  _checkCampaignEncounterCleared() {
    if (this.battlefield.isGameOver || this._campaignEnemyUnitIds.length === 0) return;
    const allDead = this._campaignEnemyUnitIds.every(id => {
      const unit = this.battlefield.getUnitById(id);
      return !unit || unit.isDead;
    });
    if (allDead) {
      this.battlefield.isGameOver = true;
      this.battlefield.winner = this.myTeam || 'western';
    }
  }

  // Starts an AI vs AI Exhibition match where both Western and Eastern armies
  // are autonomously commanded by BotAI instances.
  startExhibitionMatch(westDeckId = null, eastDeckId = null) {
    this._clearAiRematchCountdown?.();
    this._setBaseUiVisible(true);
    // Same defensive reset as startMatch() - see its comment.
    this.hud?.setVisible?.(true);
    this.hud?.setCampaignCinematicMode?.(false);
    this._endOnlineSession();

    this.isExhibition = true;
    westDeckId = westDeckId || this.currentPlayerDeckId || 'Standard';
    if (!eastDeckId) {
      eastDeckId = this.getRandomDeckId();
    }

    const diff = this.currentAIDifficulty || document.getElementById('select-ai-difficulty')?.value || DEFAULT_AI_DIFFICULTY;
    this.aiWestern = new BotAI(this.economy, this.cardSystem, this.battlefield, this.particles, this.audio, diff, 'western', null, { superweapons: this.superweaponLedger });
    this.aiEastern = new BotAI(this.economy, this.cardSystem, this.battlefield, this.particles, this.audio, diff, 'eastern', null, { superweapons: this.superweaponLedger });
    this.ai = this.aiEastern;

    const westDeckInfo = DECKS_DATA[westDeckId] || { name: westDeckId, race: 'Human' };
    const eastDeckInfo = DECKS_DATA[eastDeckId] || { name: eastDeckId, race: 'Random' };

    this.currentPlayerDeckId = westDeckId;
    if (this.deckBuilder && westDeckId) {
      this.deckBuilder._lastSelectedDeckKey = westDeckId;
    }
    this._lastMatchMode = {
      type: 'exhibition',
      westDeckId,
      eastDeckId,
      deckId: westDeckId
    };
    clearSpriteBitmapCache();
    clearAuraBitmapCache();
    clearStatueBitmapCache();
    clearModuleBitmapCache();
    this.renderer?.releaseStaticCaches?.();
    this.particles?.reset?.();
    this.deckBuilder?.releaseBay?.();
    this.battlefield.reset();
    if (this.canvas) this.canvas._suspended = false;
    this.viewport.resize();
    this.buildings.reset();
    this.economy.reset();
    this.battlefield.economy = this.economy;
    this.economy.stats = this.battlefield.stats;
    // Both bots roll their own hidden doctrine. West keeps the player's deck
    // as chosen (only its play style is flavoured); east plays its
    // doctrine's weighting of the random race deck.
    this.cardSystem.setDeck('western', westDeckId);
    this.aiWestern.selectDoctrine(this.battlefield.bases.western.race, this.debugFlags.aiDoctrine);
    this.cardSystem.setDeck('eastern', this.aiEastern.selectDoctrine(eastDeckInfo.race, this.debugFlags.aiDoctrine, eastDeckId));
    this.battlefield.mapId = this.debugFlags.map || chooseLocalMapId(this.battlefield.bases.western.race);
    this.battlefield.mapSeed = Math.floor(Math.random() * 1000000);
    this.fillStartingHands();
    this.hud?.resetForNewMatch?.();

    if (this.hud?.avatarRenderer) {
      this.hud.avatarRenderer.setDecks(westDeckId, eastDeckId);
    }
    if (this.hud?.resetLiveGamerank) {
      this.hud.resetLiveGamerank();
    }

    this.superweaponCharges = { western: 1, eastern: 1 };
    this.nukesAvailable = this.superweaponCharges;
    this.aiWestern.resetForMatch();
    this.aiEastern.resetForMatch();

    this.activeTargetingPower = null;
    this.renderer.setTargeting(false);
    this._closeGameOverModal();
    this._lastSuperBtnKey = null;
    this.updateSuperButtonState(true);
    this.updateBaseLabels();
    // Battlefield-Ambience-Loop als atmosphärische Kulisse im Match
    this.audio.playBattlefieldAmbience(this.battlefield?.mapId);
    // Batch 1 (docs/SOUND_BATCH_PLAN.md) - "round_start", approved by the
    // user against tools/sound-review/data/batch-01.json.
    this.audio.playRoundStart();
    this.isRunning = true;
    this.isPaused = false;
    const btnPause = document.getElementById('btn-pause');
    const pauseOverlay = document.getElementById('pause-overlay');
    if (btnPause) {
      btnPause.innerHTML = ICON_PAUSE;
      btnPause.title = 'Pause';
      btnPause.classList.remove('pause-active');
    }
    if (pauseOverlay) pauseOverlay.classList.add('hidden');
    this.lastTime = performance.now();
    this._lastRenderTime = performance.now();
    this.accumulator = 0;
  }

  startOnlineMatchWS(wsClient, matchData, playerDeckId) {
    // Batch 1 (docs/SOUND_BATCH_PLAN.md) - "pre_match_hype": the online-only
    // "matchmaking found" moment, right as MATCH_START data arrives and
    // before any setup runs - round_start ("begin.ogg") follows once the
    // battlefield is actually ready further down.
    this.audio.playPreMatchHype();
    this._clearAiRematchCountdown?.();
    this.isExhibition = false;
    this.wsClient = wsClient;
    this.isOnline = true;
    document.body.classList.add('online-match-active');
    this.turnScheduler = null;
    this.myTeam = matchData.team;
    const opponentTeam = this.myTeam === 'western' ? 'eastern' : 'western';
    this.currentPlayerDeckId = playerDeckId;
    if (this.deckBuilder && playerDeckId) {
      this.deckBuilder._lastSelectedDeckKey = playerDeckId;
    }
    this.opponentOnlineLevel = Number.isFinite(matchData.opponentLevel) ? matchData.opponentLevel : 1;
    this.opponentOnlineAvatarId = typeof matchData.opponentAvatarId === 'string' ? matchData.opponentAvatarId : null;
    this.opponentOnlineAvatarBorder = typeof matchData.opponentAvatarBorder === 'string' ? matchData.opponentAvatarBorder : null;

    // Server-decided (server/matchRoom.js), never chosen locally - the
    // client never simulates online matches itself (see updateOnlineWS()),
    // so this is purely a badge/informational flag, not a speed control.
    this.is2v2 = matchData.mode === '2v2';
    this.onlineMode = matchData.mode === '2v2' ? '2v2' : (matchData.mode === 'blitz' ? 'blitz' : 'normal');
    this.myRole = this.is2v2 ? matchData.playerRole : null;
    this.teammate = this.is2v2 ? matchData.teammate : null;
    this.opponents = this.is2v2 ? matchData.opponents : null;
    this.teamSuperLockout = 0;
    this._lastMatchMode = {
      type: 'online',
      onlineMode: this.onlineMode,
      deckId: playerDeckId || this.currentPlayerDeckId || 'Standard'
    };
    document.getElementById('blitz-mode-badge')?.classList.toggle('hidden', this.onlineMode !== 'blitz');
    const isOnlineFlat = matchData?.flatDraw !== undefined ? !!matchData.flatDraw : !!BALANCE.flatDrawRates;
    document.getElementById('flat-draw-badge')?.classList.toggle('hidden', isOnlineFlat);

    clearSpriteBitmapCache();
    clearAuraBitmapCache();
    clearStatueBitmapCache();
    clearModuleBitmapCache();
    this.renderer?.releaseStaticCaches?.();
    this.particles?.reset?.();
    this.deckBuilder?.releaseBay?.();
    this.battlefield.reset();
    this.battlefield.playerTeam = this.myTeam;
    if (this.canvas) this.canvas._suspended = false;
    this.viewport.resize();
    this._startCameraIntro();
    this.buildings.reset();
    this.economy.reset();
    this.battlefield.economy = this.economy;
    this.economy.stats = this.battlefield.stats;

    this.rng.reseed(matchData.simSeed || 42);

    this.cardSystem.setDeck(this.myTeam, playerDeckId);
    const oppBaseRace = matchData.opponentRace || 'Human';
    this.cardSystem.setDeck(opponentTeam, oppBaseRace === 'Sul' ? 'Sul' : 'Standard');
    if (this.battlefield?.bases?.[opponentTeam]) {
      this.battlefield.bases[opponentTeam].race = oppBaseRace;
    }
    // Server-decided (matchRoom.js dices it next to simSeed), never chosen
    // locally here - both peers must render the same map. Falls back to
    // 'human' if an older server doesn't send one yet.
    this.battlefield.mapId = MAP_IDS.includes(matchData.mapId) ? matchData.mapId : 'human';
    this.battlefield.mapSeed = Number.isFinite(matchData.mapSeed) ? matchData.mapSeed : 0;

    if (this.is2v2) {
      const baseMax = matchData.baseMaxHp || 3000;
      if (this.battlefield?.bases) {
        if (this.battlefield.bases.western) {
          this.battlefield.bases.western.maxHp = baseMax;
          this.battlefield.bases.western.hp = baseMax;
        }
        if (this.battlefield.bases.eastern) {
          this.battlefield.bases.eastern.maxHp = baseMax;
          this.battlefield.bases.eastern.hp = baseMax;
        }
      }
      this.economy.init2v2();
      this.superweaponCharges = { w1: 1, w2: 1, e1: 1, e2: 1, western: 1, eastern: 1 };
      this.nukesAvailable = this.superweaponCharges;
    } else {
      this.superweaponCharges = { western: 1, eastern: 1 };
      this.nukesAvailable = this.superweaponCharges;
    }
    this.activeTargetingPower = null;
    this.renderer.setTargeting(false);
    this._closeGameOverModal();
    // Populated from the server's MATCH_END payload (see wsClient.onMatchEnd
    // below) - this client's own battlefield.stats never accumulates
    // anything in WS-authoritative online mode, so handleGameOver() reads
    // this instead whenever it's set.
    this._onlineFinalStats = null;
    this.hud?.resetForNewMatch?.();

    if (this.is2v2) {
      this.hud?.init2v2?.(matchData);
      this.directiveTrackerHUD?.setDisabled?.(true);
    } else {
      this.hud?.hideTeammateDock?.();
      this.directiveTrackerHUD?.setDisabled?.(false);
    }

    if (this.matchDirectiveTracker) {
      this.matchDirectiveTracker.reset();
      this.matchDirectiveTracker.myTeam = this.myTeam || 'western';
    }
    if (this.directiveTrackerHUD) {
      this.directiveTrackerHUD.hideChip();
      this.directiveTrackerHUD.setPlayerTeam?.(this.myTeam || 'western');
    }
    this._directiveCheckTimer = 0;
    this._lastSuperBtnKey = null;
    this.updateSuperButtonState(true);

    this.updateBaseLabels();
    // Battlefield-Ambience-Loop als atmosphärische Kulisse im Match
    this.audio.playBattlefieldAmbience(this.battlefield?.mapId);
    // Batch 1 (docs/SOUND_BATCH_PLAN.md) - "round_start", approved by the
    // user against tools/sound-review/data/batch-01.json.
    this.audio.playRoundStart();
    this.isRunning = true;
    this.isPaused = false;
    const btnPause = document.getElementById('btn-pause');
    const pauseOverlay = document.getElementById('pause-overlay');
    if (btnPause) {
      btnPause.innerHTML = ICON_PAUSE;
      btnPause.title = 'Pause';
      btnPause.classList.remove('pause-active');
    }
    if (pauseOverlay) pauseOverlay.classList.add('hidden');
    this.lastTime = performance.now();
    this._lastRenderTime = performance.now();
    this.accumulator = 0;

    if (!this.stateStream) {
      this.stateStream = new StateStreamManager(this);
    } else {
      this.stateStream.reset();
    }

    wsClient.onState = (snapshot) => {
      this.stateStream.applyWorldState(this, snapshot, 0.45);
    };

    // Server rejected a PLAY/DRAW this client sent (see server/matchRoom.js's
    // handleMessage()) - most commonly a plasma-affordability check that
    // passed against the client's own (now-floored, see
    // GameSimulation._buildSharedSnapshotFields()) displayed plasma but
    // failed against the server's real value a tick later. Setting these
    // same fields CardSystem.playCard()/CommandProcessor._draw() already
    // set for the offline single-player case reuses js/ui/hud.js's existing
    // updateHandUI() watchdog unchanged - it already exits the Deploy/Draw
    // Window and shakes the slot the moment either seq changes, instead of
    // leaving the slot locked until DEPLOY_WINDOW_MAX_MS's fallback timeout.
    wsClient.onCmdRejected = (data) => {
      if (data.cmdType === 'PLAY') {
        this.cardSystem.lastPlayErrorSlot = { team: this.myTeam, slot: data.slot };
        this.cardSystem.playErrorSeq = (this.cardSystem.playErrorSeq || 0) + 1;
      } else if (data.cmdType === 'DRAW') {
        this.cardSystem.lastDrawErrorSlot = { team: this.myTeam, slot: data.slot };
        this.cardSystem.drawErrorSeq = (this.cardSystem.drawErrorSeq || 0) + 1;
      }
    };

    wsClient.onTaunt = (data) => {
      const team = data?.sender || (this.myTeam === 'western' ? 'eastern' : 'western');
      const tauntId = data?.tauntId || data?.text;
      if (tauntId) {
        this.tauntManager?.showTauntBubble?.(team, tauntId);
      }
    };

    this.isOnlinePaused = false;
    this.onlinePauseState = null;
    this._onlineDisconnectReason = null;
    this._onlineDisconnectedTeam = null;
    this.hideOnlineReconnectOverlay();

    wsClient.onConnectionLost = (info) => {
      this.showOnlineReconnectOverlay({
        type: 'self_disconnect',
        remainingSec: info.remainingSec,
        deadline: info.deadline
      });
    };

    wsClient.onReconnecting = (info) => {
      this.updateOnlineReconnectCountdown(info.remainingSec);
    };

    wsClient.onReconnected = () => {
      this.updateOnlineReconnectStatus({
        status: 'syncing',
        badgeText: 'SYNCHRONIZING',
        titleText: 'CONNECTION RESTORED',
        descText: 'Connection re-established. Authoritative game state synchronized...'
      });
    };

    wsClient.onMatchPaused = (data) => {
      if (data.reason === 'disconnect') {
        const oppName = data.disconnectedPlayerName || this.netClient?.opponentName || 'Opponent';
        this.showOnlineReconnectOverlay({
          type: 'opponent_disconnect',
          opponentName: oppName,
          remainingSec: data.gracePeriodSec || 20,
          deadline: data.deadline
        });
      }
    };

    wsClient.onOpponentDisconnected = (data) => {
      const oppName = data.playerName || this.netClient?.opponentName || 'Opponent';
      this.showOnlineReconnectOverlay({
        type: 'opponent_disconnect',
        opponentName: oppName,
        remainingSec: data.gracePeriodSec || 20,
        deadline: data.deadline
      });
    };

    wsClient.onOpponentReconnected = (data) => {
      const oppName = data.playerName || this.netClient?.opponentName || 'Opponent';
      this.updateOnlineReconnectStatus({
        status: 'opponent_back',
        badgeText: 'CONNECTED',
        titleText: 'OPPONENT RETURNED',
        descText: `${oppName} has returned to the match. Synchronizing battle state...`
      });
    };

    wsClient.onMatchResync = () => {
      this.updateOnlineReconnectStatus({
        status: 'synced',
        badgeText: 'SYNCHRONIZED',
        titleText: 'BATTLEFIELD SYNCED',
        descText: 'Authoritative world state verified. Preparing resumption...'
      });
    };

    wsClient.onMatchResuming = (data) => {
      this.startOnlineResumeCountdown(data.countdownSec || 3, data.resumeAt);
    };

    wsClient.onMatchResumed = () => {
      this.hideOnlineReconnectOverlay();
    };

    wsClient.onDisconnect = () => {
      if (this.isOnline && this.isRunning) {
        this.hideOnlineReconnectOverlay();
        this._onlineDisconnectReason = 'disconnect_timeout';
        this.battlefield.isGameOver = true;
        this.battlefield.winner = this.myTeam === 'western' ? 'eastern' : 'western';
        this.handleGameOver();
      }
    };

    wsClient.onMatchEnd = (endData) => {
      this.hideOnlineReconnectOverlay();
      this._onlineDisconnectReason = endData?.reason;
      this._onlineDisconnectedTeam = endData?.disconnectedTeam;
      this._onlineFinalStats = endData?.stats ? MatchStats.deserialize(endData.stats) : null;
      this.battlefield.isGameOver = true;
      this.battlefield.winner = endData.winner;
      this.handleGameOver();
      setTimeout(() => fetchProfile().catch(() => {}), 1500);
      setTimeout(() => fetchProfile().catch(() => {}), 4500);
    };

    wsClient.onError = (msg) => {
      console.warn('[UCG WS] Match error:', msg);
    };
  }

  // The western/eastern boss bars are always laid out left/right on the
  // canvas (western spawns/attacks from the left - see
  // js/engine/battlefield.js) - only the "(YOU)"/"(ENEMY)" labels flip
  // based on which side is actually mine. Single-player always shows the
  // original "(YOU)"/"(ENEMY)" wording since myTeam is always 'western' there.
  updateBaseLabels() {
    // The short "WEST"/"EAST" mobile-portrait labels (see .name-short in
    // css/main.css) never change with YOU/ENEMY context - only the
    // full-length text does, so this only ever touches .name-full.
    const westernLabel = document.querySelector('.boss-name.western .name-full');
    const easternLabel = document.querySelector('.boss-name.eastern .name-full');
    if (westernLabel && easternLabel) {
      if (this.isExhibition) {
        westernLabel.innerText = 'WEST (AI)';
        easternLabel.innerText = 'EAST (AI)';
      } else if (!this.isOnline) {
        const myName = getDisplayName();
        westernLabel.innerText = myName && myName !== 'Commander' ? `WEST (${myName})` : 'WEST (YOU)';
        easternLabel.innerText = 'EAST (AI)';
      } else if (this.is2v2) {
        const myName = getDisplayName() && getDisplayName() !== 'Commander' ? getDisplayName() : 'YOU';
        const mateName = this.teammate?.name || 'ALLY';
        const opp1Name = this.opponents?.[0]?.name || 'ENEMY 1';
        const opp2Name = this.opponents?.[1]?.name || 'ENEMY 2';
        const teamStr = `${myName} & ${mateName}`;
        const oppStr = `${opp1Name} & ${opp2Name}`;
        westernLabel.innerText = this.myTeam === 'western' ? `WEST (${teamStr})` : `WEST (${oppStr})`;
        easternLabel.innerText = this.myTeam === 'eastern' ? `EAST (${teamStr})` : `EAST (${oppStr})`;
      } else {
        const myName = getDisplayName() && getDisplayName() !== 'Commander' ? getDisplayName() : 'YOU';
        const oppName = this.netClient?.opponentName || 'ENEMY';
        westernLabel.innerText = this.myTeam === 'western' ? `WEST (${myName})` : `WEST (${oppName})`;
        easternLabel.innerText = this.myTeam === 'eastern' ? `EAST (${myName})` : `EAST (${oppName})`;
      }
    }

    // Live Combat Performance Grade bar: both a western and an eastern
    // instance exist in the markup (see index.html's boss-gauge-stack.western
    // / .eastern), because whichever side the local human is actually
    // playing needs its own live bar - in online 1v1 matches this.myTeam can
    // genuinely be 'eastern' for the second player. Only ONE side is ever
    // "mine": Exhibition (AI vs AI) makes neither side mine, single-player
    // always makes western mine (myTeam defaults to 'western' there, same as
    // the YOU/ENEMY labels above), and online mode follows this.myTeam. The
    // non-mine side is never fed real segment data (see hud.js's
    // updateLiveGamerank(), which only ever touches _rankBarSegs[myTeam]) -
    // it just keeps showing the same dimmed `.placeholder` shell class used
    // here, rather than display:none, to avoid a layout reflow between the
    // rank-bar row and the hp-bar row below it in .boss-gauge-stack (see the
    // #resource-dashboard reparenting-bug comment below for why this
    // codebase treats that kind of jank as a real bug, not cosmetic).
    const westRankBar = document.getElementById('western-live-rank-bar');
    const westRankTag = document.getElementById('western-rank-live-tag');
    const eastRankBar = document.getElementById('eastern-live-rank-bar');
    const eastRankTag = document.getElementById('eastern-rank-live-tag');

    const showWestern = !this.isExhibition && this.myTeam !== 'eastern';
    const showEastern = !this.isExhibition && this.myTeam === 'eastern';

    if (westRankBar) {
      westRankBar.classList.toggle('placeholder', !showWestern);
      if (westRankTag) westRankTag.style.display = showWestern ? '' : 'none';
    }
    if (eastRankBar) {
      eastRankBar.classList.toggle('placeholder', !showEastern);
      if (eastRankTag) eastRankTag.style.display = showEastern ? '' : 'none';
    }
    document.querySelector('.boss-avatar.western')?.classList.remove('on-fire', 'on-fire-s-plus');
    document.querySelector('.boss-avatar.eastern')?.classList.remove('on-fire', 'on-fire-s-plus');

    this.directiveTrackerHUD?.setPlayerTeam?.(this.myTeam || 'western');
    this.tauntManager?.setPlayerTeam?.(this.myTeam || 'western');
    this._applyPlayerCosmetics?.();

    // Update team-specific styling for the recessed cards docking tray (#cards-hand-container):
    // Subtle bluish (Western) vs reddish (Eastern) tint gives the player immediate visual
    // confirmation of which side they are commanding.
    const handContainer = document.getElementById('cards-hand-container');
    if (handContainer) {
      const isEastern = this.myTeam === 'eastern';
      handContainer.classList.toggle('team-western', !isEastern);
      handContainer.classList.toggle('team-eastern', isEastern);
    }

    // #resource-dashboard used to live inside whichever boss-column was
    // "mine" (moved here between western/eastern on every call, since it
    // always shows MY OWN plasma - see hud.js's update(), which reads
    // this.game.economy.resources[this.myTeam]). It now lives permanently
    // in the plasma capsule at .top-hud-center-slot (see index.html),
    // which is centered independent of team, so it no longer needs to
    // move at all - reparenting it here was actively wrong: it repeatedly
    // yanked the dashboard out of the capsule's markup (where css/main.css's
    // .plasma-reactor-housing-scoped rules style it) into a boss-column,
    // where those rules no longer match, leaving the numbers unstyled and
    // effectively invisible - see chat report "nach einem Neustart
    // verschwinden die numerischen Angaben".
  }

  pauseGame() {
    if (this.isOnline || this.isPaused || !this.isRunning) return;
    this.isPaused = true;
    if (this.hud) this.hud._hasRenderedPausedHud = false;
    const btnPause = document.getElementById('btn-pause');
    const pauseOverlay = document.getElementById('pause-overlay');
    if (btnPause) {
      btnPause.innerHTML = ICON_PLAY;
      btnPause.title = 'Resume';
      btnPause.classList.add('pause-active');
    }
    if (pauseOverlay) pauseOverlay.classList.remove('hidden');
    this._positionPauseOverlay();
    this.tauntManager?.closeMenu?.();
  }

  resumeGame() {
    if (this.isOnline || !this.isPaused || !this.isRunning) return;
    this.isPaused = false;
    if (this.hud) this.hud._hasRenderedPausedHud = false;
    const btnPause = document.getElementById('btn-pause');
    const pauseOverlay = document.getElementById('pause-overlay');
    if (btnPause) {
      btnPause.innerHTML = ICON_PAUSE;
      btnPause.title = 'Pause';
      btnPause.classList.remove('pause-active');
    }
    if (pauseOverlay) pauseOverlay.classList.add('hidden');
  }

  // True only while an online match is actually being played. `isOnline`
  // alone also stays true on the post-game screen (handleGameOver() reads
  // it for stats/rewards and the frozen frame still renders with it), so
  // guards that mean "no menus mid-match" must use this instead.
  isOnlineMatchLive() {
    return this.isOnline && this.isRunning;
  }

  // Single teardown for the online session, shared by every path that
  // leaves it: the next single-player/campaign/exhibition start, the
  // post-game menus (via pauseForMenu()) and the return to the title
  // screen. Idempotent - a no-op when no online session is set up.
  _endOnlineSession() {
    if (!this.isOnline) return;
    // Leaving while the match is still live counts as a surrender, like
    // OnlineLobby.leaveMatch(); after MATCH_END the client is already
    // inactive and only the socket is closed.
    if (this.wsClient?.isMatchActive) this.wsClient.sendSurrender?.();
    this.wsClient?.leave?.();
    this.wsClient = null;
    if (this.onlineLobby) {
      this.onlineLobby.wsClient = null;
    }
    this.isOnline = false;
    document.body.classList.remove('online-match-active');
    this.opponentOnlineLevel = null;
    this.opponentOnlineAvatarId = null;
    this.opponentOnlineAvatarBorder = null;
    this.is2v2 = false;
    this.teammate = null;
    this.opponents = null;
    this.directiveTrackerHUD?.setDisabled?.(false);
    this.myTeam = 'western';
    this.netMatch = null;
    this.turnScheduler?.destroy?.();
    this.turnScheduler = null;
    // Otherwise both hand-draw sources would still point at the old
    // online match (a dead remote buffer for 'western', a permanent
    // HIDDEN_CARD placeholder for 'eastern') instead of falling back to
    // cardSystem.rng - see js/engine/cardSystem.js's drawCard().
    this.cardSystem.setDrawSource('western', null);
    this.cardSystem.setDrawSource('eastern', null);
    const leaveBtn = document.getElementById('btn-leave-online');
    if (leaveBtn) leaveBtn.style.display = 'none';
    const stallIndicator = document.getElementById('online-stall-indicator');
    if (stallIndicator) stallIndicator.classList.add('hidden');
    this.hideOnlineReconnectOverlay();
    document.getElementById('blitz-mode-badge')?.classList.add('hidden');
    document.getElementById('flat-draw-badge')?.classList.add('hidden');
    this.onlineMode = 'normal';
  }

  // A/B flag 'freematch' (js/ui/abFlags.js): a finished match's baked background/base canvases are
  // dead weight while the player sits in a menu. They re-bake on the next render.
  // When called while a match is paused via menu (or forced), only pure render/audio caches are
  // evicted so live match simulation state and the player's hand DOM stay 100% intact.
  releaseMatchMemory(force = false) {
    if ((this.isRunning && !force && !this._pausedByMenu) || this.isOnline) return false;
    this.renderer?.releaseStaticCaches?.();

    const isMobile = typeof window !== 'undefined' && (
      /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') ||
      (window.matchMedia && (window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(max-width: 900px)').matches)) ||
      (navigator.maxTouchPoints > 0) ||
      (window.innerWidth <= 900 || window.innerHeight <= 500)
    );
    this.audio?.releaseMatchWarmSamples?.();
    this.audio?.trimSampleCache?.(isMobile ? 8 : 16);

    // Only tear down battlefield arrays, particles, post-game MVP canvas, and hand DOM
    // when the match has actually finished (!this.isRunning). Preserves winner/isGameOver/stats
    // for the post-game return overlay and rematch flow.
    if (!this.isRunning) {
      this.particles?.reset?.();
      if (this.battlefield) {
        this.battlefield.units = [];
        this.battlefield.projectiles = [];
        this.battlefield.radioactiveCraters = [];
        this.battlefield.activePrecisionStrikes = [];
        this.battlefield.selectedUnit = null;
      }
      this._unitMvpSpotlightController?.stop?.();
      this._unitMvpSpotlightController = null;
      const mvpCanvas = typeof document !== 'undefined' ? document.getElementById('unit-mvp-canvas') : null;
      if (mvpCanvas) {
        mvpCanvas.width = 0;
        mvpCanvas.height = 0;
      }
      const handContainer = typeof document !== 'undefined' ? document.getElementById('cards-hand-container') : null;
      if (handContainer) {
        const handImgs = handContainer.querySelectorAll('img');
        handImgs.forEach(img => {
          img.onload = null;
          img.onerror = null;
          img.src = '';
        });
        handContainer.innerHTML = '';
      }
    }
    return true;
  }

  // Temporarily parks the main #game-canvas (and minimap) backing stores at 0x0 while a fullscreen
  // modal like Cargo Bay covers the viewport on mobile, freeing 8-16 MB of contiguous GPU VRAM.
  suspendMainCanvas() {
    if (this.isOnline) return false;
    if (this.canvas) {
      this.canvas._suspended = true;
      this.canvas.width = 0;
      this.canvas.height = 0;
    }
    if (this.minimap?.canvas) {
      this.minimap.canvas.width = 0;
      this.minimap.canvas.height = 0;
    }
    return true;
  }

  restoreMainCanvas() {
    if (!this.canvas || !this.canvas._suspended) return false;
    this.canvas._suspended = false;
    this.viewport?.resize?.();
    if (!this.isRunning && this.battlefield && this.renderer) {
      try {
        this.renderer.render(this.battlefield, this.particles);
      } catch (_) {}
    }
    return true;
  }

  // Tier 1 cleanup: runs when opening menus like Cargo Bay on mobile.
  // Sheds match memory, releases decoded card thumbnails, and frees warmed match audio pins.
  releaseMenuCaches() {
    const isMobile = typeof window !== 'undefined' && (
      /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') ||
      (window.matchMedia && (window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(max-width: 900px)').matches)) ||
      (navigator.maxTouchPoints > 0) ||
      (window.innerWidth <= 900 || window.innerHeight <= 500)
    );
    if (!isMobile) return false;

    // Release match remnants if match is not running or paused by menu
    this.releaseMatchMemory();

    // Park the hidden #game-canvas backing store and release the Archive Hangar planes
    // immediately on menu open so WebKit reclaims ~66 MB seconds before pack tearing begins
    this.suspendMainCanvas();
    this.deckBuilder?.releaseBay?.();

    // Release preloaded thumbnail decodes (~48 MB WebKit image memory)
    releasePreloadedThumbnails();

    // Unpin match-specific unit samples and trim audio down to 10 MB
    this.audio?.releaseMatchWarmSamples?.();
    this.audio?.trimSampleCache?.(10);

    return true;
  }

  // Tier 2 deep cleanup: runs right before an intensive pack tear/reveal sequence begins on mobile.
  // Releases the heavy archive hangar background (5x 2048x1152 planes + 2 canvases),
  // zero-resizes the static glass canopy canvas, parks the hidden main game canvas, and wipes specialized sprite & slot caches.
  releaseDeepPreRevealCaches() {
    const isMobile = typeof window !== 'undefined' && (
      /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '') ||
      (window.matchMedia && (window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(max-width: 900px)').matches)) ||
      (navigator.maxTouchPoints > 0) ||
      (window.innerWidth <= 900 || window.innerHeight <= 500)
    );
    if (!isMobile) return false;

    // Force-release all match render caches and finished match remnants
    this.releaseMatchMemory(true);

    // Park the hidden #game-canvas backing store at 0x0 during pack tear/reveal
    this.suspendMainCanvas();

    // Destroy and unmount the archive bay background (re-mounted on next Archive open)
    this.deckBuilder?.releaseBay?.();

    // Zero the full-screen glass canopy canvas (re-baked on next match render)
    this.renderer?.releaseStaticGlassCanopy?.();

    // Clear unit aura, statue, sprite, and module bitmap caches (re-baked on demand)
    clearAuraBitmapCache();
    clearStatueBitmapCache();
    clearSpriteBitmapCache();
    clearModuleBitmapCache();

    // Clear slot machine reel image cache if present
    this.slotMachineModal?.clearImageCache?.();

    // Trim audio cache down to 8 MB before pack reveal
    this.audio?.trimSampleCache?.(8);

    return true;
  }

  pauseForMenu(menuId = 'default') {
    // The player is leaving a finished online match for a menu (Archive,
    // Cargo Bay, ...): drop the online session first so menus and
    // background loops stop treating this as a live match. Deliberately
    // here and not in handleGameOver(), which (and the frozen frame behind
    // the game-over modal) still needs isOnline/myTeam.
    if (this.isOnline && !this.isRunning) this._endOnlineSession();
    if (this.isOnline) return;
    const wasEmpty = this._openMenus.size === 0;
    this._openMenus.add(menuId);
    if (wasEmpty) {
      this.overlayGate?.acquire?.();
      if (!this.isPaused && this.isRunning) {
        this._pausedByMenu = true;
        this.pauseGame();
      }
    }
  }

  resumeForMenu(menuId = 'default') {
    if (this.isOnline) return;
    this._openMenus.delete(menuId);
    if (this._openMenus.size === 0) {
      this.overlayGate?.release?.();
      this.restoreMainCanvas();
      if (this._pausedByMenu) {
        this._pausedByMenu = false;
        this.resumeGame();
      }
    }
  }

  // Covers the frozen last frame left behind once a finished match's
  // game-over-modal is dismissed for the Bridge/Deck Builder - otherwise
  // closing those (e.g. closing the Bridge) drops the player back onto a
  // static, un-blurred canvas with no way back to a rematch short of a
  // reload. Sits behind those modals (lower z-index, see css/main.css) so
  // it's invisible while they're open and only revealed once they close.
  _showPostGameReturnOverlay() {
    document.getElementById('post-game-return-overlay')?.classList.remove('hidden');
    this._positionPostGameReturnOverlayButton();
  }

  _hidePostGameReturnOverlay() {
    document.getElementById('post-game-return-overlay')?.classList.add('hidden');
  }

  // #canvas-container's own box reaches well past the visible playfield on
  // desktop (margin-bottom:-280px, #bottom-hud sits on top of that hidden
  // strip - see css/main.css), so a plain CSS align-items:center inside it
  // centers against that oversized box instead of what the player can
  // actually see, landing centered content off-middle - see chat reports
  // "der button sitzt bei mir nicht in der mitte der freien Fläche" (Rematch
  // button) and "paused muss in der Mitte der Spielfläche angezeigt werden"
  // (pause overlay). Measure the real gap at call time instead of guessing a
  // fixed offset per breakpoint: shrink the given inner element's bottom
  // edge up to wherever #bottom-hud visually starts covering
  // canvas-container, then let its own flex centering do the rest - correct
  // on any viewport size or layout mode, including ones with no overlap at
  // all (bottom-hud stacked below canvas-container in normal flow, e.g. some
  // mobile layouts), where the computed gap is just 0.
  _positionOverlayOnVisiblePlayfield(innerId) {
    const inner = document.getElementById(innerId);
    const canvasContainer = document.getElementById('canvas-container');
    const bottomHud = document.getElementById('bottom-hud');
    if (!inner || !canvasContainer || !bottomHud) return;
    const canvasRect = canvasContainer.getBoundingClientRect();
    const bottomHudRect = bottomHud.getBoundingClientRect();
    const hiddenOverlap = Math.max(0, canvasRect.bottom - bottomHudRect.top);
    inner.style.bottom = `${hiddenOverlap}px`;
  }

  _positionPostGameReturnOverlayButton() {
    this._positionOverlayOnVisiblePlayfield('post-game-return-overlay-inner');
  }

  _positionPauseOverlay() {
    this._positionOverlayOnVisiblePlayfield('pause-overlay-inner');
  }

  _positionOnlineReconnectOverlay() {
    this._positionOverlayOnVisiblePlayfield('online-reconnect-overlay-inner');
  }

  showOnlineReconnectOverlay(opts = {}) {
    const overlay = document.getElementById('online-reconnect-overlay');
    if (!overlay) return;

    this.isOnlinePaused = true;
    this.onlinePauseState = opts;
    if (this.hud?.resolveCardDrag) {
      this.hud.resolveCardDrag('cancel');
    }

    const badge = document.getElementById('online-reconnect-badge');
    const badgeText = document.getElementById('online-reconnect-badge-text');
    const title = document.getElementById('online-reconnect-title');
    const desc = document.getElementById('online-reconnect-desc');
    const timerWrap = document.getElementById('online-reconnect-timer-wrap');
    const timerBar = document.getElementById('online-reconnect-timer-bar');
    const timerLabel = document.getElementById('online-reconnect-timer-label');
    const countdownWrap = document.getElementById('online-reconnect-countdown-wrap');

    if (countdownWrap) countdownWrap.classList.add('hidden');
    if (timerWrap) timerWrap.classList.remove('hidden');

    if (this._onlinePauseTimerInterval) {
      clearInterval(this._onlinePauseTimerInterval);
      this._onlinePauseTimerInterval = null;
    }

    const totalSec = opts.gracePeriodSec || opts.remainingSec || 20;
    const deadline = opts.deadline || (Date.now() + (totalSec * 1000));

    if (opts.type === 'self_disconnect') {
      if (badge) badge.className = 'online-reconnect-badge badge-error';
      if (badgeText) badgeText.innerText = 'LINK DROPPED';
      if (title) title.innerText = 'RECONNECTING...';
      if (desc) desc.innerText = 'Connection to game server lost. Re-establishing link to match...';
    } else {
      const oppName = opts.opponentName || 'Opponent';
      if (badge) badge.className = 'online-reconnect-badge';
      if (badgeText) badgeText.innerText = 'OPPONENT OFFLINE';
      if (title) title.innerText = 'MATCH PAUSED';
      if (desc) desc.innerText = `${oppName} has lost connection. Game simulation paused to preserve fair play.`;
    }

    const updateTimer = () => {
      const remainingMs = Math.max(0, deadline - Date.now());
      const remainingSec = Math.ceil(remainingMs / 1000);
      const pct = Math.max(0, Math.min(100, (remainingMs / (totalSec * 1000)) * 100));

      if (timerBar) timerBar.style.width = `${pct}%`;
      if (timerLabel) timerLabel.innerText = `${remainingSec}s remaining`;

      if (remainingMs <= 0 && this._onlinePauseTimerInterval) {
        clearInterval(this._onlinePauseTimerInterval);
        this._onlinePauseTimerInterval = null;
      }
    };

    updateTimer();
    this._onlinePauseTimerInterval = setInterval(updateTimer, 250);

    this._positionOnlineReconnectOverlay();
    overlay.classList.remove('hidden');
    this.audio?.playNetworkAlert?.();
  }

  updateOnlineReconnectCountdown(remainingSec, totalSec = 20) {
    const timerBar = document.getElementById('online-reconnect-timer-bar');
    const timerLabel = document.getElementById('online-reconnect-timer-label');
    const pct = Math.max(0, Math.min(100, (remainingSec / totalSec) * 100));
    if (timerBar) timerBar.style.width = `${pct}%`;
    if (timerLabel) timerLabel.innerText = `${remainingSec}s remaining`;
  }

  updateOnlineReconnectStatus({ status, text, badgeText, titleText, descText }) {
    const badge = document.getElementById('online-reconnect-badge');
    const badgeTextEl = document.getElementById('online-reconnect-badge-text');
    const title = document.getElementById('online-reconnect-title');
    const desc = document.getElementById('online-reconnect-desc');

    if (badge) {
      if (status === 'connected' || status === 'synced' || status === 'opponent_back') {
        badge.className = 'online-reconnect-badge badge-connected';
      } else if (status === 'syncing') {
        badge.className = 'online-reconnect-badge badge-syncing';
      } else if (status === 'error') {
        badge.className = 'online-reconnect-badge badge-error';
      }
    }

    if (badgeTextEl && badgeText) badgeTextEl.innerText = badgeText;
    if (title && titleText) title.innerText = titleText;
    if (desc && (descText || text)) desc.innerText = descText || text;
  }

  startOnlineResumeCountdown(countdownSec = 3, resumeAt = null) {
    const timerWrap = document.getElementById('online-reconnect-timer-wrap');
    const countdownWrap = document.getElementById('online-reconnect-countdown-wrap');
    const countdownNum = document.getElementById('online-reconnect-countdown-num');
    const badge = document.getElementById('online-reconnect-badge');
    const badgeText = document.getElementById('online-reconnect-badge-text');
    const title = document.getElementById('online-reconnect-title');
    const desc = document.getElementById('online-reconnect-desc');

    if (this._onlinePauseTimerInterval) {
      clearInterval(this._onlinePauseTimerInterval);
      this._onlinePauseTimerInterval = null;
    }

    if (timerWrap) timerWrap.classList.add('hidden');
    if (countdownWrap) countdownWrap.classList.remove('hidden');

    if (badge) badge.className = 'online-reconnect-badge badge-connected';
    if (badgeText) badgeText.innerText = 'ALL COMMANDERS READY';
    if (title) title.innerText = 'RESUMING MATCH';
    if (desc) desc.innerText = 'All players connected. Synchronizing battlefield and resuming combat!';

    let current = countdownSec;
    if (countdownNum) countdownNum.innerText = current;
    this.audio?.playSample?.('beep_01.ogg', { category: 'ui', volume: 0.5 });

    const targetTime = resumeAt || (Date.now() + (countdownSec * 1000));
    this._onlinePauseTimerInterval = setInterval(() => {
      const remainingMs = targetTime - Date.now();
      const nextVal = Math.max(1, Math.ceil(remainingMs / 1000));
      if (nextVal !== current) {
        current = nextVal;
        if (countdownNum) countdownNum.innerText = current;
        this.audio?.playSample?.('beep_01.ogg', { category: 'ui', volume: 0.5 });
      }
      if (remainingMs <= 0) {
        clearInterval(this._onlinePauseTimerInterval);
        this._onlinePauseTimerInterval = null;
      }
    }, 100);
  }

  hideOnlineReconnectOverlay() {
    const overlay = document.getElementById('online-reconnect-overlay');
    if (overlay) overlay.classList.add('hidden');

    if (this._onlinePauseTimerInterval) {
      clearInterval(this._onlinePauseTimerInterval);
      this._onlinePauseTimerInterval = null;
    }

    this.isOnlinePaused = false;
    this.onlinePauseState = null;
    this.audio?.playNetworkResumed?.();
  }

  getLastMatchMode() {
    if (this._lastMatchMode) return this._lastMatchMode;
    if (this.isOnline) {
      return {
        type: 'online',
        onlineMode: this.onlineMode || 'normal',
        deckId: this.currentPlayerDeckId || 'Standard'
      };
    }
    if (this.isExhibition) {
      return {
        type: 'exhibition',
        deckId: this.currentPlayerDeckId || 'Standard'
      };
    }
    return {
      type: 'singleplayer',
      deckId: this.currentPlayerDeckId || 'Standard'
    };
  }

  handleRematch() {
    this._clearAiRematchCountdown?.();
    this.audio?.playUiClick?.();
    this._closeGameOverModal();
    this._hidePostGameReturnOverlay();

    if (this._isChallengeBriefing) {
      this._isChallengeBriefing = false;
      this._challengePlayed = true;
      this.startMatch(this.currentPlayerDeckId || 'Standard');
      return;
    }

    const last = this.getLastMatchMode();

    if (last.type === 'online') {
      const mode = last.onlineMode || 'normal';
      const deckId = last.deckId || this.currentPlayerDeckId || 'Standard';
      this._endOnlineSession();
      if (this.onlineLobby) {
        this.onlineLobby.startQueue(mode, deckId);
      }
    } else if (last.type === 'exhibition') {
      this.startExhibitionMatch(last.westDeckId || last.deckId || this.currentPlayerDeckId || 'Standard', last.eastDeckId || null);
    } else {
      this.startMatch(last.deckId || this.currentPlayerDeckId || 'Standard');
    }
  }

  initRematchButton() {
    const btn = document.getElementById('btn-rematch');
    if (btn) {
      btn.addEventListener('click', () => {
        this.handleRematch();
      });
    }
    const btnDare = document.getElementById('btn-game-over-dare');
    if (btnDare) {
      btnDare.addEventListener('click', () => {
        this.handleDareShare();
      });
    }
    const btnRematchReturn = document.getElementById('btn-rematch-return');
    if (btnRematchReturn) {
      btnRematchReturn.addEventListener('click', () => {
        this.handleRematch();
      });
    }
    const btnDecksReturn = document.getElementById('btn-decks-return');
    if (btnDecksReturn) {
      btnDecksReturn.addEventListener('click', () => {
        this._closeGameOverModal();
        this.pauseForMenu('deckSelector');
        this.deckBuilder?.open();
      });
    }
    const btnBridge = document.getElementById('btn-game-over-bridge');
    if (btnBridge) {
      btnBridge.addEventListener('click', () => {
        this._closeGameOverModal();
        this.audio?.stopAmbience?.();
        this._showPostGameReturnOverlay();
        this.pauseForMenu('cargoBay');
        this.packOpeningModal?.open();
      });
    }
    const btnDecks = document.getElementById('btn-game-over-decks');
    if (btnDecks) {
      btnDecks.addEventListener('click', () => {
        this._closeGameOverModal();
        this.audio?.stopAmbience?.();
        this._showPostGameReturnOverlay();
        this.pauseForMenu('deckSelector');
        const lastDeck = this.currentPlayerDeckId || (this.deckBuilder?.getLastUsedDeckKey ? this.deckBuilder.getLastUsedDeckKey() : 'Human');
        this.deckBuilder?.openModal({ selectDeckKey: lastDeck });
      });
    }
    const btnGameOverDirectives = document.getElementById('btn-game-over-directives');
    if (btnGameOverDirectives) {
      btnGameOverDirectives.addEventListener('click', () => {
        this.audio?.playUiClick?.();
        this.pauseForMenu('directives');
        this.directivesModal?.open();
      });
    }

    // Directives Modal & Quick Actions Wiring
    const btnDirectives = document.getElementById('btn-open-directives');
    if (btnDirectives) {
      btnDirectives.addEventListener('click', () => {
        this.pauseForMenu('directives');
        this.directivesModal?.open();
      });
    }

    // Booster Packs Modal Wiring (Bridge)
    const btnOpenPacks = document.getElementById('btn-open-packs');
    if (btnOpenPacks) {
      btnOpenPacks.addEventListener('click', () => {
        this.pauseForMenu('cargoBay');
        this.packOpeningModal?.open();
      });
    }
    const btnQuickBridge = document.getElementById('btn-quick-bridge') || document.getElementById('btn-quick-packs');
    if (btnQuickBridge) {
      btnQuickBridge.addEventListener('click', () => {
        this.pauseForMenu('cargoBay');
        this.packOpeningModal?.open();
      });
    }
    const btnQuickDecks = document.getElementById('btn-quick-decks');
    if (btnQuickDecks) {
      btnQuickDecks.addEventListener('click', () => {
        this.pauseForMenu('deckSelector');
        this.deckBuilder?.open();
      });
    }
    const btnOpenDecks = document.getElementById('btn-open-decks');
    if (btnOpenDecks) {
      btnOpenDecks.addEventListener('click', () => {
        this.pauseForMenu('deckSelector');
        this.deckBuilder?.open();
      });
    }

    // Cyber Slot Machine Modal Wiring
    const btnOpenSlots = document.getElementById('btn-open-slots');
    if (btnOpenSlots) {
      btnOpenSlots.addEventListener('click', () => {
        this.pauseForMenu('slotMachine');
        this.slotMachineModal?.open();
      });
    }
    const btnQuickSlots = document.getElementById('btn-quick-slots');
    if (btnQuickSlots) {
      btnQuickSlots.addEventListener('click', () => {
        this.pauseForMenu('slotMachine');
        this.slotMachineModal?.open();
      });
    }

    // Dual Currency Bar Links (Fugazi -> Packs, Spins -> Slots)
    const hudFugaziPill = document.getElementById('hud-fugazi-pill');
    if (hudFugaziPill) {
      const openPacks = () => {
        this.pauseForMenu('cargoBay');
        this.packOpeningModal?.open();
      };
      hudFugaziPill.addEventListener('click', openPacks);
      hudFugaziPill.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPacks();
        }
      });
    }

    const hudTicketsPill = document.getElementById('hud-tickets-pill');
    if (hudTicketsPill) {
      const openSlots = () => {
        this.pauseForMenu('slotMachine');
        this.slotMachineModal?.open();
      };
      hudTicketsPill.addEventListener('click', openSlots);
      hudTicketsPill.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openSlots();
        }
      });
    }

    // Cyber Hangar & Showroom Modal Wiring
    const btnOpenShowroom = document.getElementById('btn-open-showroom');
    if (btnOpenShowroom) {
      btnOpenShowroom.addEventListener('click', () => {
        this.pauseForMenu('showroom');
        this.showroomModal?.open();
      });
    }
    const btnQuickShowroom = document.getElementById('btn-quick-showroom');
    if (btnQuickShowroom) {
      btnQuickShowroom.addEventListener('click', () => {
        this.pauseForMenu('showroom');
        this.showroomModal?.open();
      });
    }

    // Synchronize Fugazi & Spin Tickets Counters in HUD / Setup Drawer & Telemetry Bar
    const updateWalletPills = () => {
      const fugaziFormatted = wallet.getFugazi().toLocaleString();
      const ticketsFormatted = wallet.getTickets().toLocaleString();

      const fugaziEl = document.getElementById('hud-fugazi-val');
      if (fugaziEl) fugaziEl.textContent = fugaziFormatted;
      const teleFugazi = document.getElementById('telemetry-fugazi-val');
      if (teleFugazi) teleFugazi.textContent = fugaziFormatted;

      const ticketsEl = document.getElementById('hud-tickets-val');
      if (ticketsEl) ticketsEl.textContent = ticketsFormatted;
      const teleTickets = document.getElementById('telemetry-tickets-val');
      if (teleTickets) teleTickets.textContent = ticketsFormatted;
    };
    updateWalletPills();
    wallet.addListener(() => updateWalletPills());

    // Synchronize MMR & Rank Badge in Telemetry Bar
    const updateTelemetryMMR = () => {
      const mmr = getMMR() || 1500;
      const teleMmrVal = document.getElementById('telemetry-mmr-val');
      if (teleMmrVal) teleMmrVal.textContent = mmr.toLocaleString();
      const teleMmrGlyph = document.getElementById('telemetry-mmr-glyph');
      if (teleMmrGlyph) {
        const tier = getRankTier(mmr);
        teleMmrGlyph.innerHTML = tier?.icon || getIconSvg('rank_platinum', 14);
        teleMmrGlyph.style.color = tier?.color || '#00f0ff';
      }
    };
    updateTelemetryMMR();
    addIdentityListener(() => updateTelemetryMMR());

    // Blends a #rrggbb style hex toward black by `amount` (0..1) - used to
    // derive a dark tray-tint gradient from a single flat swatch hex instead
    // of storing a second color per Card Tray Glow style.
    const mixHexWithBlack = (hex, amount) => {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      const mix = (c) => Math.round(c * (1 - amount));
      return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
    };

    // Global Player Cosmetics (Frame skin, HP Bar color, Avatar border, Card Tray Glow):
    // Sets CSS custom properties on :root and updates player-team UI elements.
    const applyEquippedPlayerCosmetics = () => {
      const root = document.documentElement;

      // 1. Global Card Frame skin
      const frameStyleId = inventory.getEquippedFrame();
      const frameStyle = frameStyleId ? REEL_3_STYLES.find(s => s.id === frameStyleId) : null;
      root.classList.toggle('frame-skin-active', !!frameStyle);
      if (frameStyle) {
        root.style.setProperty('--frame-skin-color', frameStyle.hex);
        root.style.setProperty('--frame-skin-glow', frameStyle.hex);
      }

      // 2. Player HP Bar Color & Team Palette
      const hpStyleId = inventory.getEquippedHpBarColor();
      const hpStyle = hpStyleId ? REEL_3_STYLES.find(s => s.id === hpStyleId) : null;
      root.classList.toggle('hp-skin-active', !!hpStyle);
      const activeHpColor = hpStyle ? hpStyle.hex : '#00e5ff';
      const activeHpGlow = hpStyle ? hpStyle.hex : 'rgba(0, 229, 255, 0.4)';
      root.style.setProperty('--player-hp-bar-color', activeHpColor);
      root.style.setProperty('--player-hp-bar-glow', activeHpGlow);

      // Outline buttons on a dark face (game-over Cargo Bay, HUD Bridge tile) use the colour as
      // text/border/glow. Colours that vanish there (Obsidian Black, Deep Space Blue) get Chrome
      // Silver instead - see js/ui/hpBarAccent.js.
      const accent = resolveHpBarAccent(activeHpColor, activeHpGlow);
      root.style.setProperty('--player-hp-bar-accent', accent.color);
      root.style.setProperty('--player-hp-bar-accent-glow', accent.glow);

      // Contrast text color for team-colored metallic elements
      const hexClean = activeHpColor.replace('#', '');
      const rVal = parseInt(hexClean.substring(0, 2), 16) || 0;
      const gVal = parseInt(hexClean.substring(2, 4), 16) || 0;
      const bVal = parseInt(hexClean.substring(4, 6), 16) || 0;
      const lum = 0.299 * rVal + 0.587 * gVal + 0.114 * bVal;
      const isLight = lum > 120;
      root.style.setProperty('--player-hp-bar-text-color', isLight ? '#031c26' : '#ffffff');
      root.style.setProperty('--player-hp-bar-text-shadow', isLight
        ? '0 1px 0 rgba(255, 255, 255, 0.45), 0 -1px 0 rgba(0, 0, 0, 0.75)'
        : '0 1px 2px rgba(0, 0, 0, 0.95), 0 0 8px rgba(255, 255, 255, 0.5)');

      // Bright/light ("hell") tint of the equipped HP color for the Ghost Damage Bar
      const ghostR = Math.round(rVal + (255 - rVal) * 0.65);
      const ghostG = Math.round(gVal + (255 - gVal) * 0.65);
      const ghostB = Math.round(bVal + (255 - bVal) * 0.65);
      const activeHpGhostColor = `rgb(${ghostR}, ${ghostG}, ${ghostB})`;
      root.style.setProperty('--player-hp-bar-ghost', activeHpGhostColor);

      // 3. Player Avatar Border
      const avatarStyleId = inventory.getEquippedAvatarBorder();
      const avatarStyle = avatarStyleId ? REEL_3_STYLES.find(s => s.id === avatarStyleId) : null;
      root.classList.toggle('avatar-skin-active', !!avatarStyle);
      if (avatarStyle) {
        root.style.setProperty('--player-avatar-border-color', avatarStyle.hex);
        root.style.setProperty('--player-avatar-border-glow', avatarStyle.hex);
      }

      // 4. Card Tray Glow (recessed hand-dock background, overrides team blue/red)
      const trayStyleId = inventory.getEquippedTrayColor();
      const trayStyle = trayStyleId ? REEL_3_STYLES.find(s => s.id === trayStyleId) : null;
      root.classList.toggle('tray-skin-active', !!trayStyle);
      if (trayStyle) {
        root.style.setProperty('--tray-skin-color-start', mixHexWithBlack(trayStyle.hex, 0.92));
        root.style.setProperty('--tray-skin-color-end', mixHexWithBlack(trayStyle.hex, 0.86));
        root.style.setProperty('--tray-skin-border', mixHexWithBlack(trayStyle.hex, 0.15));
        root.style.setProperty('--tray-skin-glow', trayStyle.hex);
      }

      // Helper to apply custom avatar border to a specific slot and its badge
      const applySlotCosmetics = (slotEl, badgeEl, styleIdOrHex) => {
        if (!slotEl) return;
        let hex = null;
        if (styleIdOrHex) {
          if (typeof styleIdOrHex === 'string' && styleIdOrHex.startsWith('#')) {
            hex = styleIdOrHex;
          } else {
            const match = REEL_3_STYLES_MAP.get(styleIdOrHex) || REEL_3_STYLES.find(s => s.id === styleIdOrHex);
            if (match?.hex) hex = match.hex;
          }
        }
        if (hex) {
          slotEl.style.setProperty('--slot-border-color', hex);
          slotEl.style.setProperty('--slot-border-glow', hex);
          slotEl.dataset.hasCustomBorder = 'true';
          if (badgeEl) {
            badgeEl.style.borderColor = hex;
            badgeEl.style.boxShadow = `0 0 8px ${hex}, inset 0 0 4px rgba(0, 0, 0, 0.5)`;
            badgeEl.style.textShadow = `0 0 4px ${hex}`;
          }
        } else {
          if (typeof slotEl.style?.removeProperty === 'function') {
            slotEl.style.removeProperty('--slot-border-color');
            slotEl.style.removeProperty('--slot-border-glow');
          } else if (slotEl.style) {
            delete slotEl.style['--slot-border-color'];
            delete slotEl.style['--slot-border-glow'];
          }
          if (slotEl.dataset) {
            delete slotEl.dataset.hasCustomBorder;
          }
          if (badgeEl) {
            if (typeof badgeEl.style?.removeProperty === 'function') {
              badgeEl.style.removeProperty('border-color');
              badgeEl.style.removeProperty('box-shadow');
              badgeEl.style.removeProperty('text-shadow');
            } else if (badgeEl.style) {
              badgeEl.style.borderColor = '';
              badgeEl.style.boxShadow = '';
              badgeEl.style.textShadow = '';
            }
          }
        }
      };

      // Helper to set image source for an avatar img element
      const setAvatarSlotImage = (imgEl, avatarEntry) => {
        if (!imgEl) return;
        const currentAvatarId = imgEl.dataset?.avatarId || '';
        const newAvatarId = avatarEntry?.id || '';
        if (currentAvatarId && currentAvatarId === newAvatarId && imgEl.getAttribute('src')) {
          return;
        }
        if (imgEl.dataset) imgEl.dataset.avatarId = newAvatarId;
        const url = avatarEntry ? resolveAvatarImageUrl(avatarEntry) : null;
        if (url) {
          imgEl.src = url;
          imgEl.classList.remove('hidden');
        } else {
          imgEl.classList.add('hidden');
          imgEl.removeAttribute('src');
        }
      };

      // Apply classes to active human player elements
      const playerTeam = this.myTeam || 'western';
      const enemyTeam = playerTeam === 'western' ? 'eastern' : 'western';

      const westCluster = document.getElementById('western-avatar-cluster');
      const eastCluster = document.getElementById('eastern-avatar-cluster');
      const westSubSlot = document.getElementById('western-avatar-sub-slot');
      const eastSubSlot = document.getElementById('eastern-avatar-sub-slot');
      const westPlasmaWrap = document.getElementById('western-teammate-plasma-wrap');
      const eastPlasmaWrap = document.getElementById('eastern-teammate-plasma-wrap');

      const playerHpEl = document.getElementById(`${playerTeam}-hp-bar`);
      const enemyHpEl = document.getElementById(`${enemyTeam}-hp-bar`);
      const playerGhostEl = document.getElementById(`${playerTeam}-ghost-hp-bar`);
      const enemyGhostEl = document.getElementById(`${enemyTeam}-ghost-hp-bar`);
      if (playerHpEl) playerHpEl.classList.toggle('custom-player-hp', !!hpStyle);
      if (enemyHpEl) enemyHpEl.classList.remove('custom-player-hp');
      if (playerGhostEl) playerGhostEl.classList.toggle('custom-player-hp', !!hpStyle);
      if (enemyGhostEl) enemyGhostEl.classList.remove('custom-player-hp');

      const playerAvatarEl = document.querySelector(`.boss-avatar.${playerTeam}`);
      const enemyAvatarEl = document.querySelector(`.boss-avatar.${enemyTeam}`);
      if (playerAvatarEl) playerAvatarEl.classList.toggle('custom-player-avatar', !!avatarStyle);
      if (enemyAvatarEl) enemyAvatarEl.classList.remove('custom-player-avatar');

      // Elements for Friendly side (playerTeam)
      const friendMainSlot = document.getElementById(`${playerTeam}-avatar-main-slot`);
      const friendMainImg = document.getElementById(`${playerTeam}-avatar-custom-image`);
      const friendMainBadge = document.getElementById(`${playerTeam}-avatar-level-badge`);

      const friendSubSlot = document.getElementById(`${playerTeam}-avatar-sub-slot`);
      const friendSubImg = document.getElementById(`${playerTeam}-avatar-sub-image`);
      const friendSubBadge = document.getElementById(`${playerTeam}-avatar-sub-level-badge`);

      // Elements for Enemy side (enemyTeam)
      const enemyMainSlot = document.getElementById(`${enemyTeam}-avatar-main-slot`);
      const enemyMainImg = document.getElementById(`${enemyTeam}-avatar-custom-image`);
      const enemyMainBadge = document.getElementById(`${enemyTeam}-avatar-level-badge`);

      const enemySubSlot = document.getElementById(`${enemyTeam}-avatar-sub-slot`);
      const enemySubImg = document.getElementById(`${enemyTeam}-avatar-sub-image`);
      const enemySubBadge = document.getElementById(`${enemyTeam}-avatar-sub-level-badge`);

      const playerLevel = progress.getLevelProgress().level;
      const defaultStarterAvatar = getAvatarCatalog()?.[0] || null;
      const equippedAvatar = progress.getEquippedAvatar() || defaultStarterAvatar;

      if (this.is2v2) {
        westCluster?.classList.add('is-2v2');
        eastCluster?.classList.add('is-2v2');
        westSubSlot?.classList.remove('hidden');
        eastSubSlot?.classList.remove('hidden');

        // Plasma bar only under friendly duo
        if (playerTeam === 'western') {
          westPlasmaWrap?.classList.remove('hidden');
          eastPlasmaWrap?.classList.add('hidden');
        } else {
          eastPlasmaWrap?.classList.remove('hidden');
          westPlasmaWrap?.classList.add('hidden');
        }

        // 1. Friendly Main: Local Player
        setAvatarSlotImage(friendMainImg, equippedAvatar);
        applySlotCosmetics(friendMainSlot, friendMainBadge, avatarStyleId);
        if (friendMainBadge) {
          friendMainBadge.textContent = String(playerLevel);
          friendMainBadge.title = `Player Level ${playerLevel}`;
          friendMainBadge.classList.remove('hidden');
        }

        // 2. Friendly Sub: Teammate / Ally
        const mateAvatar = (this.teammate?.avatarId ? getAvatarById(this.teammate.avatarId) : null) || defaultStarterAvatar;
        setAvatarSlotImage(friendSubImg, mateAvatar);
        applySlotCosmetics(friendSubSlot, friendSubBadge, this.teammate?.avatarBorder);
        if (friendSubBadge) {
          const mateLevel = this.teammate?.level || 1;
          friendSubBadge.textContent = String(mateLevel);
          friendSubBadge.title = `Ally Level ${mateLevel}`;
          friendSubBadge.classList.remove('hidden');
        }

        // 3. Enemy Main: Opponent 1
        const opp1 = this.opponents?.[0];
        const opp1Avatar = (opp1?.avatarId ? getAvatarById(opp1.avatarId) : null)
          || (this.opponentOnlineAvatarId ? getAvatarById(this.opponentOnlineAvatarId) : null);
        setAvatarSlotImage(enemyMainImg, opp1Avatar);
        applySlotCosmetics(enemyMainSlot, enemyMainBadge, opp1?.avatarBorder || this.opponentOnlineAvatarBorder);
        if (enemyMainBadge) {
          const opp1Level = opp1?.level || this.opponentOnlineLevel || 1;
          enemyMainBadge.textContent = String(opp1Level);
          enemyMainBadge.title = `Opponent Level ${opp1Level}`;
          enemyMainBadge.classList.remove('hidden');
        }

        // 4. Enemy Sub: Opponent 2
        const opp2 = this.opponents?.[1];
        const opp2Avatar = (opp2?.avatarId ? getAvatarById(opp2.avatarId) : null) || defaultStarterAvatar;
        setAvatarSlotImage(enemySubImg, opp2Avatar);
        applySlotCosmetics(enemySubSlot, enemySubBadge, opp2?.avatarBorder);
        if (enemySubBadge) {
          const opp2Level = opp2?.level || 1;
          enemySubBadge.textContent = String(opp2Level);
          enemySubBadge.title = `Opponent Level ${opp2Level}`;
          enemySubBadge.classList.remove('hidden');
        }
      } else {
        // 1v1 / Singleplayer
        westCluster?.classList.remove('is-2v2');
        eastCluster?.classList.remove('is-2v2');
        westSubSlot?.classList.add('hidden');
        eastSubSlot?.classList.add('hidden');
        westPlasmaWrap?.classList.add('hidden');
        eastPlasmaWrap?.classList.add('hidden');

        // Local Player
        setAvatarSlotImage(friendMainImg, equippedAvatar);
        applySlotCosmetics(friendMainSlot, friendMainBadge, avatarStyleId);
        if (friendMainBadge) {
          friendMainBadge.textContent = String(playerLevel);
          friendMainBadge.title = `Player Level ${playerLevel}`;
          friendMainBadge.classList.remove('hidden');
        }

        // Opponent
        const oppAvatar = (this.isOnline && this.opponentOnlineAvatarId)
          ? getAvatarById(this.opponentOnlineAvatarId)
          : null;
        setAvatarSlotImage(enemyMainImg, oppAvatar);
        applySlotCosmetics(enemyMainSlot, enemyMainBadge, this.opponentOnlineAvatarBorder);
        if (enemyMainBadge) {
          const enemyLevel = this.isOnline ? (this.opponentOnlineLevel || 1) : 1;
          enemyMainBadge.textContent = String(enemyLevel);
          enemyMainBadge.title = this.isOnline ? `Opponent Level ${enemyLevel}` : 'AI Level 1';
          enemyMainBadge.classList.remove('hidden');
        }

        // Clear any leftover cosmetics on hidden sub-slots
        applySlotCosmetics(friendSubSlot, friendSubBadge, null);
        applySlotCosmetics(enemySubSlot, enemySubBadge, null);
      }
    };

    this._applyPlayerCosmetics = applyEquippedPlayerCosmetics;
    applyEquippedPlayerCosmetics();
    inventory.addListener(applyEquippedPlayerCosmetics);
    progress.addListener(applyEquippedPlayerCosmetics);

    // Reactive achievement check for anything that ISN'T tied to a match
    // ending (e.g. "Hochroller"/"Zielübung"/"Gold-Rausch"/"Vollspektrum") -
    // otherwise those would only ever get evaluated the next time a match
    // happens to finish. lastMatch stays null here; _checkAchievements()
    // only re-evaluates still-locked achievements and is a no-op once
    // everything currently met is already unlocked, so this is safe to
    // call on every inventory/progress change.
    const checkNonMatchAchievements = () => this._checkAchievements(null);
    inventory.addListener(checkNonMatchAchievements);
    progress.addListener(checkNonMatchAchievements);
    // Also run once at startup - covers a save that already met some
    // condition (e.g. from play before achievements existed) without
    // waiting for the next inventory/progress change to trigger it.
    checkNonMatchAchievements();

    if (typeof window !== 'undefined') {
      // Developer & Testing Debug Helpers
      window.UCG_DEBUG = {
        addFugazi: (amount = 100) => wallet.addFugazi(amount, 'debug_add'),
        spendFugazi: (amount = 50) => wallet.spendFugazi(amount, 'debug_spend'),
        addTickets: (count = 1) => wallet.addTickets(count, 'debug_ticket'),
        resetWallet: (amt = 100) => wallet.resetWallet(amt),
        getWallet: () => wallet.getState(),
        getDirectives: () => directives.getDirectives(),
        completeDirective: (id) => directives.completeDirective(id),
        setDirectiveDate: (d) => directives.setDateOverride(d),
        openDirectives: () => this.directivesModal?.open(),
        openPacks: () => this.packOpeningModal?.open(),
        openPackDirect: () => packs.openPack('core_pack'),
        openSlots: () => this.slotMachineModal?.open(),
        spinSlot: () => slotMachine.spin(),
        openShowroom: () => this.showroomModal?.open(),
        unlockDeckbuilderTheme: (themeId = 'fluted') => {
          inventory.addDeckbuilderTheme(themeId);
          console.log(`[UCG_DEBUG] Unlocked deckbuilder theme: ${themeId}`);
        },
        equipDeckbuilderTheme: (themeId = 'fluted') => {
          inventory.equipDeckbuilderTheme(themeId);
          console.log(`[UCG_DEBUG] Equipped deckbuilder theme: ${themeId}`);
        },
        unlockCardTheme: (themeId = 'holo') => {
          inventory.addCardTheme(themeId);
          console.log(`[UCG_DEBUG] Unlocked in-game card theme: ${themeId}`);
        },
        equipCardTheme: (themeId = 'holo') => {
          inventory.equipCardTheme(themeId);
          console.log(`[UCG_DEBUG] Equipped in-game card theme: ${themeId}`);
        },
        toggleAudioLog: () => this.audioDebugOverlay?.toggle(),
        // K-3.3: same aggregate _updateNetOverlay() reads for the in-game
        // HUD panel, but reachable from a console without the overlay open.
        getNetStats: () => this.stateStream?.getNetStats() ?? null,
        testLevelUp: (lvl = 14, title = 'TACTICAL OPERATOR') => this.levelUpOverlay?.show(lvl, title),
        testAchievement: (id = 'first_blood') => {
          const ach = ACHIEVEMENTS.find(a => a.id === id) || ACHIEVEMENTS[0];
          this.achievementOverlay?.show(ach);
        },
        testDirective: (id = null) => {
          const all = directives.getDirectives();
          const d = (id ? all.find(x => x.id === id) : null) || all[0] || {
            id: 'test_directive',
            title: 'BODY COUNT',
            description: 'Eliminate 15 enemy units in battle.',
            tier: 1,
            rewardFugazi: 30,
            rewardXP: 15,
            icon: 'dailyProtocol'
          };
          this.achievementOverlay?.showDirective?.(d);
        },
        testJackpot: () => {
          this.achievementOverlay?.showJackpot?.({
            title: 'DAILY JACKPOT',
            description: 'All 3 daily quests completed!',
            fugazi: 100,
            xp: 50
          });
        },
        spawn: (name = 'Hyper Tesla Walker', team = this.myTeam || 'western', x = null, y = null) => {
          return this.battlefield?.spawnUnitByName(name, team, x, y);
        },
        unlockAllArsenal: ({ unlockAchievements = true, maxLevel = 100 } = {}) => {
          inventory.beginBatch();

          // 1. Account / Player Global Cosmetics across all 24 REEL_3_STYLES
          for (const style of REEL_3_STYLES) {
            inventory.addFrame(style.id, style.name, style.hex);
            inventory.addHpBarColor(style.id, style.name, style.hex);
            inventory.addAvatarBorder(style.id, style.name, style.hex);
            inventory.addTrayColor(style.id, style.name, style.hex);
            inventory.addDeckColor(style.id, style.name, style.hex);
          }

          // 2. Playable Units (Bullet Colors, Unit Auras, Rocket Trails, Special Gold Cards)
          const playableUnits = CARDS_DATA.filter(c =>
            (c.enabled === true || c.type === 'Melt')
            && !c.isSpell && c.type !== 'Spell'
            && !c.isModule && c.type !== 'Module'
          );

          for (const card of playableUnits) {
            const name = card.name;
            if (!isBulletColorExcluded(name)) {
              for (const style of REEL_3_STYLES) {
                inventory.addBulletColor(name, style.id, style.name, style.hex);
              }
            }
            if (!isAuraExcluded(name)) {
              for (const style of REEL_3_STYLES) {
                inventory.addUnitAura(name, style.id, style.name, style.hex);
              }
            }
            if (isRocketTrailUnit(name)) {
              for (const style of REEL_3_STYLES) {
                inventory.addRocketTrail(name, style.id, style.name, style.hex);
              }
            }
            inventory.addSpecialCard(name, 'gold');
          }

          // 3. Player Taunts
          for (const tauntId of ALL_TAUNT_IDS) {
            inventory.unlockTaunt(tauntId);
          }

          // 4. Profile Artworks
          for (const artCard of getProfileArtworkCards()) {
            inventory.addProfileArtwork(artCard.name);
          }

          // 5. Deckbuilder Themes
          for (const theme of DECKBUILDER_THEMES) {
            inventory.addDeckbuilderTheme(theme.id);
          }

          // 6. In-Game Hand Card Themes
          for (const theme of CARD_THEMES) {
            inventory.addCardTheme(theme.id);
          }

          // 7. Deck Icons
          for (const icon of DECK_ICON_PRESETS) {
            inventory.addDeckIcon(icon.id, icon.label, icon.rarity);
          }

          inventory.endBatch();

          // 8. Progression: Level and Avatars
          const targetXp = 25 * maxLevel * (maxLevel - 1);
          const currentXp = progress.getLevelProgress().totalXp;
          if (currentXp < targetXp) {
            progress.addXp(targetXp - currentXp, 'cheat_arsenal', { log: false });
          }

          // 9. Achievements in Progress Tab
          if (unlockAchievements && Array.isArray(ACHIEVEMENTS)) {
            const now = new Date().toISOString();
            for (const ach of ACHIEVEMENTS) {
              if (!progress.hasUnlockedAchievement(ach.id)) {
                progress._state.achievements.unlocked[ach.id] = { at: now };
              }
            }
            progress._save();
          }

          // 10. Re-render Showroom / Arsenal if currently open
          if (this.showroomModal) {
            this.showroomModal.init();
            if (this.showroomModal.isOpen?.()) {
              this.showroomModal.renderStage();
            }
          }

          console.log('%c[UCG_DEBUG] 🎉 ALLES IM ARSENAL FREIGESCHALTET! (Units, Bullet Colors, Auren, Rocket Trails, Frames, HP Colors, Borders, Tray Glow, Deck Colors, Icons, Themes, Taunts, Avatare & Erfolge)', 'color: #00ff88; font-weight: bold;');
          return 'Arsenal completely unlocked!';
        },
        resetArsenal: () => {
          inventory.resetInventory();
          if (this.showroomModal) {
            this.showroomModal.init();
            if (this.showroomModal.isOpen?.()) {
              this.showroomModal.renderStage();
            }
          }
          console.log('[UCG_DEBUG] Arsenal inventory reset to default.');
          return 'Arsenal reset to default.';
        }
      };
      window.spawnUnit = window.UCG_DEBUG.spawn;
      window.unlockAllArsenal = window.UCG_DEBUG.unlockAllArsenal;
      window.resetArsenal = window.UCG_DEBUG.resetArsenal;
    }
  }

  cancelTargeting() {
    if (!this.activeTargetingPower) return;
    this.activeTargetingPower = null;
    this.renderer?.setTargeting(false);
    if (this.canvas) {
      this.canvas.style.cursor = '';
    }
    this._lastSuperBtnKey = null;
    this.updateSuperButtonState(true);
  }

  initSuperButton() {
    // Super Tactical Ability: 1x Superweapon Strike (1x per round, unconditional)
    const btnCoSuper = document.getElementById('btn-co-super');
    this._elBtnCoSuper = btnCoSuper;
    if (btnCoSuper) {
      btnCoSuper.addEventListener('click', (e) => {
        const charges = this.superweaponCharges || this.nukesAvailable;
        if (charges && charges[this.myTeam] > 0) {
          if (this.activeTargetingPower) {
            this.cancelTargeting();
          } else {
            const myRace = this.battlefield?.bases?.[this.myTeam]?.race || 'Human';
            const isSul = myRace === 'Sul';
            const powerName = isSul ? 'Plasma' : 'Nuclear Annihilation Strike';
            const reticleColor = isSul ? '#a855f7' : '#ff0033';

            this.activeTargetingPower = { name: powerName };
            const initialCoords = (e && typeof e.clientX === 'number' && e.clientX > 0)
              ? this.canvasPointToWorld(e.clientX, e.clientY)
              : { x: this.viewport.camera.x, y: this.viewport.camera.y };
            this.renderer.setTargeting(true, initialCoords.x, initialCoords.y, reticleColor);
            if (this.canvas) {
              this.canvas.style.cursor = 'crosshair';
            }
            this._lastSuperBtnKey = null;
            this.updateSuperButtonState(true);
          }
        } else {
          this.audio?.playError?.();
        }
      });
    }
  }

  initNukeButton() {
    this.initSuperButton();
  }

  updateSuperButtonState(force = false) {
    if (!this._elBtnCoSuper && typeof document !== 'undefined') {
      this._elBtnCoSuper = document.getElementById('btn-co-super');
    }
    const btnCoSuper = this._elBtnCoSuper;
    if (btnCoSuper) {
      const myRace = this.battlefield?.bases?.[this.myTeam]?.race || 'Human';
      const isSul = myRace === 'Sul';

      const charges = this.superweaponCharges || this.nukesAvailable;
      const myCharge = (this.is2v2 && this.myRole && charges?.[this.myRole] !== undefined)
        ? charges[this.myRole]
        : (charges ? charges[this.myTeam] : 0);

      const isLockedOut = this.is2v2 && ((this.teamSuperLockout || 0) > 0);
      const isTargeting = !!this.activeTargetingPower;
      const lockoutSec = isLockedOut ? Math.max(0, this.teamSuperLockout || 0) : 0;
      const lockoutSecCeil = isLockedOut ? Math.ceil(lockoutSec) : 0;
      const pctStr = isLockedOut ? Math.min(100, Math.max(0, ((30 - lockoutSec) / 30) * 100)).toFixed(1) : '0';

      const stateKey = `${myRace}_${myCharge}_${isTargeting ? 1 : 0}_${isLockedOut ? 1 : 0}_${lockoutSecCeil}_${pctStr}`;
      if (!force && this._lastSuperBtnKey === stateKey) {
        return;
      }
      this._lastSuperBtnKey = stateKey;

      if (this.activeTargetingPower) {
        btnCoSuper.innerHTML = 'Cancel';
        btnCoSuper.classList.add('super-targeting-active');
        btnCoSuper.classList.remove('super-lockout-active');
        btnCoSuper.style.removeProperty('--refill-pct');
        btnCoSuper.style.opacity = '1.0';
        btnCoSuper.style.pointerEvents = 'auto';
        btnCoSuper.style.cursor = 'pointer';
        btnCoSuper.title = 'Click to cancel targeting (ESC / Right-click)';
        return;
      }

      btnCoSuper.classList.remove('super-targeting-active');

      if (isSul) {
        if (isLockedOut) {
          btnCoSuper.innerHTML = `Plasma <span class="super-lockout-timer">${lockoutSecCeil}s</span>`;
        } else {
          btnCoSuper.innerHTML = 'Plasma';
        }
        btnCoSuper.classList.add('super-bioplasma');
      } else {
        btnCoSuper.innerHTML = isLockedOut
          ? `<span class="super-text-full">Tactical </span>Nuke <span class="super-lockout-timer">${lockoutSecCeil}s</span>`
          : '<span class="super-text-full">Tactical </span>Nuke';
        btnCoSuper.classList.remove('super-bioplasma');
      }

      if (myCharge > 0) {
        if (isLockedOut) {
          btnCoSuper.classList.add('super-lockout-active');
          btnCoSuper.style.setProperty('--refill-pct', `${pctStr}%`);
          btnCoSuper.style.opacity = '0.85';
          btnCoSuper.style.pointerEvents = 'none';
          btnCoSuper.style.cursor = 'not-allowed';
          btnCoSuper.title = `Team Lockout: ${lockoutSecCeil}s`;
        } else {
          btnCoSuper.classList.remove('super-lockout-active');
          btnCoSuper.style.removeProperty('--refill-pct');
          btnCoSuper.style.opacity = '1.0';
          btnCoSuper.style.pointerEvents = 'auto';
          btnCoSuper.style.cursor = 'pointer';
          btnCoSuper.title = '';
        }
      } else {
        btnCoSuper.classList.remove('super-lockout-active');
        btnCoSuper.style.removeProperty('--refill-pct');
        btnCoSuper.style.opacity = '0.45';
        btnCoSuper.style.pointerEvents = 'none';
        btnCoSuper.style.cursor = 'not-allowed';
        btnCoSuper.title = 'Superweapon Expended';
      }
    }
  }

  updateNukeButtonState() {
    this.updateSuperButtonState();
  }

  // Screen (client) coordinates -> world (sim) coordinates, going through
  // the camera - see js/ui/viewport.js. Every canvas input handler below
  // shares this one conversion instead of each computing its own
  // canvas-rect scale factor (that only worked because the canvas used to
  // be pinned 1:1 to world pixels; with a real camera it no longer is).
  canvasPointToWorld(clientX, clientY) {
    return this.viewport.screenToWorld(clientX, clientY);
  }

  // Ziel-Overlay (the "CONTESTED MIDLINE" territory badge drawn on canvas
  // by renderer.renderTerritoryIndicator) is the click target that replaced
  // the old "Ziele" HUD button - it caches its own world-space rect each
  // frame since it's pure canvas drawing, not a DOM element.
  isPointInTerritoryBadge(x, y) {
    const r = this.renderer?.territoryBadgeRect;
    if (!r) return false;
    return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
  }

  initEventListeners() {

    // Single pointer-event pipeline covers mouse hover/click AND touch
    // tap/drag - pointermove already behaves like mousemove for a hovering
    // mouse (no button down), and additionally fires while a finger drags.
    // A tap is a pointerdown+up pair that never exceeded the drag
    // threshold below; anything past it is treated as a camera pan instead
    // of a click, so panning the field on a phone doesn't also fire
    // whatever was underneath the finger. Panning itself only ever
    // triggers when the viewport is actually pannable (portrait mode -
    // see Viewport.canPan()); in landscape/desktop the whole field is
    // already on screen, so a drag there simply does nothing.
    const DRAG_THRESHOLD = 6; // CSS px
    const DOUBLE_TAP_WINDOW_MS = 300;
    const DOUBLE_TAP_MAX_DIST = 28; // CSS px
    let pointerDown = false;
    let dragOccurred = false;
    let startClientX = 0, startClientY = 0;
    let lastClientX = 0, lastClientY = 0;
    let activePointerId = null;

    // Multi-touch Pinch-to-Zoom & Double-Tap state (Mobile Portrait)
    const activePointers = new Map();
    let pinchActive = false;
    let pinchStartDist = 0;
    let suppressTapUntilAllUp = false;
    let lastEmptyTapTime = 0;
    let lastEmptyTapX = 0;
    let lastEmptyTapY = 0;

    this.canvas.addEventListener('pointerdown', (e) => {
      // Tapping/clicking the Spielfläche (battlefield) while the setup menu is open closes it
      const setupPanel = document.getElementById('setup-controls');
      if (setupPanel && setupPanel.classList.contains('open')) {
        if (!e.target.closest('#setup-controls')) {
          this.hud?.closeSetupSheet?.();
          return; // Close menu and suppress in-game canvas interaction
        }
      }

      if (e.pointerType === 'mouse') {
        activePointers.clear();
      }
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { this.canvas.setPointerCapture(e.pointerId); } catch { /* best-effort */ }

      // 2-finger Pinch-to-Zoom in Mobile Portrait & Mobile Landscape
      if (activePointers.size >= 2 && (this.viewport?.canPinchZoom?.() || this.viewport?.canPan?.()) && !this.activeTargetingPower) {
        const pts = Array.from(activePointers.values());
        pinchStartDist = Math.max(12, Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y));
        const midX = (pts[0].x + pts[1].x) * 0.5;
        pinchActive = true;
        suppressTapUntilAllUp = true;
        dragOccurred = true;
        this.viewport.startPinch(midX);
        return;
      }

      if (activePointerId !== null) return;
      activePointerId = e.pointerId;
      pointerDown = true;
      dragOccurred = false;
      startClientX = lastClientX = e.clientX;
      startClientY = lastClientY = e.clientY;
    });

    window.addEventListener('pointermove', (e) => {
      if (this.activeTargetingPower) {
        const { x, y } = this.canvasPointToWorld(e.clientX, e.clientY);
        this.renderer.setTargeting(true, x, y);
      }
    });

    this.canvas.addEventListener('pointermove', (e) => {
      if (this.activeTargetingPower) {
        const { x, y } = this.canvasPointToWorld(e.clientX, e.clientY);
        this.renderer.setTargeting(true, x, y);
      }

      if (activePointers.has(e.pointerId)) {
        activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      }

      if (activePointers.size >= 2 && (this.viewport?.canPinchZoom?.() || this.viewport?.canPan?.()) && !this.activeTargetingPower) {
        const pts = Array.from(activePointers.values());
        const dist = Math.max(12, Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y));
        const midX = (pts[0].x + pts[1].x) * 0.5;
        if (!pinchActive) {
          pinchStartDist = dist;
          pinchActive = true;
          suppressTapUntilAllUp = true;
          dragOccurred = true;
          this.viewport.startPinch(midX);
        } else {
          this.viewport.updatePinch(dist / pinchStartDist, midX);
        }
        return;
      }

      if (!pointerDown || e.pointerId !== activePointerId || suppressTapUntilAllUp) return;

      const dx = e.clientX - lastClientX;
      const dy = e.clientY - lastClientY;
      lastClientX = e.clientX;
      lastClientY = e.clientY;

      if (!dragOccurred) {
        const totalDx = e.clientX - startClientX;
        const totalDy = e.clientY - startClientY;
        if (Math.hypot(totalDx, totalDy) < DRAG_THRESHOLD) return;
        dragOccurred = true;
      }
      if (this.viewport.canPan() && !this.activeTargetingPower) {
        this.viewport.panBy(dx);
      }
    });

    const endPointer = (e) => {
      activePointers.delete(e.pointerId);
      if (pinchActive && activePointers.size < 2) {
        pinchActive = false;
        this.viewport?.endPinch?.();
        if (activePointers.size === 1) {
          const [remainingId, remainingPt] = activePointers.entries().next().value;
          activePointerId = remainingId;
          lastClientX = remainingPt.x;
          lastClientY = remainingPt.y;
        }
      }

      if (activePointers.size === 0) {
        const wasSuppressed = suppressTapUntilAllUp;
        suppressTapUntilAllUp = false;
        if (wasSuppressed) {
          activePointerId = null;
          pointerDown = false;
          dragOccurred = false;
          return;
        }
      }

      if (e.pointerId !== activePointerId) return;
      const wasDrag = dragOccurred;
      activePointerId = null;
      pointerDown = false;
      dragOccurred = false;

      if (wasDrag || suppressTapUntilAllUp) return; // camera pan or pinch, not a tap

      const { x, y } = this.canvasPointToWorld(e.clientX, e.clientY);

      if (this.activeTargetingPower) {
        this.activeTargetingPower = null;
        this.renderer.setTargeting(false);
        if (this.canvas) {
          this.canvas.style.cursor = '';
        }
        this._lastSuperBtnKey = null;
        this.updateSuperButtonState(true);
        this.enqueueCommand({ type: 'NUKE', team: this.myTeam, x, y });
      } else if (this.isPointInTerritoryBadge(x, y)) {
        this.pauseForMenu('directives');
        this.directivesModal?.open();
      } else {
        const wasDesktopUnitTracking = !!(this.viewport?.isDesktop?.() && (this.viewport?.trackedUnit || this.viewport?.trackingProgress > 0.05));
        const clickedUnit = this.handleUnitClick(x, y);
        // Double-tap / Double-click on empty battlefield ground in Mobile Portrait, Mobile Landscape, or Desktop
        // toggles between the two discrete zoom states (Portrait: Close <-> 50% Wide; Landscape/Desktop: Overview <-> Embedded Lane View).
        // When a click on empty space was just used to exit Desktop single-unit tracking, do not arm the double-click timer.
        if (!clickedUnit && !wasDesktopUnitTracking && (this.viewport?.canPinchZoom?.() || this.viewport?.canPan?.())) {
          const now = performance.now();
          const dtTap = now - lastEmptyTapTime;
          const tapDist = Math.hypot(e.clientX - lastEmptyTapX, e.clientY - lastEmptyTapY);
          if (dtTap > 30 && dtTap <= DOUBLE_TAP_WINDOW_MS && tapDist <= DOUBLE_TAP_MAX_DIST) {
            this.viewport.togglePortraitZoomState(e.clientX);
            lastEmptyTapTime = 0;
          } else {
            lastEmptyTapTime = now;
            lastEmptyTapX = e.clientX;
            lastEmptyTapY = e.clientY;
          }
        } else {
          lastEmptyTapTime = 0;
        }
      }
    };
    this.canvas.addEventListener('pointerup', endPointer);
    this.canvas.addEventListener('pointercancel', (e) => {
      activePointers.delete(e.pointerId);
      if (pinchActive && activePointers.size < 2) {
        pinchActive = false;
        this.viewport?.endPinch?.();
      }
      if (activePointers.size === 0) {
        suppressTapUntilAllUp = false;
      }
      if (e.pointerId !== activePointerId) return;
      activePointerId = null;
      pointerDown = false;
      dragOccurred = false;
    });

    // Global safety net: if a pointer is released outside the canvas (or capture fails on iOS WebKit),
    // always prune it from activePointers so a stale pointerId can never block future gestures.
    window.addEventListener('pointerup', (e) => {
      if (activePointers.has(e.pointerId)) {
        activePointers.delete(e.pointerId);
        if (pinchActive && activePointers.size < 2) {
          pinchActive = false;
          this.viewport?.endPinch?.();
        }
        if (activePointers.size === 0) {
          suppressTapUntilAllUp = false;
        }
      }
    });
    window.addEventListener('pointercancel', (e) => {
      if (activePointers.has(e.pointerId)) {
        activePointers.delete(e.pointerId);
        if (pinchActive && activePointers.size < 2) {
          pinchActive = false;
          this.viewport?.endPinch?.();
        }
        if (activePointers.size === 0) {
          suppressTapUntilAllUp = false;
        }
      }
    });

    // Native Touch Events & iOS WebKit Gesture fallback for iPhone Chrome / Safari:
    // On iOS WebKit (CriOS / MobileSafari), e.touches provides ground-truth multi-finger coordinates
    // even when WebKit coalesces or cancels secondary pointer events during a pinch gesture.
    this.canvas.addEventListener('touchstart', (e) => {
      if (e.touches && e.touches.length >= 2 && (this.viewport?.canPinchZoom?.() || this.viewport?.canPan?.()) && !this.activeTargetingPower) {
        if (e.cancelable) e.preventDefault();
        const t0 = e.touches[0];
        const t1 = e.touches[1];
        pinchStartDist = Math.max(12, Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY));
        const midX = (t0.clientX + t1.clientX) * 0.5;
        pinchActive = true;
        suppressTapUntilAllUp = true;
        dragOccurred = true;
        this.viewport.startPinch(midX);
      }
    }, { passive: false });

    this.canvas.addEventListener('touchmove', (e) => {
      if (e.touches && e.touches.length >= 2 && (this.viewport?.canPinchZoom?.() || this.viewport?.canPan?.()) && !this.activeTargetingPower) {
        if (e.cancelable) e.preventDefault();
        const t0 = e.touches[0];
        const t1 = e.touches[1];
        const dist = Math.max(12, Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY));
        const midX = (t0.clientX + t1.clientX) * 0.5;
        if (!pinchActive) {
          pinchStartDist = dist;
          pinchActive = true;
          suppressTapUntilAllUp = true;
          dragOccurred = true;
          this.viewport.startPinch(midX);
        } else {
          this.viewport.updatePinch(dist / pinchStartDist, midX);
        }
      }
    }, { passive: false });

    const endTouchPinch = (e) => {
      const remaining = e.touches ? e.touches.length : 0;
      if (pinchActive && remaining < 2) {
        pinchActive = false;
        this.viewport?.endPinch?.();
      }
      if (remaining === 0) {
        activePointers.clear();
        setTimeout(() => { suppressTapUntilAllUp = false; }, 0);
      }
    };
    this.canvas.addEventListener('touchend', endTouchPinch, { passive: true });
    this.canvas.addEventListener('touchcancel', endTouchPinch, { passive: true });

    // Suppress native iOS WebKit pinch-zoom gesture on the battlefield canvas
    for (const gEvt of ['gesturestart', 'gesturechange', 'gestureend']) {
      this.canvas.addEventListener(gEvt, (e) => {
        if (e.cancelable) e.preventDefault();
      }, { passive: false });
    }

    // Desktop Mouse Wheel & Trackpad 2-Finger Pinch/Scroll Zoom on the battlefield canvas
    this.canvas.addEventListener('wheel', (e) => {
      if (this.activeTargetingPower) return;
      if (this.viewport?.canPinchZoom?.()) {
        if (e.cancelable) e.preventDefault();
        this.viewport.handleWheelZoom?.(e.deltaY, e.deltaMode, e.clientX);
      }
    }, { passive: false });

    this.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      if (this.activeTargetingPower) {
        this.cancelTargeting();
      }
    });

    // Mobile bugfix: tapping anywhere on the battlefield container outside setup-controls closes the menu
    const canvasContainer = document.getElementById('canvas-container');
    if (canvasContainer) {
      canvasContainer.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        if (this.activeTargetingPower) {
          this.cancelTargeting();
        }
      });
      canvasContainer.addEventListener('pointerdown', (e) => {
        const setupPanel = document.getElementById('setup-controls');
        if (setupPanel && setupPanel.classList.contains('open')) {
          if (!e.target.closest('#setup-controls')) {
            this.hud?.closeSetupSheet?.();
          }
        }
      });
    }

    // Clicking the peek tab on the collapsed bottom HUD in unit-camera-active mode restores overview
    const bottomHud = document.getElementById('bottom-hud');
    if (bottomHud) {
      bottomHud.addEventListener('click', (e) => {
        if (document.body.classList.contains('unit-camera-active')) {
          const rect = bottomHud.getBoundingClientRect();
          if (e.clientY - rect.top < 28 && !e.target.closest('.card-slot') && !e.target.closest('button')) {
            this.viewport?.stopTrackingUnit();
            this.battlefield.selectedUnit = null;
            document.body.classList.remove('unit-camera-active');
          }
        }
      });
    }

    // Recompute the camera fit whenever the viewport actually changes size -
    // window resize, and orientationchange for the case some browsers fire
    // late/without an accompanying resize.
    const triggerViewportResize = () => {
      try {
        window.scrollTo(0, 0);
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      } catch (e) {}
      this.viewport.resize();
    };
    window.addEventListener('resize', triggerViewportResize);
    window.addEventListener('orientationchange', () => {
      triggerViewportResize();
      setTimeout(triggerViewportResize, 50);
      setTimeout(triggerViewportResize, 150);
      setTimeout(triggerViewportResize, 350);
      setTimeout(triggerViewportResize, 500);
    });

    // Keep the post-game Rematch overlay button centered on the visible
    // playfield across resizes/rotations too - only does anything while the
    // overlay is actually shown (it's a no-op cost otherwise).
    window.addEventListener('resize', () => {
      if (!document.getElementById('post-game-return-overlay')?.classList.contains('hidden')) {
        this._positionPostGameReturnOverlayButton();
      }
    });

    // Same as above, for the pause overlay's "PAUSED" text.
    window.addEventListener('resize', () => {
      if (!document.getElementById('pause-overlay')?.classList.contains('hidden')) {
        this._positionPauseOverlay();
      }
      if (!document.getElementById('online-reconnect-overlay')?.classList.contains('hidden')) {
        this._positionOnlineReconnectOverlay();
      }
    });

    const setupVolumeSlider = (sliderId, getVolume, setVolume, onCommit) => {
      const sliderEl = document.getElementById(sliderId);
      if (!sliderEl) return;
      const fillEl = sliderEl.querySelector('.hud-slider-fill');
      const labelEl = sliderEl.querySelector('.hud-slider-label');

      const updateVisual = (val) => {
        const clamped = Math.max(0, Math.min(1, val));
        const pct = Math.round(clamped * 100);
        if (fillEl) {
          fillEl.style.transform = `translate3d(${pct - 100}%, 0, 0)`;
        }
        sliderEl.classList.toggle('is-zero', pct === 0);
        sliderEl.setAttribute('aria-valuenow', String(pct));
        sliderEl.title = `${labelEl ? labelEl.textContent : 'Volume'}: ${pct}%`;
      };

      updateVisual(getVolume());

      let isDragging = false;
      let cachedLeft = 0;
      let cachedWidth = 0;
      let pendingRatio = null;
      let rafId = null;

      const applyRatio = (ratio, isCommit = false) => {
        const clamped = Math.max(0, Math.min(1, ratio));
        const pct = Math.round(clamped * 100);
        if (fillEl) {
          fillEl.style.transform = `translate3d(${pct - 100}%, 0, 0)`;
        }
        sliderEl.classList.toggle('is-zero', pct === 0);
        setVolume(clamped, isCommit);
        if (isCommit) {
          sliderEl.setAttribute('aria-valuenow', String(pct));
          sliderEl.title = `${labelEl ? labelEl.textContent : 'Volume'}: ${pct}%`;
        }
      };

      const handlePointerCoord = (clientX, isCommit = false) => {
        if (cachedWidth <= 0) return;
        const ratio = Math.max(0, Math.min(1, (clientX - cachedLeft) / cachedWidth));

        if (isCommit) {
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
          applyRatio(ratio, true);
          return;
        }

        pendingRatio = ratio;
        if (!rafId) {
          rafId = requestAnimationFrame(() => {
            rafId = null;
            if (pendingRatio !== null) {
              applyRatio(pendingRatio, false);
            }
          });
        }
      };

      sliderEl.addEventListener('pointerdown', (e) => {
        isDragging = true;
        sliderEl.classList.add('is-dragging');
        try { sliderEl.setPointerCapture(e.pointerId); } catch {}
        const rect = sliderEl.getBoundingClientRect();
        cachedLeft = rect.left;
        cachedWidth = rect.width;
        handlePointerCoord(e.clientX, false);
      });

      sliderEl.addEventListener('pointermove', (e) => {
        if (!isDragging || cachedWidth <= 0) return;
        handlePointerCoord(e.clientX, false);
      });

      const endDrag = (e) => {
        if (!isDragging) return;
        isDragging = false;
        sliderEl.classList.remove('is-dragging');
        try { sliderEl.releasePointerCapture(e.pointerId); } catch {}
        if (cachedWidth > 0 && e.clientX !== undefined) {
          handlePointerCoord(e.clientX, true);
        }
        if (onCommit) onCommit();
      };

      sliderEl.addEventListener('pointerup', endDrag);
      sliderEl.addEventListener('pointercancel', endDrag);

      // Keyboard navigation: arrow keys, Home, End
      sliderEl.addEventListener('keydown', (e) => {
        let cur = getVolume();
        let handled = true;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          cur = Math.max(0, cur - 0.05);
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          cur = Math.min(1, cur + 0.05);
        } else if (e.key === 'Home') {
          cur = 0;
        } else if (e.key === 'End') {
          cur = 1;
        } else {
          handled = false;
        }
        if (handled) {
          e.preventDefault();
          applyRatio(cur, true);
          if (onCommit) onCommit();
        }
      });

      // Mouse wheel adjustment
      sliderEl.addEventListener('wheel', (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 0.05 : -0.05;
        const next = Math.max(0, Math.min(1, getVolume() + delta));
        applyRatio(next, true);
        if (onCommit) onCommit();
      }, { passive: false });
    };

    setupVolumeSlider(
      'slider-volume-sfx',
      () => (this.audio.getSfxVolume ? this.audio.getSfxVolume() : this.audio.sfxVolume),
      (vol, persist) => this.audio.setSfxVolume(vol, persist),
      () => this.audio.playUiClick?.()
    );

    setupVolumeSlider(
      'slider-volume-music',
      () => (this.audio.getMusicVolume ? this.audio.getMusicVolume() : this.audio.musicVolume),
      (vol, persist) => this.audio.setMusicVolume(vol, persist)
    );

    const SOUND_MODE_LABELS = {
      sfx: `${getIconSvg('sound_sfx', 14)} Sound: SFX`,
      music: `${getIconSvg('sound_all', 14)} Sound: SFX+Music`,
      off: `${getIconSvg('sound_mute', 14)} Sound: OFF`
    };
    const btnMute = document.getElementById('btn-toggle-sound');
    if (btnMute) {
      btnMute.innerHTML = SOUND_MODE_LABELS[this.audio.getSoundMode()];
      btnMute.addEventListener('click', () => {
        const mode = this.audio.cycleSoundMode();
        btnMute.innerHTML = SOUND_MODE_LABELS[mode];
      });
    }

    const btnAutoDraw = document.getElementById('btn-toggle-autodraw');
    if (btnAutoDraw) {
      btnAutoDraw.innerHTML = '⟳';
      btnAutoDraw.title = this.autoDrawEnabled ? 'Auto-Draw: ON' : 'Auto-Draw: OFF';
      btnAutoDraw.classList.toggle('autodraw-active', this.autoDrawEnabled);
      btnAutoDraw.addEventListener('click', () => this.toggleAutoDraw());
    }

    const btnPause = document.getElementById('btn-pause');
    const pauseOverlay = document.getElementById('pause-overlay');
    const pauseOverlayInner = document.getElementById('pause-overlay-inner');
    const togglePause = () => {
      // Pausing only stops MY local rendering/input, not the opponent's
      // clock or the server - it would desync the match, not pause it.
      if (this.isOnline) return;
      this.isPaused = !this.isPaused;
      if (this.hud) this.hud._hasRenderedPausedHud = false;
      // Batch 4 (docs/SOUND_BATCH_PLAN.md section 4.4).
      this.audio.playUiClick();
      if (btnPause) {
        btnPause.innerHTML = this.isPaused ? ICON_PLAY : ICON_PAUSE;
        btnPause.title = this.isPaused ? 'Resume' : 'Pause';
        btnPause.classList.toggle('pause-active', this.isPaused);
      }
      if (pauseOverlay) pauseOverlay.classList.toggle('hidden', !this.isPaused);
      if (this.isPaused) {
        this._positionPauseOverlay();
        this.tauntManager?.closeMenu?.();
      }
    };
    if (btnPause) {
      btnPause.innerHTML = this.isPaused ? ICON_PLAY : ICON_PAUSE;
      btnPause.title = this.isPaused ? 'Resume' : 'Pause';
      btnPause.classList.toggle('pause-active', this.isPaused);
      btnPause.addEventListener('click', togglePause);
    }
    if (pauseOverlayInner) {
      // The centered "PAUSED" label doubles as an unpause button - clicking
      // it is equivalent to clicking the pause toggle again.
      pauseOverlayInner.addEventListener('click', () => {
        if (this.isPaused) togglePause();
      });
    }

    const btnSpeed = document.getElementById('btn-speed');
    if (btnSpeed) {
      btnSpeed.addEventListener('click', () => this.toggleSpeed());
    }

    const btnExhibition = document.getElementById('btn-exhibition');
    if (btnExhibition) {
      btnExhibition.addEventListener('click', () => {
        this.hud?.closeSetupSheet?.();
        this.startExhibitionMatch();
      });
    }

    const btnReplayTutorial = document.getElementById('btn-replay-tutorial');
    if (btnReplayTutorial) {
      btnReplayTutorial.addEventListener('click', () => {
        this.hud?.closeSetupSheet?.();
        this.onboardingGuide?.replay();
      });
    }

    const selDiff = document.getElementById('select-ai-difficulty');
    if (selDiff) {
      if (this.currentAIDifficulty) {
        selDiff.value = this.currentAIDifficulty;
      }
      selDiff.addEventListener('change', (e) => {
        this.setAIDifficulty(e.target.value);
      });
    }

    const selQuality = document.getElementById('select-quality');
    if (selQuality) {
      if (this.performanceManager?.manual) {
        selQuality.value = this.performanceManager.manual;
      }
      selQuality.addEventListener('change', (e) => {
        const v = e.target.value;
        this.performanceManager.setManual(v === 'auto' ? null : v);
      });
    }

    const btnToggleRefreshRate = document.getElementById('btn-toggle-refresh-rate');
    if (btnToggleRefreshRate) {
      btnToggleRefreshRate.innerText = this.isEcoMode ? 'Refresh Rate: 30Hz (ECO)' : 'Refresh Rate: 60Hz';
      btnToggleRefreshRate.classList.toggle('eco-active', this.isEcoMode);
      btnToggleRefreshRate.addEventListener('click', () => {
        this.audio?.playUiClick?.();
        this.setEcoMode(!this.isEcoMode);
      });
    }

    const btnTogglePerf = document.getElementById('btn-toggle-perf-overlay');
    if (btnTogglePerf && this.performanceManager) {
      btnTogglePerf.innerText = this.performanceManager.isOverlayEnabled() ? 'Perf HUD: ON' : 'Perf HUD: OFF';
      btnTogglePerf.classList.toggle('active', this.performanceManager.isOverlayEnabled());
      btnTogglePerf.addEventListener('click', () => {
        const isEnabled = this.performanceManager.toggleOverlay();
        btnTogglePerf.innerText = isEnabled ? 'Perf HUD: ON' : 'Perf HUD: OFF';
        btnTogglePerf.classList.toggle('active', isEnabled);
      });
    }

    const btnToggleAiHand = document.getElementById('btn-toggle-ai-hand');
    if (btnToggleAiHand) {
      const isVisible = !!this.showAiHand;
      btnToggleAiHand.innerText = isVisible ? 'AI Hand: ON' : 'AI Hand: OFF';
      btnToggleAiHand.classList.toggle('active', isVisible);
      btnToggleAiHand.addEventListener('click', () => {
        this.audio?.playUiClick?.();
        const next = this.toggleShowAiHand();
        btnToggleAiHand.innerText = next ? 'AI Hand: ON' : 'AI Hand: OFF';
        btnToggleAiHand.classList.toggle('active', next);

        const optionsModalBtn = document.getElementById('options-toggle-ai-hand');
        if (optionsModalBtn) {
          optionsModalBtn.className = `start-btn ${next ? 'start-btn-primary' : 'start-btn-secondary'}`;
          const optionsLabel = optionsModalBtn.querySelector('.start-btn-text');
          if (optionsLabel) optionsLabel.textContent = next ? 'EXHIBITION AI HAND: VISIBLE' : 'EXHIBITION AI HAND: HIDDEN';
        }
      });
    }

    const btnOpenLegal = document.getElementById('btn-open-legal');
    if (btnOpenLegal) {
      btnOpenLegal.addEventListener('click', () => {
        showLegalModal('imprint', this);
      });
    }

    const btnOpenDiscord = document.getElementById('btn-open-discord');
    if (btnOpenDiscord) {
      btnOpenDiscord.addEventListener('click', () => {
        window.open('https://discord.gg/UECTSvDB4u', '_blank', 'noopener,noreferrer');
      });
    }

    const btnOpenProfile = document.getElementById('btn-open-profile');
    if (btnOpenProfile) {
      btnOpenProfile.addEventListener('click', () => {
        showCallsignModal({ game: this });
      });
    }

    const btnOpenLeaderboard = document.getElementById('btn-open-leaderboard');
    if (btnOpenLeaderboard) {
      btnOpenLeaderboard.addEventListener('click', () => {
        showLeaderboardModal(this);
      });
    }

    const btnReturnMainMenu = document.getElementById('btn-return-main-menu');
    if (btnReturnMainMenu) {
      btnReturnMainMenu.addEventListener('click', () => {
        this.returnToMainMenu();
      });
    }

    const btnCloseSetupControls = document.getElementById('btn-close-setup-controls');
    if (btnCloseSetupControls) {
      btnCloseSetupControls.addEventListener('click', () => {
        this.hud?.closeSetupSheet?.();
      });
    }

    const btnOpenAccountIngame = document.getElementById('btn-open-account-ingame');
    if (btnOpenAccountIngame) {
      btnOpenAccountIngame.addEventListener('click', () => {
        showAccountFullscreenModal(this);
      });
    }

    const btnMainMenuReturn = document.getElementById('btn-main-menu-return');
    if (btnMainMenuReturn) {
      btnMainMenuReturn.addEventListener('click', () => {
        this.returnToMainMenu();
      });
    }

    const btnGameOverMainMenu = document.getElementById('btn-game-over-main-menu');
    if (btnGameOverMainMenu) {
      btnGameOverMainMenu.addEventListener('click', () => {
        this.returnToMainMenu();
      });
    }

    // Suppress native browser context menu on right-click / touch hold (prevents Android Chrome's "Copy image" popup on cards),
    // preserving native context menus only on editable text inputs.
    window.addEventListener('contextmenu', (e) => {
      if (this.activeTargetingPower) {
        e.preventDefault();
        this.cancelTargeting();
        return;
      }
      const target = e.target;
      if (target && ((target.matches && target.matches('input, textarea')) || (target.closest && target.closest('input, textarea, [contenteditable="true"]')))) {
        return;
      }
      e.preventDefault();
    }, { capture: true });

    // Suppress native HTML5 image dragging across the game
    window.addEventListener('dragstart', (e) => {
      if (e.target && e.target.tagName === 'IMG') {
        e.preventDefault();
      }
    }, { capture: true });

    // Hotkeys: Numbers 1-5 to play or draw cards, 'D' to draw first empty slot, 'F' for Fullscreen toggle, 'P' for Performance Overlay, 'N' for Superweapon (Plasma/Nuke), 'X' for 4x Speed toggle, 'C' for Auto-Draw toggle
    window.addEventListener('keydown', (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
        return;
      }

      if (e.key === 'Escape' || e.key === 'Esc') {
        if (this.activeTargetingPower) {
          this.cancelTargeting();
          return;
        }
        if (this.deckBuilderModal?.isOpen?.()) {
          return;
        }
        if (this.deckBuilder?.isOpen) {
          this.deckBuilder.closeModal();
          if (!this.isRunning || this.isGameOver) {
            this.startScreen?.show();
          }
          return;
        }
        if (this.packOpeningModal?.isOpen?.()) {
          this.packOpeningModal.close?.();
          return;
        }
        if (this.showroomModal?.isOpen?.()) {
          this.showroomModal.close?.();
          return;
        }
        if (this.slotMachineModal?.isOpen?.()) {
          this.slotMachineModal.close?.();
          return;
        }
        if (this.directivesModal?.isOpen?.()) {
          this.directivesModal.close?.();
          return;
        }
        if (this.hud?.isSetupSheetOpen?.()) {
          this.hud.closeSetupSheet();
          return;
        }
        if (this.viewport?.trackedUnit || document.body.classList.contains('unit-camera-active')) {
          this.viewport?.stopTrackingUnit();
          this.battlefield.selectedUnit = null;
          document.body.classList.remove('unit-camera-active');
          return;
        }
        if (this.isRunning && !this.isGameOver) {
          this.hud?.toggleSetupSheet?.();
          return;
        }
      }

      if (e.key === 'p' || e.key === 'P') {
        if (this.performanceManager) {
          const isEnabled = this.performanceManager.toggleOverlay();
          const btn = document.getElementById('btn-toggle-perf-overlay');
          if (btn) {
            btn.innerText = isEnabled ? 'Perf HUD: ON' : 'Perf HUD: OFF';
            btn.classList.toggle('active', isEnabled);
          }
        }
        return;
      }

      if (e.key === 'f' || e.key === 'F') {
        this.toggleFullscreen();
        return;
      }

      if (e.key === 'x' || e.key === 'X') {
        this.toggleSpeed();
        return;
      }

      if (e.key === 'c' || e.key === 'C') {
        this.toggleAutoDraw();
        return;
      }

      if (e.key === 'n' || e.key === 'N') {
        if (this.activeTargetingPower) {
          this.cancelTargeting();
          return;
        }
        // Superweapon hotkey: fires straight at the field's midline center,
        // no manual targeting - mirrors the tap-to-fire flow in endPointer()
        // above but skips the targeting-reticle step entirely per design.
        const charges = this.superweaponCharges || this.nukesAvailable;
        if (charges && charges[this.myTeam] > 0) {
          const centerX = this.battlefield.width / 2;
          const centerY = this.battlefield.height / 2;
          this.enqueueCommand({ type: 'NUKE', team: this.myTeam, x: centerX, y: centerY });
        } else {
          this.audio?.playError?.();
        }
        return;
      }

      if (e.key === 'd' || e.key === 'D') {
        const emptySlot = this.cardSystem.hands[this.myTeam].findIndex(s => s === null);
        // Routed through HUD.drawSlot() rather than enqueueCommand()
        // directly - see that method's comment in js/ui/hud.js. Calling
        // enqueueCommand() straight from here (as this used to) skipped
        // the Deploy/Draw Window's instant "played"/"drawing" feedback
        // entirely: the command still reached the simulation, but a card
        // played or drawn by keyboard looked like nothing had happened for
        // up to the full ~1.2s online lockstep delay, exactly the
        // regression that mechanism exists to prevent - it just never
        // reached this path.
        if (emptySlot !== -1) this.hud?.drawSlot(emptySlot);
        return;
      }

      const num = parseInt(e.key);
      if (num >= 1 && num <= 5) {
        const slotIdx = num - 1;
        const card = this.cardSystem.hands[this.myTeam][slotIdx];
        if (card) {
          this.hud?.playSlot(slotIdx);
        } else {
          this.hud?.drawSlot(slotIdx);
        }
      }
    });
  }

  startGameLoop() {
    if (this._rafId !== null && this._rafId !== undefined) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._rafId = requestAnimationFrame((t) => this.gameLoop(t));
  }

  gameLoop(timestamp) {
    // Schedule the next frame FIRST: if update() or render() throws, the rAF chain
    // must not die - otherwise a single runtime error freezes the game permanently.
    this._rafId = requestAnimationFrame((t) => this.gameLoop(t));

    // High-refresh render pacer: targets smooth 60 FPS (1/60s).
    // Allows 60Hz, 90Hz, 120Hz, and 144Hz displays to pace naturally at ~60 FPS without
    // dropping 90Hz displays down to 45 FPS or inducing micro-stutter.
    // Calculating pacer debt first avoids executing DOM queries and modal method checks on skipped 120Hz ticks.
    if (!this._lastRafTimestamp) this._lastRafTimestamp = timestamp;
    const rafElapsedSec = Math.min(0.1, Math.max(0, (timestamp - this._lastRafTimestamp) / 1000));
    this._lastRafTimestamp = timestamp;
    this._pacerDebt = Math.min(Game.TARGET_RENDER_DT * 2.0, (this._pacerDebt || 0) + rafElapsedSec);

    const minPacerDt = Game.TARGET_RENDER_DT * 0.70;
    if (this.lastTime > 0 && this._pacerDebt < minPacerDt) {
      return;
    }

    // ProMotion (120Hz) Throttle & Modal Yield:
    // When a full-screen modal (Cargo Bay, Matrix, Showroom, Deck Selector/Editor, Master, Options, Feed) is active
    // in single-player, completely yield the background game loop. This prevents background
    // viewport sync, mobile unit camera tracking, and frame-time poisoning in PerformanceManager.
    const isGameOverOpen = !this.isRunning && typeof document !== 'undefined' &&
      !!(this._elGameOverModal || (this._elGameOverModal = document.getElementById('game-over-modal')))?.classList?.contains('active');
    const hasFullscreenDialog = (!this.isRunning || this.isPaused || this.overlayGate?.isOpen?.() || (this._openMenus && this._openMenus.size > 0)) &&
      typeof document !== 'undefined' && !!document.querySelector('.fullscreen-dialog-overlay.active');
    const isFullscreenModalOpen = this.startScreen?.isVisible ||
                                  this.packOpeningModal?.isOpen() ||
                                  this.slotMachineModal?.isOpen() ||
                                  this.showroomModal?.isOpen() ||
                                  this.deckBuilder?.isOpen ||
                                  this.deckBuilderModal?.isOpen?.() ||
                                  this.directivesModal?.isOpen?.() ||
                                  this.onlineLobby?.isOpen ||
                                  this.overlayGate?.isOpen?.() ||
                                  hasFullscreenDialog;
    this._isFullscreenModalOpen = !!isFullscreenModalOpen;
    if ((isFullscreenModalOpen && !this.isOnline) || (isGameOverOpen && !this.isRunning)) {
      this.lastTime = timestamp;
      this._lastRenderTime = timestamp;
      this._pacerDebt = 0;
      return;
    }
    this._pacerDebt = Math.max(0, (this._pacerDebt || 0) - Game.TARGET_RENDER_DT);

    const elapsedSec = (timestamp - this.lastTime) / 1000;
    const frameDt = Math.min(0.1, this.lastTime > 0 ? elapsedSec : Game.TARGET_RENDER_DT);
    this.lastTime = timestamp;
    this._lastFrameDt = frameDt; // read by render() for hud.update() - see there

    const isTabHidden = typeof document !== 'undefined' && document.hidden;

    // Runs every frame regardless of pause/stall/isRunning - it's real-time
    // driven (frameDt), not tied to simulation ticks. Deliberately OUTSIDE
    // the isRunning gate below: handleGameOver() sets isRunning=false the
    // instant THIS client's own simulation reaches the winning/losing tick,
    // which (under normal lockstep operation) can easily happen a few
    // ticks before the opponent's client gets there - if polling stopped
    // here too, this client would stop submitting its own final turn(s)
    // the moment it "won"/"lost", permanently stranding the opponent's
    // canAdvanceTick() barrier waiting on data that would then never
    // arrive - the opponent's client simulates forward correctly up to
    // that point but can never reach the game-over tick itself, so only
    // one side ever shows the end screen. Keeping this running lets both
    // sides keep exchanging turns until both have actually reached it.
    if (this.isOnline && this.turnScheduler) {
      this.turnScheduler.pollNetwork(frameDt);
    }

    if (this.isRunning) {
      try {
        if (!this.isPaused) {
          if (this.cutscenePlayer.active) {
            // Cutscene owns the camera/HUD/actors this frame - normal
            // sim/AI/commands stay untouched until it finishes (see
            // CutscenePlayer.finish()'s onComplete callback). particles.
            // update() is called explicitly here too (normally part of the
            // full sim tick below, which does NOT run during a cutscene) -
            // a cutscene's own `particles` step (docs/singleplayer
            // implementation soft-foraging-flamingo.md) would otherwise
            // add an effect that never animates or expires.
            this.cutscenePlayer.update(frameDt);
            this.particles.update(frameDt);
          } else if (this.isCampaignMission && !this.campaignCombatArmed) {
            // Encounter is staged (cutscenes done, HUD interactive) but
            // combat is deliberately held until the player plays their
            // first card - see enqueueCommand()'s campaignCombatArmed flip
            // and docs/game-design-singleplayer-kampagne.md Kapitel 9.2
            // ("kein automatischer Schnitt"). Units simply sit idle.
          } else if (this.isOnline && this.wsClient) {
            // Dedicated 60 FPS visual interpolation for Authoritative WebSocket server
            this.updateOnlineWS(frameDt);
            if (this.battlefield.isGameOver) {
              this.handleGameOver();
            }
          } else {
            // Full Dual-Simulation for Single-Player, Host, and Guest!
            const pacing = (this.isOnline && this.turnScheduler?.getPacingMultiplier) ? this.turnScheduler.getPacingMultiplier() : 1.0;
            this.accumulator += frameDt * this.speedMultiplier * pacing;

            if (this.isOnline && this.turnScheduler) {
              this.accumulator = Math.min(this.accumulator, Game.MAX_ONLINE_ACCUMULATOR_SEC);
            } else if (isTabHidden) {
              // Clamp singleplayer accumulator when tab is backgrounded to prevent
              // spiral of death or burst CPU load upon refocusing
              this.accumulator = Math.min(this.accumulator, Game.TICK_DT * 2);
            }

            let ticksThisFrame = 0;
            const maxTicks = (this.performanceManager?.isMobile) ? 3 : Game.MAX_TICKS_PER_FRAME;
            while (this.accumulator >= Game.TICK_DT && ticksThisFrame < maxTicks) {
              if (this.isOnline && this.turnScheduler) {
                if (!this.turnScheduler.canAdvanceTick()) {
                  this.turnScheduler.setStalled(true);
                  break;
                }
                this.turnScheduler.setStalled(false);
                this.turnScheduler.beginTurnIfNeeded();
              }
              this.update(Game.TICK_DT);
              if (this.isOnline && this.turnScheduler) this.turnScheduler.advanceTick();
              this.accumulator -= Game.TICK_DT;
              ticksThisFrame++;
            }
            if (ticksThisFrame > 3 && this.renderer?.postProcess) {
              this.renderer.postProcess.skipHeavyPostFX = true;
            }
            if (!this.isOnline && ticksThisFrame >= maxTicks) {
              // Discard accumulated debt when frame rate drops severely below budget,
              // preventing infinite debt accumulation and subsequent 3x fast-forward replay bursts
              this.accumulator = Math.min(this.accumulator, Game.TICK_DT);
            }
            if (this.isOnline && this.autoDrawEnabled) {
              const plasma = this.economy.resources[this.myTeam]?.plasma || 0;
              const drawCost = this.cardSystem.getEffectiveDrawCost ? this.cardSystem.getEffectiveDrawCost(this.myTeam) : this.cardSystem.drawCost;
              if (plasma >= drawCost) {
                const emptySlot = this.cardSystem.hands[this.myTeam].findIndex(s => s === null);
                if (emptySlot !== -1) {
                  this.enqueueCommand({ type: 'DRAW', team: this.myTeam, slot: emptySlot });
                }
              }
            }
            if (this.battlefield.isGameOver) {
              this.handleGameOver();
            }
          }
        }
        let wasResized = false;
        if (!isTabHidden) {
          wasResized = this.viewport.syncIfNeeded();
          if (!isFullscreenModalOpen) {
            if (this.viewport?.isDesktop?.()) {
              // Desktop-exclusive unit tracking update: zooms in and follows.
              this.viewport.updateUnitTracking(frameDt);
              if (!this.viewport.trackedUnit && document.body.classList.contains('unit-camera-active')) {
                document.body.classList.remove('unit-camera-active');
                if (this.battlefield.selectedUnit?.isDead) {
                  this.battlefield.selectedUnit = null;
                }
              }
              // When single-unit tracking is not active, run the Desktop 2-state Overview <-> Embedded Lane
              // zoom spring and horizontal March / Combat Framing in State 1.
              if (!this.viewport.trackedUnit && this.viewport.trackingProgress <= 0.001) {
                this.viewport.updatePortraitZoom?.(frameDt);
                const framing = this.battlefield.getCameraFramingInfo
                  ? this.battlefield.getCameraFramingInfo(this.myTeam || 'western')
                  : (this.battlefield.getCameraFocalX ? this.battlefield.getCameraFocalX(this.myTeam || 'western') : this.battlefield.territoryDivider);
                this.viewport.followX(framing, frameDt);
              }
            } else {
              // Mobile: portrait zoom spring transition, unit tracking, match-start intro pan, or frontline/combat auto-follow
              this.viewport.updatePortraitZoom?.(frameDt);
              const wasTrackingUnit = !!this.viewport.trackedUnit;
              const handledByUnitFollow = this.viewport.updateMobileUnitFollow(frameDt);
              if (wasTrackingUnit && !this.viewport.trackedUnit && this.battlefield.selectedUnit?.isDead) {
                this.battlefield.selectedUnit = null;
              }
              if (handledByUnitFollow) {
                this.viewport.stopMobileIntro?.();
              } else {
                // If a unit crosses the line before the camera finishes its intro,
                // immediately cancel the intro and transition to the frontline tracking perspective.
                if (this.viewport.mobileIntro && this.battlefield?.hasUnitCrossedLine?.()) {
                  this.viewport.stopMobileIntro();
                }

                const handledByIntro = this.viewport.updateMobileIntro?.(frameDt);
                if (!handledByIntro) {
                  // Auto-follow the frontline in March Mode and frame both parties in Combat Mode
                  // (portrait only - see Viewport.followX, no-op elsewhere). Real-time driven like the
                  // render itself, not tied to sim ticks, and yields to a manual drag
                  // for a few seconds - see Viewport.panBy().
                  const framing = this.battlefield.getCameraFramingInfo
                    ? this.battlefield.getCameraFramingInfo(this.myTeam || 'western')
                    : (this.battlefield.getCameraFocalX ? this.battlefield.getCameraFocalX(this.myTeam || 'western') : this.battlefield.territoryDivider);
                  this.viewport.followX(framing, frameDt);
                }
              }
            }
          }
        }

        // Throttle actual canvas draws to TARGET_RENDER_DT regardless of how
        // often this RAF callback fires - see the constant's comment above.
        // The rAF chain itself (and network polling/sim ticking above) still
        // runs every callback so input latency and lockstep timing are
        // unaffected; only the expensive draw is skipped on the "extra"
        // callbacks a high-refresh display produces.
        // A generous jitter threshold (0.82 * TARGET_RENDER_DT ~= 13.6ms) ensures
        // standard 60Hz displays (which jitter between 15.2ms and 16.8ms) never
        // drop a frame to 30 FPS, while 120Hz displays still cleanly skip every
        // other callback to cap at 60 FPS.
        // While the sim is paused (pause menu / overlay over the lane) the scene
        // is static apart from ambient motion, so 15 fps is plenty - it was
        // redrawing the full battlefield at 60 fps behind the overlay.
        const targetRenderDt = this.isEcoMode
          ? (Game.TARGET_RENDER_DT * 2)
          : (this.isPaused ? (Game.TARGET_RENDER_DT * 4) : Game.TARGET_RENDER_DT);
        const renderDt = (timestamp - this._lastRenderTime) / 1000;
        if (!isTabHidden && (wasResized || renderDt >= targetRenderDt * 0.82)) {
          // Adjust lastRenderTime retaining fractional phase to eliminate drift and beat aliasing on 120Hz VRR/ProMotion displays
          // Guard against renderDt < targetRenderDt (VSync jitter) resetting lastRenderTime to the past!
          if (renderDt > 0.1 || wasResized) {
            this._lastRenderTime = timestamp;
          } else if (renderDt >= targetRenderDt) {
            this._lastRenderTime = timestamp - ((renderDt % targetRenderDt) * 1000);
          } else {
            this._lastRenderTime = timestamp;
          }
          // Optimization: Skip rendering the hidden canvas when a full-screen deck modal (Cargo Bay, Matrix, Showroom, Deck Selector/Editor) is open
          let renderWorkMs = 0;
          if (!isFullscreenModalOpen) {
            const t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
            this.render();
            renderWorkMs = (typeof performance !== 'undefined' && performance.now) ? (performance.now() - t0) : 0;
          }
          const actualFrameDt = (this._lastActualRenderTime > 0)
            ? (timestamp - this._lastActualRenderTime) / 1000
            : targetRenderDt;
          this._lastActualRenderTime = timestamp;
          this.performanceManager?.recordFrame(actualFrameDt, renderWorkMs, this._lastStageTimings);
        }
      } catch (err) {
        this.frameErrorCount = (this.frameErrorCount || 0) + 1;
        if (this.frameErrorCount <= 5) {
          console.error(`[UCG] Frame error #${this.frameErrorCount}:`, err);
        } else if (this.frameErrorCount === 6) {
          console.error('[UCG] Further frame errors suppressed.');
          // A sim that fails every single frame from here on would otherwise
          // look like a silent hang - not a visible crash - once suppression
          // above kicks in. Surfaces the same fatal-error banner a fully
          // uncaught error would (see js/engine/errorReporter.js), once.
          this.errorReporter?.report(err, 'gameLoop (repeated)');
        }
      }
    }
  }

  updateOnlineWS(dt) {
    // 1. Continuous smooth 60 FPS entity interpolation & visual timers
    this.stateStream?.updateInterpolation(this, dt);

    if (this.is2v2 && this.teamSuperLockout > 0) {
      this.teamSuperLockout = Math.max(0, this.teamSuperLockout - dt);
    }

    // 2. Continuous particle simulation & ambient effects (suppressed during fullscreen modal)
    if (!this._isFullscreenModalOpen) {
      this.particles?.update(dt);
    }
    this.audio?.updateWeather?.(this.battlefield?.mapId);

    // 3. Directives & stats tracking
    if (this.matchDirectiveTracker) {
      const myPlasma = this.economy?.resources?.[this.myTeam]?.plasma || 0;
      this.matchDirectiveTracker.update(dt, this.battlefield, myPlasma);
    }

    // 4. Auto-draw in online mode - per-slot pending tracking rather than
    // one global cooldown (docs/ONLINE_PERFORMANCE_AND_CARD_LAG_ANALYSIS.md
    // Ursache 2): a single `_lastOnlineDrawTime` throttle meant drawing into
    // one slot blocked a draw into a DIFFERENT slot that emptied moments
    // later (e.g. playing two cards back to back), stacking an extra
    // up-to-350ms wait onto the already-real RTT+broadcast delay. Each slot
    // now gets its own short retry window instead: skip a slot only while
    // it's still empty AND a DRAW for it was sent recently (still awaiting
    // the confirming WORLD_STATE); a slot that fills is dropped from
    // tracking immediately, and one whose DRAW packet got lost becomes
    // eligible again after the fallback window instead of staying stuck
    // forever.
    if (this.autoDrawEnabled) {
      const now = performance.now();
      if (!this._pendingAutoDrawSlots) this._pendingAutoDrawSlots = new Map();
      const plasma = this.economy?.resources?.[this.myTeam]?.plasma || 0;
      const drawCost = this.cardSystem.getEffectiveDrawCost ? this.cardSystem.getEffectiveDrawCost(this.myTeam) : this.cardSystem.drawCost;
      if (plasma >= drawCost) {
        const hand = this.cardSystem.hands[this.myTeam] || [];
        for (let slot = 0; slot < hand.length; slot++) {
          if (hand[slot] !== null) {
            this._pendingAutoDrawSlots.delete(slot);
            continue;
          }
          const pendingSince = this._pendingAutoDrawSlots.get(slot);
          if (pendingSince !== undefined && (now - pendingSince) < 800) continue;
          this._pendingAutoDrawSlots.set(slot, now);
          this.enqueueCommand({ type: 'DRAW', team: this.myTeam, slot });
          break; // one DRAW per frame - plasma isn't re-checked mid-loop
        }
      }
    }
  }

  update(dt) {
    if (this.isOnline && this.wsClient) {
      this.updateOnlineWS(dt);
      return;
    }

    // Apply every command queued since the last tick (click handlers,
    // hotkeys) before the tick's simulation step - same tick-boundary point
    // a network peer's commands would be applied at in an online match.
    for (const command of this.commandQueue.drain()) {
      this.commandProcessor.apply(command);
    }

    this.economy.update(dt);
    this.cardSystem.update(dt);
    this.battlefield.update(dt, this.particles, this.audio);
    this.buildings.update(dt);
    if (!this.isOnline && !this.isCampaignMission) {
      if (this.isExhibition && this.aiWestern) {
        this.aiWestern.update(dt);
        const westTaunt = this.aiWestern.takePendingTaunt?.();
        if (westTaunt) this.tauntManager?.showTauntBubble?.('western', westTaunt);
      }
      if (this.aiEastern) {
        this.aiEastern.update(dt);
        const eastTaunt = this.aiEastern.takePendingTaunt?.();
        if (eastTaunt) this.tauntManager?.showTauntBubble?.('eastern', eastTaunt);
      } else if (this.ai) {
        this.ai.update(dt);
        const botTaunt = this.ai.takePendingTaunt?.();
        if (botTaunt) this.tauntManager?.showTauntBubble?.(this.ai.team, botTaunt);
      }
    }
    this.particles.update(dt);
    this.audio?.updateWeather?.(this.battlefield?.mapId);
    if (this.matchDirectiveTracker) {
      const myPlasma = this.economy.resources[this.myTeam]?.plasma || 0;
      this.matchDirectiveTracker.update(dt, this.battlefield, myPlasma);
    }

    // Auto-draw: same command a manual 'D'/number-key press would send, just
    // fired for me automatically once a slot is empty and I can afford it.
    // One check per tick is enough - drain() at the top of the next tick
    // already reflects the slot filling before this runs again, so it can
    // never queue more than one DRAW per empty slot.
    if (this.autoDrawEnabled) {
      const plasma = this.economy.resources[this.myTeam].plasma;
      const drawCost = this.cardSystem.getEffectiveDrawCost ? this.cardSystem.getEffectiveDrawCost(this.myTeam) : this.cardSystem.drawCost;
      if (plasma >= drawCost) {
        const emptySlot = this.cardSystem.hands[this.myTeam].findIndex(s => s === null);
        if (emptySlot !== -1) {
          this.enqueueCommand({ type: 'DRAW', team: this.myTeam, slot: emptySlot });
        }
      }
    }

    // Deliberately NOT here: HUD/DOM updates (hud.update(), CO power button
    // text, nuke button state, upgrade panel affordability). update() runs
    // once per simulated TICK - up to MAX_TICKS_PER_FRAME times in a single
    // rendered frame after catching up from a stall - so doing DOM writes
    // here meant repeating the same getElementById()+innerText/style writes
    // 10-30 times for one visible frame, only the last of which mattered.
    // They're all in render() instead, which runs exactly once per frame
    // regardless of how many ticks it took to get there. See
    // MULTIPLAYER_PLAN.md's "Nachtrag" for the stutter this caused.

    if (this.isCampaignMission) {
      this._autoTrackCampaignPlayerUnit();
      this._checkCampaignEncounterCleared();
    }
    if (this.battlefield.isGameOver) {
      this.handleGameOver();
    }
  }

  // Camera automatically follows the player's spawned unit for the rest of
  // the encounter ("man darf nicht so weit blicken dürfen" - docs/game-
  // design-singleplayer-kampagne.md): reuses the exact same
  // viewport.startTrackingUnit() mechanism a manual unit click already
  // triggers in a normal match (see this file's click handler), just
  // fired automatically instead of requiring a click. One-shot per
  // mission - only the first western unit spawned gets tracked.
  _autoTrackCampaignPlayerUnit() {
    if (this._campaignShadeTracked || !this.campaignCombatArmed) return;
    const unit = this.battlefield.units.find(u => u.team === 'western' && !u.isDead);
    if (!unit) return;
    this._campaignShadeTracked = true;
    // Viewport.updateUnitTracking() reads this exact property straight off
    // the tracked unit (js/ui/viewport.js: "targetZoom = baseZoom *
    // (trackedUnit.__introZoomMult || 1.85)") - without it, combat itself
    // would default back to the normal 1.85 skirmish zoom the instant the
    // card is played, breaking continuity with the cutscene's own much
    // tighter framing ("die Kamera muss über die komplette Dauer VIEL näher
    // ans Geschehen heran" - docs/singleplayer implementation soft-
    // foraging-flamingo.md).
    unit.__introZoomMult = 5.5;
    this.viewport?.startTrackingUnit(unit);
  }

  render() {
    const t0Render = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    this.renderer.render(this.battlefield, this.particles);
    this.cutscenePlayer.renderActors(this.renderer.ctx, this.viewport);
    this.minimap.render();
    const tRenderDone = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;

    const t0Hud = tRenderDone;
    if (!this.debugFlags?.noHud) {
      this.hud.update(this._lastFrameDt || 0);
    }
    this.updateSuperButtonState();

    // 1.0 Hz In-Match Daily Directives Evaluation & Chip Update
    this._directiveCheckTimer = (this._directiveCheckTimer || 0) + (this._lastFrameDt || 0);
    if (this._directiveCheckTimer >= 1.0) {
      this._directiveCheckTimer = 0;
      if (this.directiveTrackerHUD && this.matchDirectiveTracker && this.hud && !this.is2v2) {
        this.directiveTrackerHUD.updateChip(this.matchDirectiveTracker, this.hud.getLiveGrade(), 1.0, this.myTeam || 'western');
      }
      if (!this.isExhibition && this.matchDirectiveTracker) {
        for (const d of directives.getDirectives()) {
          if (d.completed) continue;
          let completedNow = false;
          if (d.type === 'HOLD_TERRITORY' && this.matchDirectiveTracker.secAbove60Territory >= d.target) {
            completedNow = true;
          } else if (d.type === 'RAPID_PUSH' && this.matchDirectiveTracker.rapidPushCompleted) {
            completedNow = true;
          } else if (d.type === 'OVERWHELMING_FORCE' && this.matchDirectiveTracker.peakOwnUnits >= d.target) {
            completedNow = true;
          } else if (d.type === 'HIGH_RANK') {
            const g = this.hud?.getLiveGrade();
            if (g === 'A' || g === 'S' || g === 'S+') {
              completedNow = true;
            }
          } else if (d.type === 'AIR_WING_SURGE') {
            const pStats = this.battlefield?.stats?.teams?.[this.myTeam || 'western'];
            if (pStats?.airSurgeCompleted) completedNow = true;
          } else if (d.type === 'SPELL_MASTERY') {
            const pStats = this.battlefield?.stats?.teams?.[this.myTeam || 'western'];
            if ((pStats?.spellsPlayed || 0) >= d.target) completedNow = true;
          } else if (d.type === 'FORTRESS_PROTOCOL') {
            const pStats = this.battlefield?.stats?.teams?.[this.myTeam || 'western'];
            if ((pStats?.structuresPlayed || 0) >= d.target) completedNow = true;
          } else if (d.type === 'SCRAP_HARVEST') {
            const pStats = this.battlefield?.stats?.teams?.[this.myTeam || 'western'];
            if ((pStats?.salvagePlasmaEarned || 0) >= d.target) completedNow = true;
          } else if (d.type === 'SPLASH_CARNAGE') {
            const pStats = this.battlefield?.stats?.teams?.[this.myTeam || 'western'];
            if ((pStats?.splashKills || 0) >= d.target) completedNow = true;
          }
          if (completedNow) {
            directives.completeDirective(d.id);
            this.directiveTrackerHUD?.showCompletion(d);
            if (d.rewardRankBonus && this.hud) {
              this.hud.addBonusRankPoints(d.rewardRankBonus);
            }
          }
        }
      }
    }

    if (this.isOnline && this.turnScheduler) {
      const indicator = document.getElementById('online-stall-indicator');
      // isStalledForUI() (not the raw .stalled flag) requires the barrier to
      // have actually held for a sustained stretch - see its comment in
      // js/net/turnScheduler.js for why the raw flag flips too often on its
      // own, even in perfectly healthy play, to show to the player directly.
      if (indicator) indicator.classList.toggle('hidden', !this.turnScheduler.isStalledForUI());
    }

    const tHudDone = (typeof performance !== 'undefined' && performance.now) ? performance.now() : 0;
    this._lastStageTimings = {
      worldMs: this.renderer?.lastWorldMs || (tRenderDone - t0Render),
      postMs: this.renderer?.lastPostMs || 0,
      hudMs: tHudDone - t0Hud,
      renderMs: tHudDone - t0Render
    };
  }

  // Checks both teams' built attachments against the click point (canvas
  // Selects the topmost unit under a click for the on-field highlight.
  // Neustart "Marine Null" removed the on-field upgrade panel this used to
  // open (see NEUSTART_PLAN.html Phase 4). It's mostly visual now, but
  // clone_unit/upper_hand still read it as the intended target the instant
  // their card is played - see enqueueCommand()'s targetUnitId snapshot,
  // which is what actually crosses the network (this field itself never
  // does).
  handleUnitClick(x, y) {
    let clickedUnit = null;

    // Prioritize western first, then eastern
    for (const team of ['western', 'eastern']) {
      if (clickedUnit) break;
      for (let i = this.battlefield.units.length - 1; i >= 0; i--) {
        const u = this.battlefield.units[i];
        if (u.team === team && !u.isDead) {
          const uiY = u.isAir ? u.y - 18 : u.y;
          if (Math.hypot(u.x - x, uiY - y) <= u.radius + this.viewport.hitRadiusBonus()) {
            clickedUnit = u;
            break;
          }
        }
      }
    }

    this.battlefield.selectedUnit = clickedUnit;
    if (clickedUnit && clickedUnit.team === this.myTeam) {
      this.audio?.playBark?.(clickedUnit.race, 'select');
    }

    // Camera follows the clicked unit on every platform; only desktop also
    // zooms in (Viewport.updateUnitTracking) and gets the collapsed-HUD
    // 'unit-camera-active' treatment - mobile just pans (updateMobileUnitFollow).
    if (clickedUnit) {
      this.viewport?.startTrackingUnit(clickedUnit);
      if (this.viewport?.isDesktop?.()) document.body.classList.add('unit-camera-active');
    } else if (this.isCampaignMission && !this._campaignFullHudCombat) {
      // "Vollwertiger Kampf" paradigm (docs/singleplayer implementation
      // soft-foraging-flamingo.md): zooming out to the whole-map overview
      // is reserved for a full-HUD battle (e.g. a boss fight) - tapping
      // empty battlefield space during a lightweight encounter like
      // Mission 1 must NOT snap the camera back out, or it breaks the
      // cinematic framing the whole encounter was staged around.
    } else {
      this.viewport?.stopTrackingUnit();
      document.body.classList.remove('unit-camera-active');
    }
    return clickedUnit;
  }

  // Called once from update() while isGameOver just became true. Stops the
  // sim right here - update()/render() won't run again until startMatch()
  // flips isRunning back on - so this never re-fires and re-writes the DOM.
  // Ends a campaign mission without the PvP game-over modal: plays the
  // mission's closing cutscene (if any) on victory, then stops the loop.
  // isRunning is deliberately left true until the cutscene's onComplete
  // fires - gameLoop() only renders/updates a cutscene while isRunning is
  // true, so flipping it false here first would freeze the closing scene
  // on its very first frame.
  _handleCampaignMissionEnd() {
    // isRunning deliberately stays true through the closing cutscene (see
    // this method's own doc comment above), so it can't guard re-entrancy
    // here the way handleGameOver()'s top-level check does for a normal
    // match - a multi-tick catch-up burst in the same frame (see that
    // check's comment in handleGameOver()) would otherwise call this again
    // on every remaining tick and restart the cutscene each time.
    if (this._campaignMissionEnded) return;
    this._campaignMissionEnded = true;

    if (this.viewport?.trackedUnit) {
      this.viewport.stopTrackingUnit();
    }
    document.body.classList.remove('unit-camera-active');

    const isVictory = this.battlefield.winner === (this.myTeam || 'western');
    const mission = CAMPAIGN_MISSIONS[this.currentCampaignMissionId];
    // Built dynamically from wherever the Shade actually ended combat
    // (docs/singleplayer implementation soft-foraging-flamingo.md) rather
    // than a fixed js/data/campaignCutscenes.js timeline - unlike the intro
    // (a scripted approach with known waypoints), the fight's end position
    // varies with how combat actually played out, so this can't be
    // pre-authored data.
    const survivor = isVictory ? this.battlefield.units.find(u => u.team === 'western' && !u.isDead) : null;
    const timeline = survivor ? this._buildCampaignVictoryTimeline(survivor) : null;

    if (timeline) {
      // Hands the already-fighting Shade back to cutscene control (same
      // real unit throughout, not a second placeholder actor) - mirrors
      // startCampaignMission()'s own intro-cutscene unitActor pattern, just
      // re-using an existing Unit instead of spawning a new one.
      survivor.isCinematic = true;
      // Clears the stale combat target: js/engine/render/units.js's
      // isWalking() heuristic treats "has a target AND off cooldown AND
      // that target is in range" as "not walking" (attacking pose) - a
      // leftover reference to the marine it just killed would otherwise
      // freeze the Shade in its stand pose for the walk-off step below
      // even though its x/y is visibly changing ("er slidet stehend").
      survivor.target = null;
      this.cutscenePlayer.play(timeline, () => {
        this.isRunning = false;
        this._playCampaignOutroSequence();
        console.log(`[UCG] Campaign mission "${this.currentCampaignMissionId}" complete.`);
      });
      this.cutscenePlayer.actors.set('shade', survivor);
    } else {
      this.isRunning = false;
      if (isVictory) this._playCampaignOutroSequence();
      console.log(`[UCG] Campaign mission "${this.currentCampaignMissionId}" ended (${isVictory ? 'victory' : 'defeat'}).`);
    }
  }

  // Victory-only closing beat (docs/singleplayer implementation soft-
  // foraging-flamingo.md): camera zooms in tight on the Shade wherever he
  // actually ended the fight, he hops in place (a plain position bob - no
  // dedicated jump sprite exists, and none is needed for a single small
  // gesture), a particle burst lands with him, then he walks off screen
  // WITHOUT a matching cameraFollow step - the camera deliberately stays
  // exactly where the first step left it, per "die Kamera bleibt stehen,
  // der Shade läuft aus dem Bild".
  _buildCampaignVictoryTimeline(survivor) {
    const sx = survivor.x, sy = survivor.y;
    return {
      steps: [
        { type: 'cameraFollow', actorId: 'shade', zoom: 7.5, duration: 0.6, at: 0 },
        { type: 'actor', actorId: 'shade', anim: 'walk', path: [{ x: sx, y: sy }, { x: sx, y: sy - 22 }, { x: sx, y: sy }], duration: 0.5, at: 0.6 },
        { type: 'particles', actorId: 'shade', effect: 'shockwave', radius: 42, color: '#00f0ff', duration: 0.3, at: 1.0 },
        // Kurze Stand-Pause nach der Landung (Sprung endet bei 1.1) bevor er
        // losgeht - reine Timing-Lücke, keine neue Logik nötig (die Steh-
        // Pose greift automatisch, solange kein Actor-Schritt seine Position
        // bewegt).
        { type: 'actor', actorId: 'shade', anim: 'walk', path: [{ x: sx, y: sy }, { x: sx + 260, y: sy + 6 }], duration: 2.2, at: 1.7 }
      ]
    };
  }

  // Only ever run after a campaign mission's victory cutscene finishes -
  // Mission 1 is the only content that exists yet, so this plays a scripted
  // "outro" instead of a real chapter-complete/rewards screen (docs/
  // singleplayer implementation soft-foraging-flamingo.md): fade the frozen
  // last frame to black, "DECK INVADERS" grows from a single pixel to
  // nearly full-screen, jitters in its last moments, explodes past the
  // viewer's camera, hold black for a beat, then cross-fade into deck
  // selection (the deck modal is opened WHILE still hidden behind full
  // black, so the fade-out reveals it directly rather than flashing the
  // stale battlefield frame first).
  _playCampaignOutroSequence() {
    const overlay = document.getElementById('campaign-demo-end-overlay');
    const backdrop = document.getElementById('campaign-demo-end-backdrop');
    const jitterWrap = document.getElementById('campaign-demo-end-jitter');
    const text = document.getElementById('campaign-demo-end-text');
    if (!overlay || !backdrop || !jitterWrap || !text) {
      this.isCampaignMission = false;
      this.deckBuilder?.open();
      return;
    }

    const FADE_TO_BLACK_MS = 800;
    const GROW_MS = 2200;
    const JITTER_MS = 450;
    const EXPLODE_MS = 320;
    const HOLD_BLACK_MS = 1000;
    const FADE_TO_MENU_MS = 800;

    overlay.classList.remove('hidden');
    void backdrop.offsetWidth; // force reflow so the class toggle below actually transitions
    backdrop.classList.add('visible');

    setTimeout(() => {
      void text.offsetWidth;
      text.classList.add('growing');
    }, FADE_TO_BLACK_MS);

    setTimeout(() => {
      jitterWrap.classList.add('jittering');
    }, FADE_TO_BLACK_MS + GROW_MS - JITTER_MS);

    setTimeout(() => {
      jitterWrap.classList.remove('jittering');
      text.classList.remove('growing');
      void text.offsetWidth;
      text.classList.add('exploding');
    }, FADE_TO_BLACK_MS + GROW_MS);

    const holdStart = FADE_TO_BLACK_MS + GROW_MS + EXPLODE_MS;
    setTimeout(() => {
      this.deckBuilder?.open();
      backdrop.classList.remove('visible'); // starts the fade-from-black; deck modal is already there underneath
    }, holdStart + HOLD_BLACK_MS);

    setTimeout(() => {
      overlay.classList.add('hidden');
      backdrop.classList.remove('visible');
      text.classList.remove('growing', 'exploding');
      jitterWrap.classList.remove('jittering');
      this.isCampaignMission = false;
    }, holdStart + HOLD_BLACK_MS + FADE_TO_MENU_MS + 50);
  }

  // Shared achievement-facts assembly (docs/XP_PROGRESSION_PLAN.md section 7)
  // - used both right after a graded match (handleGameOver(), lastMatch
  // populated) and reactively for non-match achievements like "Hochroller"
  // or "Zielübung" that would otherwise only ever get checked the next
  // time a match happens to end (see _checkAchievements() below).
  _assembleAchievementFacts(lastMatch = null) {
    return buildAchievementFacts({ progress, inventory, lastMatch });
  }

  _checkAchievements(lastMatch = null) {
    const newlyUnlocked = progress.evaluateAchievements(ACHIEVEMENTS, this._assembleAchievementFacts(lastMatch));
    if (newlyUnlocked && newlyUnlocked.length > 0) {
      for (const def of newlyUnlocked) {
        this.achievementOverlay?.show?.(def);
      }
    }
  }

  _clearAiRematchCountdown() {
    if (this._aiRematchTimer) {
      clearInterval(this._aiRematchTimer);
    }
    this._aiRematchTimer = null;
    const btnRematch = typeof document !== 'undefined' ? document.getElementById('btn-rematch') : null;
    if (btnRematch) {
      btnRematch.innerText = 'Rematch';
    }
  }

  // Cleanly terminates an ongoing match (e.g. when editing or adding a deck in Archive)
  terminateOngoingMatch() {
    this._clearAiRematchCountdown();
    this._endOnlineSession();
    this.isRunning = false;
    this.isGameOver = true;
    this.cancelTargeting?.();
    if (this.viewport?.trackedUnit) {
      this.viewport.stopTrackingUnit();
    }
    document.body.classList.remove('unit-camera-active');
    this.audio?.stopAmbience?.();
    this.battlefield?.reset?.();
    this.hud?.resetForNewMatch?.();
    if (this.hud?.resetLiveGamerank) {
      this.hud.resetLiveGamerank();
    }
    this.stateStream?.reset?.();
    this._hidePostGameReturnOverlay?.();
    this.hubNavigator?.updateFloatingVoidButton?.();
  }

  showGameOverToast(msg) {
    const toast = document.getElementById('game-over-toast');
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.remove('show');
    void toast.offsetWidth; // force reflow
    toast.classList.add('show');
    clearTimeout(this._gameOverToastTimer);
    this._gameOverToastTimer = setTimeout(() => {
      toast.classList.remove('show');
    }, 2500);
  }

  handleDareShare() {
    this.audio?.playUiClick?.();
    const dareData = this._lastMatchDareData || {};
    const deckSource = dareData.deck || this.currentPlayerDeckId || 'Human';
    const encodedDeck = encodeDeck(deckSource);

    const params = new URLSearchParams();
    params.set('dare', '1');
    if (dareData.by) params.set('by', dareData.by);
    params.set('deck', encodedDeck);
    if (dareData.grade) params.set('grade', dareData.grade.replace('+', 'plus').replace('-', 'minus'));
    if (typeof dareData.score === 'number') params.set('score', String(dareData.score));
    if (dareData.duration) params.set('time', String(dareData.duration));
    if (dareData.mvp) params.set('mvp', dareData.mvp);
    const auth = getAuth();
    if (auth?.accountId) params.set('ref', String(auth.accountId));

    const origin = window.location.origin + window.location.pathname;
    const dareUrl = `${origin}?${params.toString()}`;

    const isMobile = typeof navigator !== 'undefined' && /mobile|android|iphone|ipad|ipod/i.test(navigator.userAgent || '');
    if (isMobile && typeof navigator.share === 'function') {
      const shareTitle = 'VOID Conflict — Tactical Dare';
      const shareText = `Can you beat Commander ${dareData.by || 'Overlord'}'s ${dareData.grade || 'S'} record in VOID Conflict? Accept my Tactical Dare now!`;
      navigator.share({
        title: shareTitle,
        text: shareText,
        url: dareUrl
      }).catch((err) => {
        if (err.name !== 'AbortError') {
          this._copyDareLink(dareUrl);
        }
      });
    } else {
      this._copyDareLink(dareUrl);
    }
  }

  _copyDareLink(dareUrl) {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(dareUrl).then(() => {
        this.showGameOverToast('CHALLENGE LINK COPIED');
      }).catch(() => {
        this._fallbackCopyText(dareUrl);
      });
    } else {
      this._fallbackCopyText(dareUrl);
    }
  }

  _fallbackCopyText(text) {
    try {
      const input = document.createElement('textarea');
      input.value = text;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.focus();
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      this.showGameOverToast('CHALLENGE LINK COPIED');
    } catch {
      this.showGameOverToast('CHALLENGE LINK COPIED');
    }
  }

  _checkTacticalDareLink() {
    if (typeof window === 'undefined' || !window.location?.search) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('dare') !== '1') return;

      const by = (params.get('by') || 'COMMANDER').trim();
      const deckCode = params.get('deck') || 'p:Human';
      const grade = (params.get('grade') || 'B').replace('plus', '+').replace('minus', '-');
      const score = Math.max(0, Math.min(100, parseInt(params.get('score'), 10) || 50));
      const timeSec = Math.max(1, parseInt(params.get('time'), 10) || 180);
      const mvp = params.get('mvp') || 'Rocketship';
      const ref = params.get('ref') || null;

      const enemyDeck = decodeDeck(deckCode);

      this.activeDare = {
        by,
        deckCode,
        grade,
        score,
        timeSec,
        mvp,
        ref,
        enemyDeck
      };

      grantAlphaAccess();

      // Activate recruit welcome bonus if arriving with ref
      if (ref) {
        try {
          fetch('api/war_bounty.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'activate',
              ref,
              dareCode: deckCode,
              recruitAccountId: getAuth()?.accountId || null
            })
          }).then(res => res.json()).then(data => {
            if (data?.ok && data.bountyId) {
              this.activeDare.bountyId = data.bountyId;
              if (data.starterRewards?.fugazi) {
                wallet.addFugazi(data.starterRewards.fugazi, 'recruit_bounty');
              }
              if (data.starterRewards?.packs) {
                wallet.addTickets(data.starterRewards.packs, 'recruit_bounty');
              }
            }
          }).catch(() => {});
        } catch {}
      }

      // Hide start screen and present Challenge Briefing
      setTimeout(() => {
        this.startScreen?.hide();
        this._showChallengeBriefingModal();
      }, 150);
    } catch (err) {
      console.warn('[Game] Error checking tactical dare link:', err);
    }
  }

  _showChallengeBriefingModal() {
    if (!this.activeDare) return;
    this._isChallengeBriefing = true;

    const modal = document.getElementById('game-over-modal');
    const modalBox = modal?.querySelector('.game-over-modal-box');
    const badge = document.getElementById('game-over-badge');
    const title = document.getElementById('game-over-title');
    const desc = document.getElementById('game-over-desc');
    const durationEl = document.getElementById('game-over-duration');
    const modeEl = document.getElementById('game-over-mode');
    const gamerankShowcase = document.getElementById('gamerank-showcase');
    const gamerankBadge = document.getElementById('gamerank-badge');
    const letterVal = document.getElementById('gamerank-letter-val');
    const titleVal = document.getElementById('gamerank-title-val');
    const scoreVal = document.getElementById('gamerank-score-val');
    const barFill = document.getElementById('gamerank-bar-fill');
    const fugaziRewardEl = document.getElementById('game-over-fugazi-reward');
    const btnRematch = document.getElementById('btn-rematch');
    const btnDare = document.getElementById('btn-game-over-dare');
    const statsContainer = modal?.querySelector('.stats-comparison-container');
    const playerName = document.getElementById('stats-name-player');
    const enemyName = document.getElementById('stats-name-enemy');
    const playerPill = document.getElementById('stats-pill-player');
    const enemyPill = document.getElementById('stats-pill-enemy');

    if (!modal) return;

    if (!modal.classList.contains('active')) {
      this.overlayGate?.acquire?.();
    }
    modal.className = 'modal-container active';

    if (modalBox) {
      modalBox.className = 'modal-box game-over-modal-box victory challenge-briefing';
    }

    if (badge) {
      badge.innerHTML = `${getIconSvg('energy', 14)} TACTICAL DARE RECEIVED`;
      badge.style.color = '#c084fc';
      badge.style.borderColor = 'rgba(168, 85, 247, 0.5)';
      badge.style.background = 'rgba(168, 85, 247, 0.16)';
    }

    const mins = Math.floor(this.activeDare.timeSec / 60);
    const secs = this.activeDare.timeSec % 60;
    const formattedDuration = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

    if (title) {
      title.innerText = `COMMANDER ${this.activeDare.by.toUpperCase()} CHALLENGES YOU`;
      title.style.color = '#c084fc';
      title.style.display = 'block';
    }

    if (desc) {
      desc.innerText = `Can you defeat Commander ${this.activeDare.by}'s fleet in under ${formattedDuration} Min?`;
      desc.style.display = 'block';
    }

    if (durationEl) durationEl.innerText = `Beat ${formattedDuration} Min`;
    if (modeEl) modeEl.innerText = 'Challenge';

    if (playerName) playerName.innerText = 'YOU';
    if (playerPill) {
      playerPill.className = 'team-outcome-pill winner';
      playerPill.innerText = 'CHALLENGER';
    }

    if (enemyName) enemyName.innerText = `${this.activeDare.by.toUpperCase()} (GHOST)`;
    if (enemyPill) {
      enemyPill.className = 'team-outcome-pill loser';
      enemyPill.innerText = 'DEFENDER';
    }

    if (gamerankShowcase) {
      gamerankShowcase.hidden = false;
      const cleanGrade = (this.activeDare.grade || 'B').toUpperCase();
      const gradeClass = `rank-${cleanGrade.toLowerCase().replace('+', '-plus').replace('-', '-minus')}`;
      if (gamerankBadge) gamerankBadge.className = `gamerank-crest-outer ${gradeClass}`;
      if (letterVal) letterVal.innerText = cleanGrade;
      if (titleVal) titleVal.innerText = 'RECORD TO BEAT';
      if (scoreVal) scoreVal.innerText = String(this.activeDare.score);
      if (barFill) barFill.style.width = `${this.activeDare.score}%`;
    }

    if (fugaziRewardEl) fugaziRewardEl.hidden = true;

    // MVP Spotlight for the enemy's MVP unit
    const unitMvpRow = document.getElementById('unit-mvp-spotlight');
    const unitMvpCanvas = document.getElementById('unit-mvp-canvas');
    const unitMvpNameEl = document.getElementById('unit-mvp-name-val');
    const unitMvpStatLabelEl = document.getElementById('unit-mvp-stat-label');
    const unitMvpDamageEl = document.getElementById('unit-mvp-damage-val');
    const unitMvpLifespanEl = document.getElementById('unit-mvp-lifespan-val');

    if (unitMvpRow) {
      unitMvpRow.hidden = false;
      const mvpName = this.activeDare.mvp || 'Shade';
      let card = getCardByName(mvpName);
      if (!card) {
        card = CARDS_DATA.find(c =>
          c.name?.toLowerCase() === mvpName.toLowerCase() ||
          c.unitType?.toLowerCase() === mvpName.toLowerCase()
        );
      }
      const unitType = card?.unitType || mvpName;
      const race = card?.race || (unitType.includes('Sul') || ['AntiShade', 'RejectedLoaf', 'LootingVulture', 'ParasiteVulture', 'EnforcerVulture'].includes(unitType) ? 'Sul' : 'Human');

      if (unitMvpNameEl) unitMvpNameEl.textContent = card?.name || mvpName;
      if (unitMvpStatLabelEl) unitMvpStatLabelEl.textContent = 'MVP';
      if (unitMvpDamageEl) unitMvpDamageEl.textContent = 'LEAD';
      if (unitMvpLifespanEl) unitMvpLifespanEl.textContent = formattedDuration;

      this._unitMvpSpotlightController?.stop();
      this._unitMvpSpotlightController = null;

      if (unitMvpCanvas) {
        const syntheticMvp = {
          id: 'dare_mvp_1',
          score: 100,
          lifetime: this.activeDare.timeSec || 180,
          team: 'western',
          cardData: card || { name: mvpName, unitType, race },
          unitType: unitType,
          damageDealt: 100,
          healingDone: 0,
          damageTaken: 0,
          materialCredit: 0,
          dominantAxis: 'damage'
        };

        const initSpotlight = () => {
          if (!unitMvpCanvas || !this._isChallengeBriefing) return;
          const rect = unitMvpCanvas.getBoundingClientRect();
          const w = Math.round(rect.width) || Math.round(unitMvpCanvas.parentElement?.getBoundingClientRect().width) || 300;
          const h = Math.round(rect.height) || 64;
          unitMvpCanvas.width = Math.max(1, w);
          unitMvpCanvas.height = Math.max(1, h);
          this._unitMvpSpotlightController?.stop();
          this._unitMvpSpotlightController = createUnitMvpSpotlight(unitMvpCanvas, syntheticMvp);
        };

        initSpotlight();
        requestAnimationFrame(() => {
          if (this._isChallengeBriefing && unitMvpCanvas) {
            const freshRect = unitMvpCanvas.getBoundingClientRect();
            if (freshRect.width > 10 && Math.abs(unitMvpCanvas.width - freshRect.width) > 5) {
              initSpotlight();
            }
          }
        });
      }
    }

    if (btnRematch) {
      btnRematch.innerText = 'Consider it done';
    }
    const btnDecks = document.getElementById('btn-game-over-decks');
    if (btnDecks) {
      btnDecks.innerText = 'Edit Deck';
    }
    if (btnDare) {
      btnDare.style.display = 'none'; // hide until first match is finished
    }
    if (statsContainer) {
      statsContainer.style.display = 'none'; // hide comparison stats before combat
    }

    // Hide Quests button in briefing
    const footerActions = modal?.querySelector?.('.game-over-footer-actions');
    if (footerActions) {
      footerActions.style.display = 'none';
    }
    const btnGameOverDirectives = document.getElementById('btn-game-over-directives');
    if (btnGameOverDirectives) {
      btnGameOverDirectives.style.display = 'none';
    }
  }

  _closeGameOverModal() {
    this._clearAiRematchCountdown();
    const gameOverModal = document.getElementById('game-over-modal');
    if (gameOverModal) {
      if (gameOverModal.classList.contains('active')) {
        this.overlayGate?.release?.();
      }
      gameOverModal.className = 'modal-container';
    }
    const btnRematch = document.getElementById('btn-rematch');
    if (btnRematch) btnRematch.innerText = 'Rematch';
    const btnDecks = document.getElementById('btn-game-over-decks');
    if (btnDecks) btnDecks.innerText = 'Archive';
    const btnDare = document.getElementById('btn-game-over-dare');
    if (btnDare) btnDare.style.display = '';
    const statsContainer = gameOverModal?.querySelector('.stats-comparison-container');
    if (statsContainer) statsContainer.style.display = '';

    if (this.activeDare && !this._isChallengeBriefing) {
      this.activeDare = null;
      this._challengePlayed = false;
    }

    const footerActions = gameOverModal?.querySelector?.('.game-over-footer-actions');
    if (footerActions && !this.isExhibition && !this.activeDare) {
      footerActions.style.display = '';
    }
    const btnGameOverDirectives = document.getElementById('btn-game-over-directives');
    if (btnGameOverDirectives && !this.isExhibition && !this.activeDare) {
      btnGameOverDirectives.style.display = '';
    }

    this._hidePostGameReturnOverlay?.();
    this._unitMvpSpotlightController?.stop();
    this._unitMvpSpotlightController = null;
    const unitMvpRow = document.getElementById('unit-mvp-spotlight');
    if (unitMvpRow) unitMvpRow.hidden = true;
    const unitMvpNameEl = document.getElementById('unit-mvp-name-val');
    if (unitMvpNameEl) unitMvpNameEl.textContent = '--';
    const fugaziRewardEl = document.getElementById('game-over-fugazi-reward');
    if (fugaziRewardEl) fugaziRewardEl.hidden = true;
    inboxManager.checkInbox();
  }

  // Returns player to the title Startseite, terminating simulation and closing all modals
  returnToMainMenu() {
    this._clearAiRematchCountdown();
    this.isExhibition = false;
    this.activeDare = null;
    this._isChallengeBriefing = false;
    this.terminateOngoingMatch();
    this.deckBuilder?.closeModal?.();
    if (this.deckBuilder) {
      this.deckBuilder._lastSelectedDeckKey = null;
    }
    this.showroomModal?.close?.();
    this.slotMachineModal?.close?.();
    this.packOpeningModal?.close?.();
    this.directivesModal?.close?.();
    this.onlineLobby?.closeModal?.();
    
    // Close in-game burger controls if open
    this.hud?.closeSetupSheet?.();
    const setupControls = document.getElementById('setup-controls');
    if (setupControls) setupControls.classList.remove('active', 'open');
    
    // Close game-over modal if open
    this._closeGameOverModal();

    // Close post-game-return-overlay if open
    this._hidePostGameReturnOverlay?.();

    this.startScreen?.show();
    this.audio?.playMenuMusic?.();
    notifyUpdateManagerIdle(this);
  }

  handleGameOver() {
    // Guards against a real re-entrancy case: update() runs once per tick,
    // and the tick-consuming loop in gameLoop() doesn't check isRunning
    // between ticks - so if the game ends mid-way through a multi-tick
    // catch-up burst (after a stall clears), this would otherwise fire
    // again on every remaining tick in that same burst.
    if (!this.isRunning) return;

    if (this.isCampaignMission) {
      // Campaign encounters skip the whole PvP-oriented modal below
      // (rank/deck-name/stats comparisons that don't apply to a scripted
      // encounter) - see _handleCampaignMissionEnd().
      this._handleCampaignMissionEnd();
      return;
    }

    this.isRunning = false;

    if (!this._lastMatchMode) {
      this._lastMatchMode = this.getLastMatchMode();
    }

    if (this.viewport?.trackedUnit) {
      this.viewport.stopTrackingUnit();
    }
    document.body.classList.remove('unit-camera-active');

    if (this.hud?.resetLiveGamerank) {
      this.hud.resetLiveGamerank();
    }

    this.tauntManager?.closeMenu?.();

    const playerTeam = this.myTeam || 'western';
    const enemyTeam = playerTeam === 'western' ? 'eastern' : 'western';
    const isVictory = this.battlefield.winner === playerTeam;

    // AI Adaptive Session Memory: record match for counter-deck learning in single-player
    if (!this.isOnline && this.ai && !this.isExhibition) {
      const playerCards = this.cardSystem?.playerDeck || this.cardSystem?.decks?.[playerTeam] || [];
      aiAdaptiveMemory.recordMatch({
        playerCards,
        botWon: this.battlefield?.winner === enemyTeam,
        difficulty: this.currentAIDifficulty || this.ai?.difficulty || 'hard',
        botArchetype: this.ai?.subArchetypeId || this.ai?.archetypeId
      });
    }

    // The bots' hidden match plans learn from the result (js/engine/ai/strategist.js).
    if (!this.isOnline && !this.isCampaignMission) {
      for (const bot of new Set([this.ai, this.aiWestern, this.aiEastern])) {
        if (bot?.strategist) bot.strategist.recordOutcome(this.battlefield?.winner === bot.team);
      }
      // ... and the persistent memory of this player learns the deck, the habits and the result.
      if (this.ai && !this.isExhibition) {
        this.ai.recordMatchMemory({
          playerCards: this.cardSystem?.playerDeck || this.cardSystem?.decks?.[playerTeam] || [],
          won: this.battlefield?.winner === enemyTeam
        });
      }
    }

    // Track consecutive singleplayer losses & wins against AI for "Change AI Mode" suggestion
    if (!this.isOnline && this.ai && !this.isExhibition && !this.isCampaignMission) {
      if (isVictory) {
        this.consecutiveLossesAgainstAI = 0;
        this.consecutiveWinsAgainstAI = (this.consecutiveWinsAgainstAI || 0) + 1;
      } else {
        this.consecutiveLossesAgainstAI = (this.consecutiveLossesAgainstAI || 0) + 1;
        this.consecutiveWinsAgainstAI = 0;
      }
      if (typeof sessionStorage !== 'undefined') {
        try {
          sessionStorage.setItem('ucg_consecutive_ai_losses', String(this.consecutiveLossesAgainstAI));
          sessionStorage.setItem('ucg_consecutive_ai_wins', String(this.consecutiveWinsAgainstAI));
        } catch (_) {}
      }
    }

    // Batch 1 (docs/SOUND_BATCH_PLAN.md, Runde 2) - match_end_stinger + victory/defeat
    this.audio.playMatchResult(isVictory, false);
    this.audio?.setMatchTension?.(0);
    this.audio?.stopAmbience?.();
    this.audio?.stopWeather?.();

    const modal = document.getElementById('game-over-modal');
    const modalBox = modal?.querySelector('.game-over-modal-box');
    const badge = document.getElementById('game-over-badge');
    const title = document.getElementById('game-over-title');
    const desc = document.getElementById('game-over-desc');
    const durationEl = document.getElementById('game-over-duration');
    const modeEl = document.getElementById('game-over-mode');

    // Online (server-authoritative WS) matches never run the local
    // simulation on this client, so battlefield.stats itself stays empty -
    // the server sends the real MatchStats in its MATCH_END message instead
    // (see wsClient.onMatchEnd above), deserialized into _onlineFinalStats.
    const onlineStatsMissing = this.isOnline && !this._onlineFinalStats;
    const stats = (this.isOnline && this._onlineFinalStats) ? this._onlineFinalStats : this.battlefield.stats;
    const statsFallback = { kills: 0, hitRate: 100, damageDealt: 0, plasmaHarvested: 0, plasmaUtilization: 0, resourceEfficiency: 0, damagePerMinute: 0, materialValueDestroyed: 0, cardsPlayed: 0, avgTerritoryControl: 50 };
    const playerStats = stats?.getSummary(playerTeam) || statsFallback;
    const enemyStats = stats?.getSummary(enemyTeam) || statsFallback;
    const formattedDuration = stats?.getFormattedDuration() || '00:00';

    // AI Match Telemetry: Send anonymized singleplayer match results for balance tuning
    // Strictly ignores matches under 30s to prevent spam and noise.
    if (!this.isOnline && this.ai && !this.isExhibition) {
      try {
        const playerCards = this.cardSystem?.playerDeck || this.cardSystem?.decks?.[playerTeam] || [];
        const diff = this.currentAIDifficulty || this.ai?.difficulty || DEFAULT_AI_DIFFICULTY;
        const durationSec = Math.floor(stats?.duration || 0);

        const aiCards = (enemyTeam === 'eastern' ? this.cardSystem?.enemyDeck : this.cardSystem?.playerDeck)
          || this.ai?.deckInfo?.cards
          || [];

        sendAiMatchTelemetry({
          difficulty: diff,
          aiMetadata: this.ai?.getTelemetryMetadata?.(),
          aiThreatDetected: aiAdaptiveMemory.getPlayerThreatProfile(diff)?.primaryThreat || null,
          winner: isVictory ? 'player' : 'ai',
          durationSeconds: durationSec,
          playerRace: this.cardSystem?.playerRace || 'Human',
          aiRace: this.ai?.race || 'Sul',
          playerDeck: playerCards,
          aiDeck: aiCards,
          statsSummary: {
            playerDamage: playerStats?.damageDealt || 0,
            aiDamage: enemyStats?.damageDealt || 0,
            playerCardsPlayed: playerStats?.cardsPlayed || 0,
            aiCardsPlayed: enemyStats?.cardsPlayed || 0,
            consecutivePlayerWins: isVictory ? (aiAdaptiveMemory.consecutiveLosses || 0) : 0,
            sessionMatchCount: aiAdaptiveMemory.sessionMatches?.length || 1
          }
        });
      } catch {
        // Fail-silent
      }
    }

    if (modal && title && desc) {
      if (!modal.classList.contains('active')) {
        this.overlayGate?.acquire?.();
      }
      modal.className = 'modal-container active';

      if (modalBox) {
        modalBox.className = `modal-box game-over-modal-box ${isVictory ? 'victory' : 'defeat'}`;
      }

      const statsContainer = modal?.querySelector('.stats-comparison-container');
      if (statsContainer) statsContainer.style.display = '';

      // Header Team Columns - shared by both the Exhibition and regular
      // branches below (previously declared only inside the `else` branch,
      // which threw a ReferenceError whenever an Exhibition match ended).
      const playerRole = document.getElementById('stats-role-player');
      const enemyRole = document.getElementById('stats-role-enemy');
      const playerName = document.getElementById('stats-name-player');
      const enemyName = document.getElementById('stats-name-enemy');
      const playerPill = document.getElementById('stats-pill-player');
      const enemyPill = document.getElementById('stats-pill-enemy');

      const playerDeck = DECKS_DATA[this.currentPlayerDeckId]?.name || this.currentPlayerDeckId || 'Standard';
      const enemyDeck = this.ai?.deckInfo?.name || 'AI';

      if (this.isExhibition) {
        const westWon = this.battlefield.winner === 'western';
        if (badge) {
          badge.innerHTML = `${getIconSvg('robot', 14)} EXHIBITION COMPLETED`;
          badge.style.color = '#00e5ff';
          badge.style.borderColor = 'rgba(0, 229, 255, 0.4)';
          badge.style.background = 'rgba(0, 229, 255, 0.12)';
        }
        title.innerText = westWon ? 'WEST AI WINS!' : 'EAST AI WINS!';
        title.style.color = '#00e5ff';
        desc.innerText = westWon ? 'Western AI has prevailed in the exhibition match.' : 'Eastern AI has prevailed in the exhibition match.';
        if (durationEl) durationEl.innerHTML = `${getIconSvg('stopwatch', 12)} ${formattedDuration} Min`;
        if (modeEl) modeEl.innerHTML = `${getIconSvg('robot', 14)} AI vs AI Exhibition`;
        if (playerRole) playerRole.innerText = '';
        if (enemyRole) enemyRole.innerText = '';
        if (playerName) playerName.innerText = 'WEST AI';
        if (enemyName) enemyName.innerText = 'EAST AI';
        if (playerPill) {
          playerPill.className = `team-outcome-pill ${westWon ? 'winner' : 'loser'}`;
          playerPill.innerText = westWon ? 'VICTOR' : 'DEFEATED';
        }
        if (enemyPill) {
          enemyPill.className = `team-outcome-pill ${westWon ? 'loser' : 'winner'}`;
          enemyPill.innerText = westWon ? 'DEFEATED' : 'VICTOR';
        }
      } else {
        if (badge) {
          if (this.activeDare) {
            badge.innerHTML = isVictory
              ? `${getIconSvg('trophy', 14)} DARE COMPLETED — ${this.activeDare.by.toUpperCase()} DEFEATED!`
              : `${getIconSvg('skull', 14)} DARE FAILED — ${this.activeDare.by.toUpperCase()} WINS`;
            badge.style.color = isVictory ? '#00ff66' : '#ff3344';
            badge.style.borderColor = isVictory ? 'rgba(0, 255, 102, 0.4)' : 'rgba(255, 51, 68, 0.4)';
            badge.style.background = isVictory ? 'rgba(0, 255, 102, 0.12)' : 'rgba(255, 51, 68, 0.12)';
          } else if (this._onlineDisconnectReason === 'disconnect_timeout') {
            badge.innerHTML = isVictory ? `${getIconSvg('trophy', 14)} OPPONENT FORFEIT` : `${getIconSvg('skull', 14)} CONNECTION LOST`;
            badge.style.color = isVictory ? '#00ff66' : '#ff3344';
            badge.style.borderColor = isVictory ? 'rgba(0, 255, 102, 0.4)' : 'rgba(255, 51, 68, 0.4)';
            badge.style.background = isVictory ? 'rgba(0, 255, 102, 0.12)' : 'rgba(255, 51, 68, 0.12)';
          } else {
            badge.innerHTML = isVictory ? `${getIconSvg('trophy', 14)} VICTORY` : `${getIconSvg('skull', 14)} FORFEIT`;
            badge.style.color = isVictory ? '#00ff66' : '#ff3344';
            badge.style.borderColor = isVictory ? 'rgba(0, 255, 102, 0.4)' : 'rgba(255, 51, 68, 0.4)';
            badge.style.background = isVictory ? 'rgba(0, 255, 102, 0.12)' : 'rgba(255, 51, 68, 0.12)';
          }
        }

        if (this._onlineDisconnectReason === 'disconnect_timeout') {
          title.innerText = isVictory ? 'VICTORY BY DISCONNECT' : 'DISCONNECT FORFEIT';
          title.style.color = isVictory ? '#00ff66' : '#ff3344';
          title.style.display = 'block';
          desc.innerText = isVictory
            ? 'Your opponent lost connection and failed to reconnect within the grace period.'
            : 'Your connection was lost and could not be restored in time.';
          desc.style.display = 'block';
        } else {
          // Victory & defeat title/desc removed as requested: MATCH VICTOR badge + Gamerank are the hero elements
          title.innerText = '';
          title.style.display = 'none';
          desc.innerText = '';
          desc.style.display = 'none';
        }

        if (durationEl) durationEl.innerText = `${formattedDuration} Min`;
        if (modeEl) {
          if (this.isOnline) {
            if (this.is2v2 || this.onlineMode === '2v2') {
              modeEl.innerText = '2v2 Online';
            } else if (this.onlineMode === 'blitz') {
              modeEl.innerText = '1v1 Blitz Online';
            } else {
              modeEl.innerText = '1v1 Online';
            }
          } else {
            modeEl.innerText = '1v1';
          }
        }

        const myName = getDisplayName() && getDisplayName() !== 'Commander' ? getDisplayName() : 'YOU';
        if (playerName) playerName.innerText = myName;
        if (enemyName) enemyName.innerText = this.isOnline ? (this.netClient?.opponentName || 'ENEMY COMMANDER') : 'AI ENEMY';
        if (this.activeDare?.by && enemyName && !this.isOnline) enemyName.innerText = `${this.activeDare.by.toUpperCase()} (GHOST)`;

        if (playerPill) {
          playerPill.className = `team-outcome-pill ${isVictory ? 'winner' : 'loser'}`;
          playerPill.innerText = isVictory ? 'VICTOR' : 'DEFEATED';
        }
        if (enemyPill) {
          enemyPill.className = `team-outcome-pill ${isVictory ? 'loser' : 'winner'}`;
          enemyPill.innerText = isVictory ? 'DEFEATED' : 'VICTOR';
        }
      }

      // Gamerank: skill grade for the local player only, skipped for
      // Exhibition (AI vs AI has no "local player" to grade) and Sandbox
      // (never reaches handleGameOver() at all). See docs/GAMERANK_PLAN.md.
      const gamerankShowcase = document.getElementById('gamerank-showcase');
      const gamerankBadge = document.getElementById('gamerank-badge');
      const letterVal = document.getElementById('gamerank-letter-val');
      const titleVal = document.getElementById('gamerank-title-val');
      const scoreVal = document.getElementById('gamerank-score-val');
      const barFill = document.getElementById('gamerank-bar-fill');

      let rank = null;
      let matchMvp = null;
      if (gamerankShowcase) {
        // Stop any Unit MVP Spotlight animation left running from a
        // previous match (the modal is reused across Rematch cycles without
        // a page reload) before either branch below runs.
        this._unitMvpSpotlightController?.stop();
        this._unitMvpSpotlightController = null;

        if (this.isExhibition || onlineStatsMissing) {
          gamerankShowcase.hidden = true;
        } else {
          rank = computeGamerank(playerStats, isVictory, {
            enemySummary: enemyStats,
            duration: stats?.duration,
            tracker: this.matchDirectiveTracker
          });
          gamerankShowcase.hidden = false;

          const gradeClass = `rank-${rank.grade.toLowerCase().replace('+', '-plus').replace('-', '-minus')}`;
          if (gamerankBadge) gamerankBadge.className = `gamerank-crest-outer ${gradeClass}`;

          const titles = {
            'S+': 'ORBITAL APEX',
            'S': 'MASTER COMBATANT',
            'S-': 'APEX CONTENDER',
            'A+': 'TACTICAL MASTER',
            'A': 'ELITE SPECIALIST',
            'B+': 'FIELD SPECIALIST',
            'B': 'TACTICAL OPERATOR',
            'C': 'FIELD RECON',
            'D': 'COMBAT ABORT'
          };

          if (letterVal) letterVal.innerText = rank.grade;
          if (titleVal) titleVal.innerText = titles[rank.grade] || 'COMBAT RATING';
          if (scoreVal) scoreVal.innerText = rank.composite;
          if (barFill) barFill.style.width = `${Math.max(4, Math.min(100, rank.composite))}%`;

          const isSRank = rank.grade === 'S' || rank.grade === 'S+';
          const b = rank.breakdown;
          const bonusParts = [];
          if (b.noSuperweaponBonus) bonusParts.push(`Nuke +${b.noSuperweaponBonus}`);
          if (b.tempoBonus) bonusParts.push(`Tempo +${b.tempoBonus}`);
          if (b.plasmaDisciplineBonus) bonusParts.push(`Discipline +${b.plasmaDisciplineBonus}`);
          const bonusStr = bonusParts.length > 0 ? ` [${bonusParts.join(', ')}]` : '';
          gamerankShowcase.title = `Territory ${b.territoryIndex} / Damage ${b.damageIndex} / Eff ${b.efficiencyIndex} / Base ${b.baseHpIndex} / K/D ${b.kdIndex}${bonusStr}`;

          // Batch 1 (docs/SOUND_BATCH_PLAN.md) - "victory_s_rank", approved
          // by the user against tools/sound-review/data/batch-01.json.
          // Plain "victory"/"defeat" stayed needs_alt (voice lines rejected,
          // a non-voice candidate is still pending), so this S/S+ fanfare is
          // the only win/loss sound wired from Batch 1 so far.
          try {
            if (isVictory && isSRank) this.audio?.playSample?.('flawless_victory.ogg');
          } catch (err) {
            console.warn('[Game] Error playing victory fanfare:', err);
          }

          // Daily Directives Progress Ingestion (Cumulative) & Evaluation
          try {
            directives.addProgress('DEAL_DAMAGE', playerStats.damageDealt || 0);
            if (isVictory && rank.grade !== 'D') directives.addProgress('WIN_MATCHES', 1);
            directives.addProgress('PLAY_CARDS', playerStats.cardsPlayed || 0);
            directives.addProgress('BODY_COUNT', playerStats.kills || 0);
            directives.addProgress('MERGE_PROTOCOL', playerStats.meldsPerformed || 0);
            directives.addProgress('AIR_SUPERIORITY', playerStats.airKills || 0);
            for (const [race, count] of Object.entries(playerStats.cardsPlayedByRace || {})) {
              directives.addProgress('FACTION_LOYALTY', count, { race });
            }

            // Single-Match Win Directives Evaluation
            for (const d of directives.getDirectives()) {
              if (d.completed) continue;
              let winConditionMet = false;
              if (isVictory && rank.grade !== 'D') {
                if (d.type === 'S_RANK_VICTORY' && isSRank) {
                  winConditionMet = true;
                } else if (d.type === 'CLEAN_SHEET') {
                  const baseHp = this.battlefield?.bases?.[playerTeam]?.hp || 0;
                  const initHp = this.matchDirectiveTracker?.initialHp || 1500;
                  if ((baseHp / initHp) >= 0.85) winConditionMet = true;
                } else if (d.type === 'COMEBACK_KING' && (this.matchDirectiveTracker?.lowestBaseHp || 1500) < 500) {
                  winConditionMet = true;
                } else if (d.type === 'PLASMA_DISCIPLINE' && !this.matchDirectiveTracker?.plasmaEverCapped) {
                  winConditionMet = true;
                } else if (d.type === 'SUPERWEAPON_WIN' && (playerStats.superweaponsUsed || 0) > 0) {
                  winConditionMet = true;
                } else if (d.type === 'UNTOUCHABLE') {
                  const tracker = this.matchDirectiveTracker;
                  if (tracker && tracker.initialHp !== null && tracker.minBaseHp >= tracker.initialHp) winConditionMet = true;
                } else if (d.type === 'UNDERDOG') {
                  const aiDiff = this.ai?.difficulty;
                  if (this.isOnline || aiDiff === 'hard' || aiDiff === 'veryHard' || aiDiff === 'insane' || aiDiff === 'invincible') winConditionMet = true;
                } else if (d.type === 'CONVENTIONAL_WAR' && (playerStats.superweaponsUsed || 0) === 0) {
                  winConditionMet = true;
                } else if (d.type === 'TITAN_WARFARE' || d.type === 'LOW_PROFILE') {
                  let heavyCount = playerStats.deckHeavyCount;
                  let maxCost = playerStats.deckMaxCost;
                  if (!playerStats.deckProfileRecorded && !maxCost && !heavyCount) {
                    const localTeamStats = this.battlefield?.stats?.teams?.[playerTeam];
                    if (localTeamStats?.deckProfileRecorded || localTeamStats?.deckMaxCost > 0 || localTeamStats?.deckHeavyCount > 0) {
                      heavyCount = localTeamStats.deckHeavyCount;
                      maxCost = localTeamStats.deckMaxCost;
                    } else if (this.cardSystem?.getDeckProfile) {
                      const prof = this.cardSystem.getDeckProfile(playerTeam);
                      heavyCount = prof.heavyCount;
                      maxCost = prof.maxCost;
                    }
                  }
                  if (d.type === 'TITAN_WARFARE' && (heavyCount || 0) >= 3) {
                    winConditionMet = true;
                  } else if (d.type === 'LOW_PROFILE' && (maxCost || 0) < 40 && (heavyCount || 0) === 0) {
                    winConditionMet = true;
                  }
                } else if (d.type === 'BLITZKRIEG' && (playerStats.duration || this.battlefield?.stats?.duration || 0) <= 270) {
                  winConditionMet = true;
                } else if (d.type === 'FUSION_OVERDRIVE' && (playerStats.meldsPerformed || 0) >= d.target) {
                  winConditionMet = true;
                } else if (d.type === 'AIR_WING_SURGE' && playerStats.airSurgeCompleted) {
                  winConditionMet = true;
                } else if (d.type === 'SPELL_MASTERY' && (playerStats.spellsPlayed || 0) >= d.target) {
                  winConditionMet = true;
                } else if (d.type === 'FORTRESS_PROTOCOL' && (playerStats.structuresPlayed || 0) >= d.target) {
                  winConditionMet = true;
                } else if (d.type === 'SCRAP_HARVEST' && (playerStats.salvagePlasmaEarned || 0) >= d.target) {
                  winConditionMet = true;
                } else if (d.type === 'SPLASH_CARNAGE' && (playerStats.splashKills || 0) >= d.target) {
                  winConditionMet = true;
                }
              }
              if (winConditionMet) {
                directives.completeDirective(d.id);
                this.directiveTrackerHUD?.showCompletion(d);
              }
            }

            // THREE_PEAT: consecutive wins today (a loss resets the run)
            const threePeat = directives.recordMatchOutcome(isVictory && rank.grade !== 'D');
            if (threePeat) this.directiveTrackerHUD?.showCompletion(threePeat);
          } catch (err) {
            console.error('[Game] Error evaluating daily directives:', err);
          }

          // Fugazi Settlement & Reward Card Presentation (with duration scaling & online bonus)
          const matchDuration = stats?.duration || 0;
          const reward = wallet.calculateMatchReward(isVictory, rank.grade, {
            durationSeconds: matchDuration,
            isOnline: this.isOnline
          });
          wallet.addFugazi(reward.total, 'match_settlement');

          const fugaziRewardEl = document.getElementById('game-over-fugazi-reward');
          const fugaziValEl = document.getElementById('game-over-fugazi-val');
          const fugaziBreakdownEl = document.getElementById('game-over-fugazi-breakdown');
          const fugaziTotalEl = document.getElementById('game-over-fugazi-total');

          if (fugaziRewardEl) {
            fugaziRewardEl.hidden = false;
          }
          if (fugaziValEl) {
            fugaziValEl.textContent = reward.total;
          }
          if (fugaziBreakdownEl) {
            if (rank.grade === 'D') {
              fugaziBreakdownEl.textContent = '';
              fugaziBreakdownEl.hidden = true;
            } else {
              const parts = [`Base ${reward.base} × ${reward.multiplier.toFixed(2)}x (${rank.grade})`];
              if (reward.timeBonus > 0) parts.push(`+${reward.timeBonus} Time`);
              if (reward.isOnline) parts.push('× 1.5x Online');
              fugaziBreakdownEl.textContent = parts.join(' ');
              fugaziBreakdownEl.hidden = false;
            }
          }
          if (fugaziTotalEl) {
            fugaziTotalEl.textContent = wallet.getFugazi().toLocaleString();
          }

          // XP / Progress (docs/XP_PROGRESSION_PLAN.md): Skirmish and Online
          // are both graded, so both feed lifetime combat stats and XP -
          // Exhibition/onlineStatsMissing never reach this branch at all.
          progress.recordMatchResult({
            mode: this.isOnline ? 'online' : 'skirmish',
            isVictory,
            grade: rank.grade,
            composite: rank.composite,
            damageDealt: playerStats.damageDealt || 0
          });

          // Achievement evaluation - facts assembled from whichever module
          // actually owns each stat (progress.js never imports
          // inventory.js/wallet.js itself, see js/data/achievements.js).
          try {
            let playerCards = (playerTeam === 'western' ? this.cardSystem?.playerDeck : this.cardSystem?.enemyDeck)
              || this.cardSystem?.playerDeck
              || (this.currentPlayerDeckId ? resolveDeckCards(this.currentPlayerDeckId) : []);
            const deckFacts = inspectDeckForAchievements(playerCards, this.currentPlayerDeckId);

            this._checkAchievements({
              isVictory,
              grade: rank.grade,
              plasmaUtilization: playerStats.plasmaUtilization || 0,
              territoryControl: playerStats.avgTerritoryControl || 0,
              duration: playerStats.duration || this.battlefield?.stats?.duration || 0,
              fogCloakKills: playerStats.fogCloakKills || 0,
              antiShadeDuelKills: playerStats.antiShadeDuelKills || 0,
              heavyUnitHijacks: playerStats.heavyUnitHijacks || 0,
              meldsPerformed: playerStats.meldsPerformed || 0,
              salvagePlasmaEarned: playerStats.salvagePlasmaEarned || 0,
              splashKills: playerStats.splashKills || 0,
              airKills: playerStats.airKills || 0,
              metamorphosisCast: playerStats.metamorphosisCast || 0,
              minOwnBaseHp: typeof playerStats.minOwnBaseHp === 'number' ? playerStats.minOwnBaseHp : 1500,
              ownBaseDamageTaken: playerStats.ownBaseDamageTaken || 0,
              superweaponsUsed: playerStats.superweaponsUsed || 0,
              enemySuperweaponsUsed: enemyStats?.superweaponsUsed || 0,
              ...deckFacts
            });
          } catch (err) {
            console.error('[Game] Error checking achievements:', err);
          }

          // Unit MVP Spotlight (docs/UNIT_MVP_PLAN.md): best unit of the
          // WINNING team only (not the global cross-team best the formula
          // itself allows, see section 2.8), shown as a small walk-in/
          // sparkle/walk-out animation inside this same Combat Rating badge.
          // Coupled to Gamerank's own isExhibition visibility (section 2.6)
          // by simply living inside this same branch - no separate gate.
          try {
            const unitMvpRow = document.getElementById('unit-mvp-spotlight');
            const unitMvpCanvas = document.getElementById('unit-mvp-canvas');
            const unitMvpNameEl = document.getElementById('unit-mvp-name-val');
            const unitMvpDamageEl = document.getElementById('unit-mvp-damage-val');
            const unitMvpStatLabelEl = document.getElementById('unit-mvp-stat-label');
            const unitMvpLifespanEl = document.getElementById('unit-mvp-lifespan-val');

            matchMvp = null;
            if (unitMvpRow && unitMvpCanvas) {
              const mvp = computeUnitMvp(stats, this.battlefield?.winner);
              matchMvp = mvp;
              if (mvp) {
                // Only the local player's own MVP counts toward "Veteran"
                // (docs/XP_PROGRESSION_PLAN.md 7.2) - computeUnitMvp() always
                // returns the WINNING team's best unit, which is the enemy's
                // when the local player lost.
                if (isVictory) progress.recordUnitMvp(mvp.cardData?.name || mvp.unitType);
                unitMvpRow.hidden = false;
                if (unitMvpNameEl) unitMvpNameEl.textContent = mvp.cardData?.name || mvp.unitType;
                const axisLabels = { damage: 'DMG', killCredit: 'KILL', healing: 'HEAL', damageTaken: 'TANK' };
                const axisValues = { damage: mvp.damageDealt, killCredit: mvp.materialCredit, healing: mvp.healingDone, damageTaken: mvp.damageTaken };
                if (unitMvpStatLabelEl) unitMvpStatLabelEl.textContent = axisLabels[mvp.dominantAxis] || 'DMG';
                if (unitMvpDamageEl) unitMvpDamageEl.textContent = axisValues[mvp.dominantAxis] ?? mvp.damageDealt;
                if (unitMvpLifespanEl) {
                  const mins = Math.floor(mvp.lifetime / 60);
                  const secs = Math.floor(mvp.lifetime % 60);
                  unitMvpLifespanEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
                }

                const initSpotlight = () => {
                  if (!unitMvpCanvas || !modal?.classList.contains('active')) return;
                  const rect = unitMvpCanvas.getBoundingClientRect();
                  const w = Math.round(rect.width) || Math.round(unitMvpCanvas.parentElement?.getBoundingClientRect().width) || 300;
                  const h = Math.round(rect.height) || 64;
                  unitMvpCanvas.width = Math.max(1, w);
                  unitMvpCanvas.height = Math.max(1, h);
                  this._unitMvpSpotlightController?.stop();
                  this._unitMvpSpotlightController = createUnitMvpSpotlight(unitMvpCanvas, mvp);
                };

                initSpotlight();
                if (typeof requestAnimationFrame !== 'undefined') {
                  requestAnimationFrame(() => {
                    if (unitMvpCanvas && modal?.classList.contains('active')) {
                      const freshRect = unitMvpCanvas.getBoundingClientRect();
                      if (freshRect.width > 10 && Math.abs(unitMvpCanvas.width - freshRect.width) > 5) {
                        initSpotlight();
                      }
                    }
                  });
                }
              } else {
                unitMvpRow.hidden = true;
              }
            }
          } catch (err) {
            console.error('[Game] Error creating unit MVP spotlight:', err);
          }
        }
      } else {
        const fugaziRewardEl = document.getElementById('game-over-fugazi-reward');
        if (fugaziRewardEl) fugaziRewardEl.hidden = true;
      }

      // Store Tactical Dare snapshot for sharing
      const myCallsign = (getDisplayName() && getDisplayName() !== 'Commander') ? getDisplayName() : 'YOU';
      const playerTeamKey = this.isOnline ? (this.myTeam || 'western') : 'western';
      const playedDeck = (playerTeamKey === 'western' ? this.cardSystem?.playerDeck : this.cardSystem?.enemyDeck)
        || this.currentPlayerDeckId
        || 'Human';
      const rawDuration = Math.round(playerStats?.duration || this.battlefield?.stats?.duration || this.battlefield?.gameTime || 0);

      this._lastMatchDareData = {
        by: myCallsign,
        deck: playedDeck,
        grade: rank?.grade || 'B',
        score: rank?.composite ?? 50,
        duration: rawDuration,
        formattedDuration: formattedDuration || '00:00',
        mvp: (typeof matchMvp !== 'undefined' && matchMvp) ? (matchMvp.cardData?.name || matchMvp.unitType) : null,
        ref: getAuth()?.accountId || null
      };

      if (this.activeDare) {
        if (isVictory && this.activeDare.timeSec && rawDuration > 0 && rawDuration < this.activeDare.timeSec) {
          if (desc) {
            const diff = this.activeDare.timeSec - rawDuration;
            desc.innerHTML = `${getIconSvg('energy', 14)} NEW FLEET RECORD! You beat ${this.activeDare.by}'s time by ${diff}s!`;
            desc.style.display = 'block';
            desc.style.color = '#00f0ff';
          }
        }
        if (this.activeDare.ref) {
          try {
            fetch('api/war_bounty.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                action: 'complete',
                ref: this.activeDare.ref,
                bountyId: this.activeDare.bountyId || null,
                duration: rawDuration
              })
            }).catch(() => {});
          } catch {}
        }
      }

      // Populate KPIs & Highlight Superior Value + Animated Visual Difference Bars
      const shares = [];
      // Tracks each row's normalized share-spread (0 = dead even, 100 = one
      // side took it all) so the 3 rows with the biggest *relative* delta
      // can be glow-highlighted below, regardless of the stat's own unit
      // (kills vs. damage vs. a %) - see the diff-magnitude ranking after
      // all setKPI() calls.
      const kpiDeltaTrack = [];
      const setKPI = (kpiId, pVal, eVal, formatP, formatE, higherIsBetter = true, isDirectPercent = false) => {
        const elP = document.getElementById(`stat-val-player-${kpiId}`);
        const elE = document.getElementById(`stat-val-enemy-${kpiId}`);
        const barP = document.getElementById(`stat-bar-player-${kpiId}`);
        const barE = document.getElementById(`stat-bar-enemy-${kpiId}`);
        const badge = document.getElementById(`stat-diff-${kpiId}`);
        const labelWrap = document.querySelector(`.stat-row[data-kpi="${kpiId}"] .stat-label-wrap`);
        if (!elP || !elE) return;

        elP.innerText = formatP ? formatP(pVal) : pVal;
        elE.innerText = formatE ? formatE(eVal) : eVal;
        elP.classList.remove('winner-stat');
        elE.classList.remove('winner-stat');

        let pShare = 50;
        let eShare = 50;
        const sum = pVal + eVal;
        if (sum > 0) {
          pShare = (pVal / sum) * 100;
          eShare = (eVal / sum) * 100;
        }

        // Clamp shares cleanly
        pShare = Math.min(100, Math.max(0, pShare));
        eShare = Math.min(100, Math.max(0, eShare));
        shares.push(higherIsBetter ? pShare : (100 - pShare));
        kpiDeltaTrack.push({ delta: Math.abs(pShare - eShare), elP, elE, barP, barE, labelWrap });

        const pWins = higherIsBetter ? pVal > eVal : pVal < eVal;
        const eWins = higherIsBetter ? eVal > pVal : eVal < pVal;

        const pShareFixed = Number(pShare.toFixed(1));
        const eShareFixed = Number((100 - pShareFixed).toFixed(1));

        if (barP && barE) {
          barP.style.width = `${pShareFixed}%`;
          barE.style.width = `${eShareFixed}%`;
          barP.classList.toggle('dominant-lead', pVal !== eVal && pWins);
          barE.classList.toggle('dominant-lead', pVal !== eVal && eWins);
        }

        if (badge) {
          const diff = pVal - eVal;
          if (diff === 0) {
            badge.className = 'stat-diff-badge visible badge-tie';
            badge.innerText = 'EQUAL';
          } else {
            badge.className = `stat-diff-badge visible ${pWins ? 'badge-player-lead' : 'badge-enemy-lead'}`;
            const absDiff = Math.abs(diff);
            const prefix = pWins ? '+' : '-';
            const formattedDiff = isDirectPercent ? `${absDiff}%` : absDiff.toLocaleString('en-US');
            badge.innerText = `${prefix}${formattedDiff}`;
          }
        }

        if (pVal !== eVal) {
          if (pWins) {
            elP.classList.add('winner-stat');
          } else {
            elE.classList.add('winner-stat');
          }
        }
      };

      setKPI('kills', playerStats.kills, enemyStats.kills, v => v, v => v, true);
      setKPI('hitrate', playerStats.hitRate, enemyStats.hitRate, v => `${v}%`, v => `${v}%`, true, true);
      setKPI('damage', playerStats.damageDealt, enemyStats.damageDealt, v => v.toLocaleString('en-US'), v => v.toLocaleString('en-US'), true);
      setKPI('plasma', playerStats.plasmaHarvested, enemyStats.plasmaHarvested, v => v.toLocaleString('en-US'), v => v.toLocaleString('en-US'), true);
      setKPI('cards', playerStats.cardsPlayed, enemyStats.cardsPlayed, v => v, v => v, true);
      setKPI('territory', playerStats.avgTerritoryControl, enemyStats.avgTerritoryControl, v => `${v}%`, v => `${v}%`, true, true);
      setKPI('material', playerStats.materialValueDestroyed, enemyStats.materialValueDestroyed, v => v.toLocaleString('en-US'), v => v.toLocaleString('en-US'), true);

      // Glow-highlight the 3 rows with the biggest relative delta (share
      // spread away from the dead-even 50/50 split), so the standout stats
      // of the match pop out visually instead of all 7 rows reading equally.
      document.querySelectorAll('.stat-bar-fill.stat-hot-delta, .stat-val.stat-hot-delta, .stat-label-wrap.stat-hot-delta')
        .forEach(el => el.classList.remove('stat-hot-delta'));
      [...kpiDeltaTrack]
        .sort((a, b) => b.delta - a.delta)
        .slice(0, 3)
        .forEach(({ elP, elE, barP, barE, labelWrap }) => {
          elP?.classList.add('stat-hot-delta');
          elE?.classList.add('stat-hot-delta');
          barP?.classList.add('stat-hot-delta');
          barE?.classList.add('stat-hot-delta');
          labelWrap?.classList.add('stat-hot-delta');
        });

      // Overall Dominance Score & Bar
      const avgShareP = shares.length > 0 ? Math.round(shares.reduce((a, b) => a + b, 0) / shares.length) : 50;
      const avgShareE = 100 - avgShareP;

      const domPctP = document.getElementById('dom-pct-player');
      const domPctE = document.getElementById('dom-pct-enemy');
      const domBarP = document.getElementById('dom-bar-player');
      const domBarE = document.getElementById('dom-bar-enemy');

      if (domPctP) domPctP.innerText = `${avgShareP}%`;
      if (domPctE) domPctE.innerText = `${avgShareE}%`;
      if (domBarP) domBarP.style.width = `${avgShareP}%`;
      if (domBarE) domBarE.style.width = `${avgShareE}%`;

      // Quests button visibility (hidden on AI fight and tactical dare end screens)
      const footerActions = modal?.querySelector?.('.game-over-footer-actions');
      if (footerActions) {
        footerActions.style.display = (this.isExhibition || this.activeDare) ? 'none' : '';
      }
      const btnGameOverDirectives = document.getElementById('btn-game-over-directives');
      if (btnGameOverDirectives) {
        btnGameOverDirectives.style.display = (this.isExhibition || this.activeDare) ? 'none' : '';
      }

      // Rematch button countdown for AI fight (10s countdown to continuous auto-match)
      this._clearAiRematchCountdown();
      const btnRematch = document.getElementById('btn-rematch');
      if (this.isExhibition && btnRematch) {
        let remaining = 10;
        btnRematch.innerText = `Rematch (${remaining})`;
        this._aiRematchTimer = setInterval(() => {
          remaining -= 1;
          if (remaining > 0) {
            btnRematch.innerText = `Rematch (${remaining})`;
          } else {
            this._clearAiRematchCountdown();
            this.handleRematch();
          }
        }, 1000);
      } else if (btnRematch) {
        btnRematch.innerText = 'Rematch';
      }
    }
    notifyUpdateManagerIdle(this);
  }

  spawnUnitByName(name, team) {
    this.battlefield.spawnUnitByName(name, team);
  }

  toggleSpeed() {
    // Speeding up would race ahead of turn data that hasn't arrived
    // yet, just causing more stalling instead of a faster match.
    if (this.isOnline) return;
    this.speedMultiplier = this.speedMultiplier === 1 ? 4 : 1;
    const btnSpeed = document.getElementById('btn-speed');
    btnSpeed?.classList.toggle('speed-active', this.speedMultiplier === 4);
    // Batch 4 (docs/SOUND_BATCH_PLAN.md section 4.4).
    this.audio.playUiClick();
  }

  toggleAutoDraw() {
    this.autoDrawEnabled = !this.autoDrawEnabled;
    const btnAutoDraw = document.getElementById('btn-toggle-autodraw');
    if (btnAutoDraw) {
      btnAutoDraw.title = this.autoDrawEnabled ? 'Auto-Draw: ON' : 'Auto-Draw: OFF';
      btnAutoDraw.classList.toggle('autodraw-active', this.autoDrawEnabled);
    }
    // Batch 4 (docs/SOUND_BATCH_PLAN.md section 4.4).
    this.audio.playUiClick();
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.warn('Fullscreen request error:', err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.warn('Exit fullscreen error:', err);
        });
      }
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('DOMContentLoaded', () => {
    const game = new Game();
    game.init();
    window.tcgGame = game;
  });
}
